<template>
  <div class="chat-box-container"
    :style="{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }">
    <!-- 用户聊天的 match detail 卡片 -->
    <div v-if="isUserChat && (userMatchDetail || userMatchDetailLoading)" class="user-match-detail-card"
      @click="openUserMatchDetail">
      <div class="user-match-detail-content">
        <div style="display: flex; align-items: flex-start; width: 100%;">
          <span style="flex-shrink: 0; font-size: 14px; line-height: 1.5; margin-right: 6px;">✨</span>
          <span v-if="userMatchDetailLoading" style="color: #8A8A8A; font-size: 13px;">{{ $t('loading_match_info') }}</span>
          <span v-else-if="userMatchDetail && userMatchDetail.briefMatchReason"
            style="color: #4A4A4A; font-size: 13px; flex: 1; line-height: 1.5; word-break: break-word;">
            {{ userMatchDetail.briefMatchReason }}
          </span>
          <span v-else style="color: #4A4A4A; font-size: 13px; flex: 1;">{{ $t('view_match_details') }}</span>
          <van-icon name="arrow" color="#999" size="14" style="flex-shrink: 0; margin-top: 2px;" />
        </div>
        <div v-if="userMatchDetail && userMatchDetail.createdAt" class="user-match-detail-time">
          {{ formatMatchDetailTime(userMatchDetail.createdAt) }}
        </div>
      </div>
    </div>
    <!-- 私聊搜索入口 -->
    <div v-if="isUserChat && !showSearchPanel" class="chat-search-toolbar" @click="toggleInChatSearch(true)">
      <van-icon name="search" size="14" color="#8A8A8A" />
    </div>
    <!-- 私聊页内搜索（限定与对方的私聊消息，MySQL 全文搜索） -->
    <div v-if="isUserChat && showSearchPanel" class="chat-search-panel">
      <div class="chat-search-input-row">
        <van-icon name="search" size="14" color="#8A8A8A" />
        <input v-model="msgSearchKeyword" type="text" class="chat-search-input"
          :placeholder="$t('chatMsgSearch_inChatPlaceholder')" @keyup.enter="doInChatMsgSearch" />
        <van-icon v-if="msgSearchKeyword" name="clear" size="14" color="#C0C4CC" @click="clearInChatSearch" />
        <van-icon name="cross" size="16" color="#666" class="chat-search-close" @click="toggleInChatSearch(false)" />
      </div>
      <div v-if="msgSearchLoading" class="chat-search-status">{{ $t('loading') }}</div>
      <div v-else-if="msgSearchKeyword.trim() && !inChatKeywordValid" class="chat-search-status">{{ $t('chatMsgSearch_tooShort') }}</div>
      <div v-else-if="msgSearchDone && msgSearchResults.length == 0" class="chat-search-status">{{ $t('noData') }}</div>
      <div class="chat-search-results">
        <div v-for="m in msgSearchResults" :key="m.msgId" class="chat-search-item" @click="onInChatResultClicked(m)">
          <div class="chat-search-item-time">{{ $common.formatDateTime(m.createdAt) }}</div>
          <div class="chat-search-item-content" v-html="formatInChatPreview(m)"></div>
        </div>
      </div>
    </div>
    <!-- 用户自己的话 - 始终置顶展示（仅 AI 聊天；私聊不展示，reset 属于 AI 会话逻辑） -->
    <div v-if="!isUserChat && showUserMsgBanner && (displayUserMsg || firstUserMessageImages.length > 0)" style="padding: 10px 14px; margin: 12px 12px 0; background: #F7F5F2; border-radius: 10px; border: 1px solid #E0DDD8; text-align: left;">
      <div style="display: flex; align-items: flex-start; justify-content: flex-start; gap: 8px;">
        <span style="flex-shrink: 0; font-size: 14px; line-height: 1.6; margin-top: 0px;">💬</span>
        <span style="flex: 1; font-size: 14px; color: #1A1A1A; line-height: 1.6; word-break: break-word;">{{ displayUserMsg }}</span>
        <!-- 重置：清空整个会话（含本条用户消息），重新开始 -->
        <span style="display: inline-flex; align-items: center; flex-shrink: 0; cursor: pointer; font-size: 11px; color: #8A8A8A; padding: 2px 4px;" @click="resetSession">
          <van-icon name="replay" size="12" color="#8A8A8A" />
          <span style="margin-left: 3px;">{{ $t('aiChat_newChat') }}</span>
        </span>
      </div>
      <!-- 图片预览 -->
      <div v-if="firstUserMessageImages.length > 0" style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;">
        <div v-for="(imgUrl, idx) in firstUserMessageImages" :key="'banner-img-' + idx"
          style="width: 56px; height: 56px; border-radius: 6px; overflow: hidden; border: 1px solid #E0DDD8;">
          <img :src="$common.getFullImageUrl(imgUrl, $config)" style="width: 100%; height: 100%; object-fit: cover;" />
        </div>
      </div>
    </div>
    <!-- 地图组件 -->
    <!-- <MapLocationPicker width="100%" height="400px" @location-selected="handleLocationSelected" /> -->
    <!-- 需求澄清按钮区域 - 信使故事线视觉 -->
    <div v-if="shouldShowRequirementButtons"
      style="padding: 14px; margin: 12px; background: #FEF0ED; border-radius: 12px; border: 1px solid #F8A090;">
      <!-- 标题行：icon + 标题 -->
      <div style="display: flex; align-items: center; margin-bottom: 10px;">
        <van-icon name="star-o" size="16" color="#E8543A" style="margin-right: 6px; position: relative; top: -3px; flex-shrink: 0;" />
        <span style="flex: 1; font-size: 13px; font-weight: 600; color: #1A1A1A; line-height: 1.5; word-break: break-word; text-align: left;">{{ requirementClarified == '1' ? $t('aiChat_readyToPost') : $t('aiChat_clarifyingProgress') }}</span>
      </div>
      <!-- 横向 step pills -->
      <div style="display: flex; align-items: center;">
        <!-- Step 1: Understand Needs -->
        <div :style="{ display: 'inline-flex', alignItems: 'center', padding: '4px 8px', borderRadius: '10px', background: requirementClarified == '1' ? 'rgba(76,175,80,0.1)' : 'rgba(232,84,58,0.1)' }">
          <van-icon v-if="requirementClarified == '1'" name="success" size="12" color="#34A853" />
          <div v-else :style="{ width: '6px', height: '6px', borderRadius: '50%', background: '#E8543A' }"></div>
          <span :style="{ fontSize: '10px', fontWeight: 600, marginLeft: '4px', color: requirementClarified == '1' ? '#34A853' : '#E8543A' }">{{ $t('aiChat_progressStep1') }}</span>
        </div>
        <!-- 连接线 -->
        <div :style="{ flex: 1, height: '1px', margin: '0 4px', background: requirementClarified == '1' ? '#34A853' : '#E0DDD8' }"></div>
        <!-- Step 2: Post to Hub（聊清楚后自动生成 AI 摘要，直接进入发布步骤） -->
        <div :style="{ display: 'inline-flex', alignItems: 'center', padding: '4px 8px', borderRadius: '10px', background: requirementClarified == '1' ? 'rgba(232,84,58,0.1)' : '#E0DDD8' }">
          <div v-if="requirementClarified != '1'" :style="{ width: '12px', height: '12px', borderRadius: '50%', border: '1.5px solid #8A8A8A' }"></div>
          <div v-else :style="{ width: '6px', height: '6px', borderRadius: '50%', background: '#E8543A' }"></div>
          <span :style="{ fontSize: '10px', fontWeight: 600, marginLeft: '4px', color: requirementClarified == '1' ? '#E8543A' : '#8A8A8A' }">{{ $t('aiChat_progressStep3') }}</span>
        </div>
      </div>
      <!-- 副标题行 -->
      <div v-if="requirementClarified != '1' && completeness != null" style="display: flex; align-items: center; margin-top: 8px;">
        <span style="font-size: 11px; font-weight: 700; color: #E8543A;">{{ completeness }}%</span>
        <span style="font-size: 11px; color: #8A8A8A; margin-left: 6px;">{{ clarifyingSubtitle }}</span>
      </div>
      <div v-else-if="requirementClarified == '1' && (aiSummary || requirementDetail)" style="display: flex; align-items: flex-start; margin-top: 8px;">
        <span style="display: inline-block; margin-top: -1px; padding: 1px 5px; background: #FEF0ED; border: 0.5px solid #F8A090; border-radius: 4px; font-size: 9px; color: #8A8A8A; font-weight: 500; flex-shrink: 0; line-height: 1.5;">{{ $t('aiChat_matchSummaryLabel') }}</span>
        <span style="font-size: 11px; color: #8A8A8A; margin-left: 6px; line-height: 1.5;">{{ aiSummary || requirementDetail }}</span>
      </div>
      <div v-else-if="requirementClarified == '1'" style="margin-top: 8px;">
        <span style="font-size: 11px; color: #8A8A8A;">{{ $t('aiChat_progressStep1Done') }}</span>
      </div>
      <!-- expire_at 信息展示 -->
      <div v-if="hasExpireAt" style="font-size: 0.75rem; margin-top: 8px; text-align: center;"
        :style="{ color: isExpired ? '#E8543A' : '#999' }">
        {{ isExpired ? $t('aiChat_expireExpired') : $t('aiChat_expireLine', { date: expireAtDisplayText }) }}
      </div>
      <!-- Post to Hub 按钮（仅 clarified 且 isEmbedded 时） -->
      <van-button v-if="requirementClarified == '1' && isEmbedded && channelId && !isExpired" size="normal" type="primary" class="btn"
        @click="handlePublishToChannel"
        style="margin-top: 12px; background: #E8543A; border-color: #E8543A;" :loading="publishingToChannel"
        :disabled="publishingToChannel" :loading-text="$t('aiChat_publishing')">
        {{ $t('channel_chat_session_publishToChannel') }}
      </van-button>
      <!-- 非 embedded 模式下的按钮 -->
      <template v-if="requirementClarified == '1' && !isEmbedded">
        <!-- 独立会话发布：预拉取匹配群直达确认框 / AI 自动建群兜底 / 选群面板回落（对齐 client _publishStandalone） -->
        <van-button v-if="!isExpired && chatSessionId" size="normal" type="primary" class="btn"
          @click="handlePublishStandalone"
          style="margin-top: 12px; background: #E8543A; border-color: #E8543A;" :loading="publishingStandalone"
          :disabled="publishingStandalone" :loading-text="$t('aiChat_publishing')">
          {{ $t('channel_chat_session_publishToChannel') }}
        </van-button>
        <!-- 非 embedded 模式的公开/匹配入口：对齐 client/ —— 正文不放大按钮，
             公开由 AI 消息内嵌的 set_public 动作按钮触发（ChatMsgList emit），匹配结果经发布后自动/手动查看 -->
        <van-button v-if="public === '1'" size="normal" type="primary" class="btn" @click="handleMatchRequirement"
          style="margin-top: 12px; background: #E8543A; border-color: #E8543A;">
          {{ $t('aiChat_findMatches') }}
        </van-button>
        <!-- 暂停/恢复匹配（与公开/私密解耦：消息仍可见，仅停止/恢复 AI 持续匹配） -->
        <van-button v-if="public === '1'" plain size="normal" type="info" class="btn"
          style="margin-top: 12px; color: #4A4A4A; border-color: #E0DDD8;"
          :loading="matchPausedLoading" :disabled="matchPausedLoading" :loading-text="$t('setting')"
          @click="handleToggleMatchPaused">
          {{ matchPaused == 1 ? $t('session_resumeMatching') : $t('session_pauseMatching') }}
        </van-button>
        <van-button v-if="public === '1'" plain size="normal" type="info" class="btn"
          style="margin-top: 12px; color: #E8543A; border-color: #E8543A;"
          :loading="setPrivateLoading" :disabled="setPrivateLoading" :loading-text="$t('setting')"
          @click="handleSetPrivateGlobal">
          {{ $t('make_private') }}
        </van-button>
      </template>
    </div>
    <!-- Welcome 状态（消息为空时展示 logo + prompt message） -->
    <div v-if="!isUserChat && !hasChatMessages" class="welcome-state">
      <div class="welcome-logo-wrapper">
        <img src="/static/logo-transparent.png" class="welcome-logo" />
      </div>
      <div class="welcome-message">
        <div class="welcome-avatar">
          <img src="/static/logo-transparent.png" class="welcome-avatar-img" />
        </div>
        <div class="welcome-bubble">{{ $t('aiChat_welcome') }}</div>
      </div>
    </div>
    <!-- ChatMsgList 消息列表 -->
    <ChatMsgList ref="chatMsgList" :chatSessionId="chatSessionId" :toUserId="chat.contact.targetUserId"
      :platformUserId="platformUserId" :pageSize="30" :filterThink="1" :style="{ ...chatMsgListStyle, flex: 1, overflow: 'auto' }"
      @image-click="previewImage" @location-selected="handleLocationSelectedFromList"
      @date-selected="handleDateSelectedFromList" @requirement-clarified="handleRequirementClarified"
      @progress-update="handleProgressUpdate" @first-user-msg-extracted="handleFirstUserMsgExtracted"
      @set-public="handleSetPublicGlobal" @user-msg-options="onUserMsgOptions"
      :currentAvatarUrl="currentAvatarUrl" :currentUserName="currentUserName" :targetAvatarUrl="targetUserAvatar"
      :targetUserName="targetUserName" :currentUserId="currentUserId" :isUserChat="isUserChat"
      @avatar-click="onTargetAvatarClick" @reply="onReplyClicked" />
    <!-- 图片分析 loading 提示 -->
    <div v-if="analyzingImage" class="analyzing-image-loading"
      style="display: flex; align-items: center; justify-content: center; padding: 10px; background: #E0DDD8; color: #8A8A8A; font-size: 0.85rem;">
      <van-loading size="16" type="spinner" style="margin-right: 8px;" />
      <span>{{ $t('analyzing_image') }}</span>
    </div>

    <!-- 快捷建议：仅独立会话（无真实 channelId）展示，对齐 client/ ai_chat_page（嵌入群页不展示） -->
    <div v-if="!isUserChat && !hasChatMessages && suggestions.length > 0 && (!channelId || channelId == '20210802')" style="padding: 12px 16px 10px; background: #F7F5F2;">
      <div style="padding: 14px 16px 16px; background: #FEF0ED; border-radius: 16px; border: 1px solid #F8A090;">
        <div style="font-size: 13px; color: #E8543A; font-weight: 500; margin-bottom: 10px; text-align: left;">✨ {{ $t('aiChat_quickSuggestions') }}</div>
        <div style="display: flex; flex-wrap: wrap; gap: 8px;">
          <div v-for="(s, index) in suggestions" :key="index" style="display: inline-flex; align-items: center; background: #F7F5F2; border: 0.5px solid #F8A090; border-radius: 18px; padding: 6px 12px; cursor: pointer; font-size: 13px; color: #4A4A4A; line-height: 1.4; transition: background 0.2s;" @click="sendSuggestion(s)">
            <span style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ s }}</span>
            <van-icon name="arrow" style="font-size: 10px; color: #E8543A; margin-left: 6px; flex-shrink: 0;" />
          </div>
        </div>
      </div>
    </div>

    <div>
      <!-- 图片、文件和位置预览区域 -->
      <!-- 仅当有实际内容时才显示预览区域 -->
      <div
        v-if="uploadedFiles.length > 0 || selectedLocations.length > 0 || uploadedFilesList.length > 0 || pendingFiles.length > 0 || uploadedVoice || uploadedVideos.length > 0"
        class="image-preview-container">
        <!-- 图片预览 -->
        <div v-for="(file, index) in uploadedFiles" :key="'img-' + index" class="image-preview-item">
          <img :src="$common.getFullImageUrl(file.url, $config)" class="preview-image" />
          <div class="image-delete-btn" @click="removeFile(index)">
            <van-icon name="cross" size="12" />
          </div>
        </div>
        <!-- 待上传图片 -->
        <div v-for="(file, index) in pendingFiles" :key="'pending-img-' + index" class="image-preview-item">
          <img :src="file.url" class="preview-image" />
          <div class="uploading-overlay">
            <van-loading size="16" />
          </div>
          <div class="image-delete-btn" @click="removePendingFile(index)">
            <van-icon name="cross" size="12" />
          </div>
        </div>
        <!-- 已上传文件预览 -->
        <div v-for="(file, index) in uploadedFilesList" :key="'file-' + index" class="file-preview-item">
          <div class="file-preview-content">
            <van-icon :name="getFileIcon(file.name)" size="20" color="#E8543A" />
            <div class="file-preview-info">
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size">{{ formatFileSize(file.fileSize) }}</span>
            </div>
          </div>
          <div class="image-delete-btn" @click="removeUploadedFileItem(index)">
            <van-icon name="cross" size="12" />
          </div>
        </div>
        <!-- 已上传视频（首帧缩略图+播放角标） -->
        <div v-for="(video, index) in uploadedVideos" :key="'video-' + index" class="video-preview-item">
          <img v-if="video.thumbDataUrl" class="video-preview-thumb" :src="video.thumbDataUrl" />
          <div v-else class="video-preview-fallback">
            <van-icon name="video-o" size="20" color="#E8543A" />
          </div>
          <div class="video-preview-play">
            <van-icon name="play" size="14" color="#ffffff" />
          </div>
          <div class="image-delete-btn" @click="removeUploadedVideo(index)">
            <van-icon name="cross" size="12" />
          </div>
        </div>
        <!-- 待上传视频 -->
        <div v-for="(video, index) in pendingVideos" :key="'pending-video-' + index" class="video-preview-item">
          <img v-if="video.thumbDataUrl" class="video-preview-thumb" :src="video.thumbDataUrl" />
          <div v-else class="video-preview-fallback">
            <van-icon name="video-o" size="20" color="#E8543A" />
          </div>
          <div class="video-preview-play">
            <van-icon name="play" size="14" color="#ffffff" />
          </div>
          <div class="uploading-overlay" v-if="uploadingVideos">
            <van-loading size="16" />
          </div>
          <div class="image-delete-btn" v-else @click.stop="removePendingVideo(index)">
            <van-icon name="cross" size="12" />
          </div>
        </div>

        <!-- 位置预览 -->
        <div v-for="(location, index) in selectedLocations" :key="'loc-' + index" class="location-preview-item">
          <div class="location-preview-content">
            <van-icon name="location-o" size="16" color="#E8543A" />
            <span class="location-preview-text">{{ location.address || $t('selected_location') }}</span>
          </div>
          <div class="image-delete-btn" @click="removeLocation(index)">
            <van-icon name="cross" size="12" />
          </div>
        </div>
        <!-- 已上传语音预览（点击试听） -->
        <div v-if="uploadedVoice" class="voice-preview-item" @click="toggleVoicePreview()">
          <div class="voice-preview-content">
            <van-icon :name="voicePreviewPlaying ? 'pause-circle-o' : 'play-circle-o'" size="16" color="#E8543A" />
            <span class="voice-preview-text">{{ $t('voice') }} {{ formatRecordingDuration(uploadedVoice.duration) }}</span>
          </div>
          <div class="uploading-overlay" v-if="uploadingVoice">
            <van-loading size="16" />
          </div>
          <div class="image-delete-btn" v-else @click.stop="removeUploadedVoice()">
            <van-icon name="cross" size="12" />
          </div>
        </div>
      </div>
    </div>
    <!-- 录音状态条 -->
    <div v-if="isRecording" class="recording-bar">
      <span class="recording-dot"></span>
      <span class="recording-text">{{ $t('voice_recording') }}</span>
      <span class="recording-time">{{ formatRecordingDuration(recordingSeconds) }}</span>
      <span class="recording-cancel" @click="stopRecording(true)">{{ $t('cancel') }}</span>
      <span class="recording-done" @click="stopRecording(false)">{{ $t('confirm') }}</span>
    </div>
    <!-- 点击区域外的遮罩层，用于关闭操作面板 -->
    <div v-if="showActionPanel" class="action-panel-overlay" @click="showActionPanel = false"></div>

    <!-- 操作面板 - 显示在输入区域上方 -->
    <div v-if="showActionPanel" class="action-panel">
      <div class="action-panel-item" @click="triggerFileUpload">
        <div class="action-panel-icon" style="background: #E8543A;">
          <van-icon name="photo-o" size="20" color="white" />
        </div>
        <span class="action-panel-label">{{ $t('action_photo') }}</span>
      </div>
      <!-- 视频仅私聊支持 -->
      <div v-if="isUserChat" class="action-panel-item" @click="triggerVideoUpload">
        <div class="action-panel-icon" style="background: #E8543A;">
          <van-icon name="video-o" size="20" color="white" />
        </div>
        <span class="action-panel-label">{{ $t('action_video') }}</span>
      </div>
      <!-- 文件仅私聊支持 -->
      <div v-if="isUserChat" class="action-panel-item" @click="triggerFileInput">
        <div class="action-panel-icon" style="background: #E8543A;">
          <van-icon name="description" size="20" color="white" />
        </div>
        <span class="action-panel-label">{{ $t('action_file') }}</span>
      </div>
      <!-- 语音仅私聊支持 -->
      <div v-if="isUserChat" class="action-panel-item" @click="openVoiceRecording">
        <div class="action-panel-icon" style="background: #E8543A;">
          <!-- vant 内置图标没有 mic，用内联 SVG（与发送按钮同风格） -->
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#fff" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <path d="M12 19v4" />
            <path d="M8 23h8" />
          </svg>
        </div>
        <span class="action-panel-label">{{ $t('voice') }}</span>
      </div>
      <div class="action-panel-item" v-if="!isCnRegion" @click="openLocationPicker">
        <div class="action-panel-icon" style="background: #E8543A;">
          <van-icon name="location-o" size="20" color="white" />
        </div>
        <span class="action-panel-label">{{ $t('action_location') }}</span>
      </div>
    </div>

    <!-- Channel 选择面板 -->
    <div v-if="showInputArea && showChannelPicker" class="channel-picker-panel">
      <div style="padding: 8px 12px; border: 1px solid #E0DDD8;">
        <div style="display: flex; align-items: center; background: #E0DDD8; border-radius: 8px; padding: 0 8px;">
          <van-icon name="search" size="14" color="#999" style="flex-shrink: 0;" />
          <input ref="channelPickerSearchInput" v-model="channelPickerSearchQuery" type="text"
            :placeholder="$t('channel_search_placeholder')"
            style="flex: 1; border: none; background: transparent; padding: 8px; font-size: 14px; outline: none;" />
        </div>
      </div>
      <div style="max-height: 180px; overflow-y: auto;">
        <!-- 用户输入的新 channel 选项 -->
        <div v-if="channelPickerSearchQuery && !channelPickerSearchResults.some(c => c.name.toLowerCase() === channelPickerSearchQuery.toLowerCase())"
          class="channel-picker-item" @click="selectChannelFromPicker(channelPickerSearchQuery, true)">
          <span style="font-size: 14px; color: #1A1A1A; font-weight: 500;">#{{ channelPickerSearchQuery }}</span>
          <span style="font-size: 12px; color: #E8543A; margin-left: 6px; font-weight: 500;">{{ $t('badge_new') }}</span>
        </div>
        <!-- 搜索结果 -->
        <div v-for="channel in channelPickerSearchResults" :key="channel.channelId"
          class="channel-picker-item" :class="{ 'channel-picker-item-active': selectedChannel && channel.name.toLowerCase() === selectedChannel.toLowerCase() }"
          @click="selectChannelFromPicker(channel, false)">
          <span style="font-size: 14px; color: #1A1A1A; font-weight: 500;">#{{ channel.name }}</span>
          <van-icon v-if="selectedChannel && channel.name.toLowerCase() === selectedChannel.toLowerCase()" name="success" size="14" color="#E8543A" />
        </div>
        <!-- 空状态 -->
        <div v-if="!channelPickerSearchQuery && channelPickerSearchResults.length === 0" style="padding: 20px; text-align: center; color: #8A8A8A; font-size: 13px;">
          <span style="font-size: 24px; font-weight: bold; color: #ccc;">#</span>
          <div style="margin-top: 6px;">{{ $t('channel_picker_empty') }}</div>
        </div>
      </div>
    </div>

    <!-- AI 生成内容界面级显著提示（合规要求，仅 AI 聊天 + cn 版展示） -->
    <div v-if="showInputArea && !isUserChat && isCnRegion" class="ai-generated-notice">
      <van-icon name="info-o" size="11" color="#8A8A8A" style="margin-right: 4px; flex-shrink: 0;" />
      <span>{{ $t('ai_content_disclaimer') }}</span>
    </div>

    <div v-if="showInputArea" class="chatbox-input-bar">
      <!-- 引用（回复）条：展示被引用消息摘要，可取消 -->
      <div v-if="reply.msg" class="reply-preview-bar">
        <van-icon name="edit" size="13" color="#E8543A" style="flex-shrink:0;" />
        <span class="reply-preview-text">{{ reply.msg }}</span>
        <van-icon name="cross" size="14" color="#8A8A8A" style="flex-shrink:0;padding:6px;cursor:pointer;"
          @click="clearReply" />
      </div>
      <div class="chatbox-input-wrapper">
        <!-- + 按钮 -->
        <div class="add-btn-container" @click="toggleActionPanel">
          <van-icon :name="showActionPanel ? 'cross' : 'plus'" size="22"
            :color="showActionPanel ? '#E8543A' : '#666'" />
        </div>
        <EmojiInputArea ref="emojiInputArea" :placeholder="inputPlaceholder"
          @editor-click="handleEditorClick" @enter="onSendMsg()" />
        <!-- 隐藏的文件输入，仅支持图片 -->
        <input type="file" ref="imageInput" multiple accept="image/*" style="display: none;"
          @change="handleFileSelect" @cancel="handleFilePickerCancel" />
        <!-- 隐藏的文件输入，支持任意文件 -->
        <input type="file" ref="fileInput" style="display: none;" @change="handleFileInputChange" @cancel="handleFilePickerCancel" />
        <!-- 隐藏的视频输入，仅私聊支持 -->
        <input type="file" ref="videoInput" multiple accept="video/*" style="display: none;" @change="handleVideoInputChange" @cancel="handleFilePickerCancel" />
        <!-- Emoji 按钮（仅私聊时显示） -->
        <van-popover v-if="isUserChat" v-model="showEmojiPickerPopup" trigger="click" placement="top-end" :offset="[0, 10]"
          close-on-click-overlay @update:show="(val) => { if (!val) showEmojiPickerPopup = false }">
          <div style="display: flex; flex-direction: column; max-height: 300px; width: 320px;">
            <!-- 头部 -->
            <div
              style="display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border: 1px solid #E0DDD8;">
              <span style="font-size: 14px; color: #1A1A1A;">{{ $t('select_emoji') }}</span>
              <span style="cursor: pointer; font-size: 18px; color: #8A8A8A;"
                @click="showEmojiPickerPopup = false">×</span>
            </div>
            <!-- Emoji列表 -->
            <div style="flex: 1; overflow-y: auto; padding: 8px;">
              <div style="display: grid; grid-template-columns: repeat(8, 1fr); gap: 4px;">
                <div v-for="emoji in filteredEmojis" :key="emoji.unicode"
                  style="display: flex; justify-content: center; align-items: center; padding: 4px; cursor: pointer; border-radius: 4px;"
                  @click="selectEmoji(emoji.unicode)" v-html="parseEmoji(emoji.unicode)">
                </div>
              </div>
            </div>
            <!-- 分类导航 -->
            <div style="display: flex; padding: 8px; border: 1px solid #E0DDD8; overflow-x: auto;">
              <div v-for="category in emojiCategories" :key="category"
                :class="['emoji-category-tab', { 'active': activeEmojiCategory === category }]"
                @click="setEmojiCategory(category)" v-html="parseEmoji(emojiCategoryIcons[category])">
              </div>
            </div>
          </div>
          <template #reference>
            <span
              style="margin-right:8px;display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; vertical-align: middle; cursor: pointer; flex-shrink: 0;">
              <van-icon name="smile-o" size="38" color="#666" />
            </span>
          </template>
        </van-popover>
        <div class="chatbox-send-btn" @click="onSendMsg()">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12H19" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.5 5.5L19 12L12.5 18.5" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>

    <!-- 位置选择器弹窗 -->
    <van-popup v-model="showLocationPickerPopup" round position="bottom" :style="{ height: '70%' }">
      <div style="display: flex; flex-direction: column; height: 100%;">
        <div
          style="padding: 12px; border: 1px solid #E0DDD8; display: flex; align-items: center; justify-content: space-between;">
          <span style="font-size: 14px; font-weight: 500;">{{ $t('select_location') }}</span>
          <van-icon name="cross" size="20" color="#666" @click="showLocationPickerPopup = false" />
        </div>
        <div style="flex: 1;">
          <MapLocationPicker ref="locationPicker" width="100%" height="100%" :auto-get-location="true"
            @location-selected="handleLocationSelected" />
        </div>
      </div>
    </van-popup>

    <!-- 匹配结果弹窗（对齐 ChannelDetail MatchHistorySheet 样式） -->
    <van-popup v-model="showMatchResult" round position="bottom" :style="{ height: '85%' }">
      <div class="chatbox-match-sheet">
        <!-- 头部 -->
        <div class="chatbox-match-sheet-header">
          <div style="display: flex; align-items: center; gap: 8px;">
            <van-icon name="link-o" size="20" color="#2A8FAD" />
            <span style="font-size: 16px; font-weight: 600; color: #1A1A1A;">{{ $t('chatSessionMatch_viewMatches') }}</span>
          </div>
          <van-icon name="cross" size="22" color="#8A8A8A" @click="showMatchResult = false" style="cursor: pointer;" />
        </div>

        <!-- 主体内容区（可滚动） -->
        <div style="flex: 1; overflow-y: auto; min-height: 0;">
          <!-- 持续匹配横幅（始终展示） -->
          <div style="margin: 12px 16px 8px; padding: 14px 16px; background: linear-gradient(to right, #FEF0ED, rgba(254, 240, 237, 0.5)); border-radius: 12px; border: 0.5px solid rgba(248, 160, 144, 0.3); display: flex; align-items: center; gap: 12px;">
            <img src="/static/logo-transparent.png" class="spinning-logo" style="width: 24px; height: 24px; flex-shrink: 0;" />
            <div style="flex: 1;">
              <div style="font-size: 13px; font-weight: 600; color: #C93E1A;">{{ $t('match_continuousMatching') }}</div>
              <div style="font-size: 11px; color: #8A8A8A; margin-top: 2px;">{{ hasNewMatches ? $t('match_newMatchFound', {n: newMatchCount}) : $t('match_continuousMatchingHint') }}</div>
            </div>
            <div style="width: 8px; height: 8px; border-radius: 50%; background: #E8543A; animation: pulse-dot 1s ease-in-out infinite alternate;"></div>
          </div>

          <!-- 匹配进度指示器（仅显示当前阶段，弱化视觉） -->
          <div v-if="matchIsMatching && getStageIndex(matchCurrentStage) >= 0"
            style="text-align: center; margin: 6px 0;">
            <div style="display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; background: #F7F5F2; border-radius: 12px;">
              <span class="mini-spinner" style="width: 10px; height: 10px; border-width: 1.2px; border-color: #E0DDD8; border-top-color: #8A8A8A;"></span>
              <span style="font-size: 10px; color: #8A8A8A;">
                {{ stageLabels[getStageIndex(matchCurrentStage)].label }}
              </span>
            </div>
          </div>

          <!-- 加载中（首次加载且无结果时） -->
          <div v-if="matchLoading && matchResults.length === 0 && matchInterested.length === 0 && !matchIsMatching" style="padding: 60px 0; display: flex; justify-content: center;">
            <van-loading size="24" color="#E8543A" />
          </div>

          <!-- 匹配完成且有结果 -->
          <div v-else-if="matchIsComplete && (matchResults.length > 0 || matchInterested.length > 0)"
            style="padding: 16px;">
            <!-- 匹配结果统计头部 -->
            <div
              style="display: flex; flex-direction: column; margin-bottom: 16px; padding: 12px; background: #FEF0ED; border-radius: 8px;">
              <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px;">
                <van-icon name="friends-o" color="#E8543A" size="16" style="margin-right: 8px;" />
                <span style="font-size: 13px; font-weight: 600; color: #1A1A1A;">
                  {{ $t('match_result_stats', {matched: matchResults.length, matchedPlural: matchResults.length > 1 ? 's' : '', interested: matchInterested.length > 0 ? $t('match_interested_suffix', {count: matchInterested.length}) : ''}) }}
                </span>
                <span style="font-size: 11px; color: #E8543A;">
                  {{ $t('match_tap_to_connect') }}
                </span>
              </div>
              <!-- Channel 匹配说明 -->
              <div v-if="sessionChannel"
                style="margin-top: 10px; padding: 8px 12px; background: #FEF0ED; border-radius: 6px; display: flex; align-items: center;">
                <van-icon name="tag-o" size="12" style="margin-right: 4px; flex-shrink: 0;" />
                <span style="font-size: 11px; color: #E8543A;" v-html="$t('match_channel_hint', {channel: sessionChannel})"></span>
              </div>
            </div>
            <!-- Matched 结果 -->
            <div v-if="matchResults.length > 0">
              <div style="margin-bottom: 10px; padding: 6px 10px; background: #E8F5E9; border-radius: 4px; display: flex; align-items: center;">
                <van-icon name="checked" size="12" color="#34A853" style="margin-right: 4px; flex-shrink: 0;" />
                <span style="font-size: 11px; color: #34A853;">{{ $t('match_connection_notify_hint') }}</span>
              </div>
              <div v-for="(match, index) in matchResults" :key="'match-' + index"
                @click="goToMatchedSession(match.matchDetailId, match.knowledge_id, match.brief_match_reason, match.detailed_match_reason)"
                style="background: #F7F5F2; padding: 12px; border-radius: 8px; margin-bottom: 12px; cursor: pointer; border-left: 3px solid #F8A090;">
                <!-- 标签和 userMsg/aiSummary -->
                <div style="margin-bottom: 8px;">
                  <div style="display: flex; gap: 6px; margin-bottom: 6px;">
                    <div
                      style="background: #FEF0ED; color: #E8543A; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">
                      {{ $t('label_perfect_match') }}
                    </div>
                    <div v-if="match.channel"
                      style="background: #FEF0ED; color: #E8543A; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">
                      #{{ match.channel }}
                    </div>
                  </div>
                  <div v-if="match.matchedUserMsg && isUserMsgMixed(match.matchedUserMsg)" style="font-size: 14px; color: #1A1A1A; line-height: 1.5;">
                    <div v-for="(item, idx) in parseMixedContent(match.matchedUserMsg)" :key="'mumix-' + idx">
                      <div v-if="item.type == 'plaintext'">{{ item.content }}</div>
                      <div v-else-if="item.type == 'img'" style="margin-top: 6px;">
                        <img :src="$common.getFullImageUrl(item.content.url || '', $config)" style="width: 120px; height: 120px; object-fit: cover; border-radius: 6px;" />
                      </div>
                    </div>
                  </div>
                  <div v-else-if="match.matchedUserMsg" style="font-size: 14px; color: #1A1A1A; line-height: 1.5;">{{ $common.formatUserMsgPreview(match.matchedUserMsg) }}</div>
                  <div v-if="match.aiSummary" :style="{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: match.matchedUserMsg ? '0' : '0' }">
                    <div v-if="match.matchedUserMsg" style="height: 8px; border-bottom: 0.5px solid #E0DDD8; margin-bottom: 4px; width: 100%;"></div>
                    <span style="font-size: 10px; flex-shrink: 0; margin-top: 2px;">✨</span>
                    <span style="font-size: 11px; color: #8A8A8A; line-height: 1.4;">{{ match.aiSummary }}</span>
                  </div>
                  <!-- 对方会话私有时展示提示（后端不下发内容） -->
                  <div v-if="isPeerPrivate(match)" style="display: flex; align-items: flex-start; gap: 4px; margin-top: 4px;">
                    <van-icon name="lock" size="12" color="#8A8A8A" style="margin-top: 2px; flex-shrink: 0;" />
                    <span style="font-size: 11px; color: #8A8A8A; line-height: 1.4;">{{ $t('match_private_hint') }}</span>
                  </div>
                </div>
                <!-- 头像、name、创建时间 弱化展示 -->
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                  <div style="display: flex; align-items: center; gap: 6px;">
                    <img v-if="match.avatar" :src="$common.getFullImageUrl(match.avatar, $config, 's')"
                      style="width: 20px; height: 20px; border-radius: 4px; object-fit: cover;" />
                    <div v-else
                      style="width: 20px; height: 20px; border-radius: 4px; background: #E8543A; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: bold;">
                      {{ (match.userName || $t('anonymous'))[0] }}
                    </div>
                    <span style="font-size: 11px; color: #8A8A8A;">{{ match.userName || $t('anonymous') }}</span>
                  </div>
                  <span v-if="match.createdAt" style="font-size: 10px; color: #ccc;">{{ $common.formatDateTime(match.createdAt) }}</span>
                </div>
                <!-- brief_match_reason 匹配详情（在最底部） -->
                <div v-if="match.brief_match_reason" class="brief-match-reason-card">
                  <span class="brief-match-reason-icon">⇄</span>
                  <span class="brief-match-reason-text">{{ match.brief_match_reason }}</span>
                </div>
              </div>
            </div>

            <!-- Interested 分隔标题 -->
            <div v-if="matchInterested.length > 0 && matchResults.length > 0" style="margin-top: 20px; padding: 8px 0;">
              <div style="display: flex; align-items: center; gap: 6px;">
                <van-icon name="lightbulb" color="#E8543A" size="16" />
                <span style="font-size: 13px; font-weight: 500; color: #8A8A8A;">{{ $t('label_worth_a_look') }}</span>
              </div>
              <div style="margin-top: 6px; padding: 6px 10px; background: #FEF0ED; border-radius: 4px; display: flex; align-items: center;">
                <van-icon name="info-o" size="12" color="#E8543A" style="margin-right: 4px; flex-shrink: 0;" />
                <span style="font-size: 11px; color: #E8543A;">{{ $t('match_worth_exploring_hint') }}</span>
              </div>
            </div>

            <!-- Interested 结果 -->
            <div v-if="matchInterested.length > 0">
              <div v-for="(item, index) in matchInterested" :key="'interested-' + index"
                @click="goToMatchedSession(item.matchDetailId, item.knowledge_id, item.brief_match_reason, item.detailed_match_reason)"
                style="background: #FEF0ED; padding: 12px; border-radius: 8px; margin-bottom: 12px; cursor: pointer; border-left: 3px solid #F8A090;">
                <!-- 标签和 userMsg/aiSummary -->
                <div style="margin-bottom: 8px;">
                  <div style="display: flex; gap: 6px; margin-bottom: 6px;">
                    <div
                      style="background: #FEF0ED; color: #E8543A; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">
                      {{ $t('label_worth_a_look') }}
                    </div>
                    <div v-if="item.channel"
                      style="background: #FEF0ED; color: #E8543A; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">
                      #{{ item.channel }}
                    </div>
                  </div>
                  <div v-if="item.matchedUserMsg" style="font-size: 14px; color: #1A1A1A; line-height: 1.5;">{{ $common.formatUserMsgPreview(item.matchedUserMsg) }}</div>
                  <div v-if="item.aiSummary" :style="{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: item.matchedUserMsg ? '0' : '0' }">
                    <div v-if="item.matchedUserMsg" style="height: 8px; border-bottom: 0.5px solid #E0DDD8; margin-bottom: 4px; width: 100%;"></div>
                    <span style="font-size: 10px; flex-shrink: 0; margin-top: 2px;">✨</span>
                    <span style="font-size: 11px; color: #8A8A8A; line-height: 1.4;">{{ item.aiSummary }}</span>
                  </div>
                  <!-- 对方会话私有时展示提示（后端不下发内容） -->
                  <div v-if="isPeerPrivate(item)" style="display: flex; align-items: flex-start; gap: 4px; margin-top: 4px;">
                    <van-icon name="lock" size="12" color="#8A8A8A" style="margin-top: 2px; flex-shrink: 0;" />
                    <span style="font-size: 11px; color: #8A8A8A; line-height: 1.4;">{{ $t('match_private_hint') }}</span>
                  </div>
                </div>
                <!-- 头像、name、创建时间 弱化展示 -->
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                  <div style="display: flex; align-items: center; gap: 6px;">
                    <img v-if="item.avatar" :src="$common.getFullImageUrl(item.avatar, $config, 's')"
                      style="width: 20px; height: 20px; border-radius: 4px; object-fit: cover;" />
                    <div v-else
                      style="width: 20px; height: 20px; border-radius: 4px; background: #E8543A; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: bold;">
                      {{ (item.userName || $t('anonymous'))[0] }}
                    </div>
                    <span style="font-size: 11px; color: #8A8A8A;">{{ item.userName || $t('anonymous') }}</span>
                  </div>
                  <span v-if="item.createdAt" style="font-size: 10px; color: #ccc;">{{ $common.formatDateTime(item.createdAt) }}</span>
                </div>
                <!-- brief_match_reason 匹配详情 -->
                <div v-if="item.brief_match_reason" class="brief-match-reason-card">
                  <span class="brief-match-reason-icon">⇄</span>
                  <span class="brief-match-reason-text">{{ item.brief_match_reason }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 无匹配结果（持续匹配中） -->
          <div v-else-if="!matchLoading && matchResults.length === 0 && matchInterested.length === 0"
            style="padding: 24px; display: flex; flex-direction: column; align-items: center;">
            <img src="/static/logo-transparent.png" class="chatbox-rotating-logo"
              style="padding: 16px; background: #FEF0ED; border-radius: 16%; width: 64px; height: 64px; object-fit: contain;" />
            <div style="margin-top: 16px; font-size: 16px; font-weight: 600; color: #1A1A1A;">{{ $t('match_no_result_title') }}</div>
            <div style="margin-top: 6px; font-size: 11px; color: #E8543A; text-align: center; padding: 0 24px;">
              {{ $t('match_continuousMatchingHint') }}
            </div>
          </div>

        </div>

        <!-- 匹配流程广告：finding 转圈和结果展示期间都常驻（对齐 client matches 页） -->
        <AdBanner page="chatBoxMatch" />
      </div>
    </van-popup>

    <!-- App 下载提示弹窗 -->
    <van-popup v-model="showAppDownloadPopup" round style="padding: 20px; width: 260px; text-align: center;">
      <svg viewBox="0 0 24 24" fill="none" style="width: 40px; height: 40px; color: #E8543A; margin: 0 auto 8px;">
        <path
          d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
          stroke="currentColor" stroke-width="2" />
        <path d="M12 18V12M12 12L9 9M12 12L15 9" stroke="currentColor" stroke-width="2" stroke-linecap="round"
          stroke-linejoin="round" />
      </svg>
      <div style="font-weight: bold; font-size: 16px; color: #1A1A1A; margin-bottom: 4px;">{{ $t('stay_connected') }}</div>
      <div style="font-size: 13px; color: #8A8A8A; margin-bottom: 16px;">{{ $t('download_app_desc') }}</div>
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <a :href="appStoreUrl" target="_blank" class="app-download-btn app-store">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path
              d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.21-1.98 1.07-3.11-1.05.05-2.31.7-3.06 1.53-.68.74-1.26 1.93-1.1 3.07 1.18.09 2.38-.59 3.09-1.49z" />
          </svg>
          {{ $t('app_store') }}
        </a>
        <a :href="googlePlayUrl" target="_blank" class="app-download-btn google-play">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path
              d="M3 20.5V3.5c0-.74.8-1.21 1.45-.88l15.84 8.5c.66.35.66 1.31 0 1.66L4.45 21.38c-.65.33-1.45-.14-1.45-.88z" />
          </svg>
          {{ $t('google_play') }}
        </a>
        <a :href="apkUrl" target="_blank" class="app-download-btn apk" download>
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 16px; height: 16px;">
            <path
              d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
          </svg>
          {{ $t('direct_apk') }}
        </a>
      </div>
    </van-popup>

    <!-- 历史匹配记录弹窗 -->
    <van-popup v-model="showMatchHistoryPopup" round position="bottom" :style="{ height: '70%' }"
      :overlay-style="{ zIndex: 2002 }">
      <div style="display: flex; flex-direction: column; height: 100%; background: #F7F5F2;">
        <!-- 头部 -->
        <div
          style="padding: 16px; background: white; border: 1px solid #E0DDD8; display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <van-icon name="clock-o" color="#E8543A" size="20" />
            <span style="font-size: 16px; font-weight: 600; color: #1A1A1A;">{{ $t('match_history') }}</span>
          </div>
          <van-icon name="cross" size="20" color="#666" @click="showMatchHistoryPopup = false" />
        </div>

        <!-- 历史列表 -->
        <div style="flex: 1; overflow-y: auto; padding: 12px;">
          <!-- 加载中 -->
          <div v-if="matchHistoryLoading" style="display: flex; justify-content: center; padding: 40px;">
            <van-loading size="24" />
          </div>

          <!-- 空状态 -->
          <div v-else-if="matchHistoryList.length === 0" style="text-align: center; padding: 40px; color: #8A8A8A;">
            <van-icon name="clock-o" size="48" color="#ddd" />
            <div style="margin-top: 16px; font-size: 14px;">{{ $t('no_match_history') }}</div>
          </div>

          <!-- 历史列表 -->
          <div v-else>
            <div v-for="(item, index) in matchHistoryList" :key="index" @click="loadMatchHistoryItem(item)"
              style="background: white; padding: 12px; border-radius: 8px; margin-bottom: 12px; cursor: pointer; border-left: 3px solid #E8543A;">
              <!-- 时间和匹配级别 -->
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <div
                    :style="{ background: item.matchLevel === 'matched' ? '#FEF0ED' : '#FEF9E7', color: '#E8543A', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }">
                    {{ item.matchLevel === 'matched' ? $t('label_perfect_match') : $t('label_worth_a_look') }}
                  </div>
                  <span v-if="item.createdAt" style="font-size: 11px; color: #bbb;">{{ $common.formatDateTime(item.createdAt) }}</span>
                </div>
              </div>

              <!-- 被匹配用户的userMsg（优先展示） -->
              <div v-if="item.matchedUserMsg"
                style="font-size: 14px; color: #1A1A1A; line-height: 1.5; margin-bottom: 6px;">
                {{ $common.formatUserMsgPreview(item.matchedUserMsg) }}
              </div>
              <!-- 被匹配用户的AI摘要（缩小+特殊标识） -->
              <div v-if="item.matchedAiSummary" :style="{ display: 'flex', alignItems: 'flex-start', gap: '3px', marginBottom: '8px', marginTop: item.matchedUserMsg ? '6px' : '8px', paddingTop: item.matchedUserMsg ? '6px' : '0', borderTop: item.matchedUserMsg ? '1px solid rgba(128,128,128,0.15)' : 'none' }">
                <span style="font-size: 10px; flex-shrink: 0; color: #E8543A; background: #FEF0ED; padding: 1px 6px; border-radius: 4px; margin-top: 1px; line-height: 1.4;">{{ $t('aiChat_matchSummaryLabel') }}</span>
                <span style="font-size: 11px; color: #E8543A; line-height: 1.3;">{{ item.matchedAiSummary }}</span>
              </div>
              <!-- 对方会话私有时展示提示（后端不下发内容） -->
              <div v-if="isPeerPrivate(item)" style="display: flex; align-items: flex-start; gap: 3px; margin-bottom: 8px;">
                <van-icon name="lock" size="12" color="#8A8A8A" style="margin-top: 2px; flex-shrink: 0;" />
                <span style="font-size: 11px; color: #8A8A8A; line-height: 1.4;">{{ $t('match_private_hint') }}</span>
              </div>

              <!-- 被匹配用户信息 -->
              <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
                <img v-if="item.matchedAvatar" :src="$common.getFullImageUrl(item.matchedAvatar, $config, 's')"
                  style="width: 20px; height: 20px; border-radius: 4px; object-fit: cover;" />
                <div v-else
                  style="width: 20px; height: 20px; border-radius: 4px; background: #E8543A; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: bold;">
                  {{ (item.matchedUserName || $t('anonymous'))[0] }}
                </div>
                <span style="font-size: 11px; color: #8A8A8A;">{{ item.matchedUserName || $t('anonymous') }}</span>
              </div>

              <!-- 简要匹配原因（在最底部） -->
              <div v-if="item.briefMatchReason" class="brief-match-reason-card">
                <span class="brief-match-reason-tag">{{ $t('match_reasonTag') }}</span>
                <span class="brief-match-reason-text">{{ item.briefMatchReason }}</span>
              </div>
            </div>
            <div v-if="matchHistoryHasMore" style="padding: 12px; text-align: center;">
              <van-button size="small" :loading="matchHistoryLoadingMore" @click="loadMoreMatchHistory">{{ $t('loadMore') || 'Load more' }}</van-button>
            </div>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 发布到群确认弹窗 -->
    <van-popup v-model="showPublishConfirmPopup" round position="bottom" :style="{ maxHeight: '80%' }">
      <div style="padding: 20px 20px 24px;">
        <!-- 拖拽条 -->
        <div style="display: flex; justify-content: center; margin-bottom: 16px;">
          <div style="width: 36px; height: 4px; background: #ddd; border-radius: 2px;"></div>
        </div>
        <!-- 标题 + 重新选群（仅独立会话选定目标群时展示；嵌入群页发布目标固定不需要） -->
        <div style="display: flex; align-items: center; margin-bottom: 16px;">
          <div style="font-size: 17px; font-weight: 600; color: #1A1A1A; flex: 1;">{{ $t('channel_chat_session_publishToChannel') }}</div>
          <span v-if="publishTarget && !publishConfirmLoading" @click="repickChannel"
            style="font-size: 12px; font-weight: 500; color: #C93E1A; cursor: pointer; flex-shrink: 0;">{{ $t('aiChat_repickChannel') }}</span>
        </div>
        <!-- 选群流程进来的：缩略展示将发布到哪个群（名称 + 成员数 + 一行描述） -->
        <div v-if="publishTarget" style="display: flex; align-items: center; padding: 10px 12px; background: #FFF8F6; border: 1px solid #FEF0ED; border-radius: 10px; margin-bottom: 16px; text-align: left;">
          <img v-if="publishTarget.avatar" :src="$common.getFullImageUrl(publishTarget.avatar, $config)"
            style="width: 40px; height: 40px; border-radius: 8px; object-fit: cover; flex-shrink: 0;" />
          <div v-else style="width: 40px; height: 40px; border-radius: 8px; background: #FEF0ED; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; color: #E8543A; flex-shrink: 0;">#</div>
          <div style="flex: 1; min-width: 0; margin-left: 10px;">
            <div style="display: flex; align-items: center;">
              <span style="font-size: 14px; font-weight: 600; color: #1A1A1A; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ publishTarget.name }}</span>
              <span v-if="publishTarget.memberCnt > 0" style="display: inline-flex; align-items: center; margin-left: 6px; flex-shrink: 0;">
                <van-icon name="friends-o" size="12" color="#8A8A8A" />
                <span style="font-size: 11px; color: #8A8A8A; margin-left: 2px;">{{ publishTarget.memberCnt }}</span>
              </span>
            </div>
            <div v-if="publishTarget.description" style="font-size: 12px; color: #8A8A8A; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ publishTarget.description }}</div>
          </div>
        </div>
        <!-- 你的消息输入 -->
        <div style="margin-bottom: 8px;">
          <div style="display: flex; align-items: center; margin-bottom: 6px;">
            <span style="font-size: 13px; font-weight: 600; color: #1A1A1A;">{{ $t('aiChat_publishMsgLabel') }}</span>
            <span style="margin-left: 6px; font-size: 10px; font-weight: 600; color: #fff; background: #E8543A; padding: 1px 5px; border-radius: 4px;">{{ $t('aiChat_publishMsgRequired') }}</span>
          </div>
          <van-field v-model="publishConfirmExtraMsg" type="textarea" :maxlength="255" rows="2" :show-word-limit="false"
            :placeholder="$t('aiChat_publishExtraHint')" :disabled="publishConfirmLoading"
            style="border: 1px solid rgba(214,78,60,0.3); border-radius: 8px;" />
          <!-- 图片预览 -->
          <div v-if="firstUserMessageImages.length > 0" style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;">
            <div v-for="(imgUrl, idx) in firstUserMessageImages" :key="'pub-img-' + idx"
              style="width: 64px; height: 64px; border-radius: 6px; overflow: hidden; border: 1px solid #E0DDD8;">
              <img :src="$common.getFullImageUrl(imgUrl, $config)" style="width: 100%; height: 100%; object-fit: cover;" />
            </div>
          </div>
        </div>
        <!-- AI 建议的用户消息选项 -->
        <div v-if="userMsgOptions && userMsgOptions.length > 0" style="margin-top: 8px; margin-bottom: 16px;">
          <div style="display: flex; align-items: center; margin-bottom: 6px;">
            <span style="display: inline-flex; align-items: center; font-size: 11px; color: #8A8A8A;">
              <span style="font-size: 12px; line-height: 1; margin-right: 4px;">✨</span>{{ $t('aiChat_publishSuggestionHint') }}
            </span>
          </div>
          <div style="display: flex; flex-wrap: wrap; gap: 8px;">
            <div v-for="(opt, idx) in userMsgOptions" :key="idx"
              @click="!publishConfirmLoading && (publishConfirmExtraMsg = opt)"
              style="padding: 5px 12px; background: #FAFAF8; border-radius: 999px; border: 1px solid #E0DDD8; font-size: 12px; color: #8A8A8A; cursor: pointer; transition: all 0.15s ease;">
              {{ opt }}
            </div>
          </div>
        </div>
        <!-- 匹配摘要（AI Summary）作为系统元数据 -->
            <div v-if="aiSummary" style="margin-bottom: 16px;">
              <div style="padding: 10px 12px; background: #F7F5F2; border-radius: 8px; border: 1px solid #E0DDD8; text-align: left;">
                <div style="display: flex; align-items: flex-start; gap: 4px;">
                  <span style="display: inline-block; margin-top: 1px; padding: 1px 5px; background: #FEF0ED; color: #8A8A8A; font-size: 10px; border-radius: 4px; font-weight: 500; flex-shrink: 0; line-height: 1.5;">{{ $t('aiChat_matchSummaryLabel') }}</span>
                  <span style="font-size: 13px; color: #4A4A4A; line-height: 1.5; flex: 1;">{{ aiSummary }}</span>
                </div>
              </div>
            </div>
        <!-- 允许直接联系开关 -->
        <div style="padding: 8px 12px; background: #FAFAF8; border: 1px solid #E0DDD8; margin-bottom: 20px;">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div>
              <div style="font-size: 14px; font-weight: 500; color: #1A1A1A;">{{ $t('aiChat_allowDirectContact') }}</div>
              <div style="font-size: 11px; color: #8A8A8A; margin-top: 2px;">{{ $t('aiChat_allowDirectContactDesc') }}</div>
            </div>
            <van-switch v-model="publishConfirmAllowDirectContact" active-color="#E8543A" :disabled="publishConfirmLoading" size="22px" />
          </div>
        </div>
        <!-- 确认按钮 -->
        <van-button type="primary" :loading="publishConfirmLoading" :disabled="publishConfirmLoading || !publishConfirmExtraMsg.trim()"
          :loading-text="$t('common_sending')" @click="confirmPublishToChannel"
          style="background: #E8543A; border-color: #E8543A; border-radius: 8px; height: 44px;">
          {{ $t('channel_chat_session_publishToChannel') }}
        </van-button>
        <!-- 发布提示 -->
        <div style="display: flex; align-items: center; justify-content: center; margin-top: 12px;">
          <van-icon name="info-o" size="12" color="#8A8A8A" style="flex-shrink: 0; margin-right: 4px;" />
          <span style="font-size: 11px; color: #8A8A8A; line-height: 1.4;">{{ $t('aiChat_readyToPostHint') }}</span>
        </div>
      </div>
    </van-popup>

    <!-- 选群面板（独立会话发布流程，对齐 client _showPickChannelSheet）：agent 推荐群 + 搜索列表 + 创建新群 -->
    <van-popup v-model="showPickChannelPopup" round position="bottom" :style="{ height: '70%' }">
      <div style="display: flex; flex-direction: column; height: 100%; padding: 16px 20px 0;">
        <div style="display: flex; justify-content: center; margin-bottom: 14px; flex-shrink: 0;">
          <div style="width: 36px; height: 4px; background: #ddd; border-radius: 2px;"></div>
        </div>
        <div style="font-size: 15px; font-weight: 600; color: #1A1A1A; text-align: center; margin-bottom: 14px; flex-shrink: 0;">{{ $t('aiChat_pickChannelTitle') }}</div>
        <!-- 1）agent 推荐的匹配群：最高优先级展示 -->
        <div style="flex-shrink: 0; max-height: 42%; overflow-y: auto;">
          <div v-if="pickChannelLoadingMatches"
            style="padding: 12px; background: #FFF8F6; border: 1px solid #FEF0ED; border-radius: 10px; margin-bottom: 8px;">
            <!-- agent 查重要 2~3 次 LLM 调用（几秒到半分钟），裸 spinner 用户不知道在等什么：
                 与"创建中"卡片同一套结构（标题+说明+慢速进度条）解释等待 -->
            <div style="display: flex; align-items: center;">
              <div style="width: 40px; height: 40px; border-radius: 8px; background: #FEF0ED; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                <van-loading size="18" color="#E8543A" />
              </div>
              <div style="flex: 1; min-width: 0; margin-left: 10px;">
                <div style="font-size: 14px; font-weight: 600; color: #1A1A1A;">{{ $t('aiChat_matchingChannelTitle') }}</div>
                <div style="font-size: 12px; color: #8A8A8A; margin-top: 2px; line-height: 1.4;">{{ pickChannelWaitSlow ? $t('aiChat_waitSlowHint') : $t('aiChat_matchingChannelHint') }}</div>
              </div>
            </div>
            <div class="pick-channel-slow-bar" style="margin-top: 10px;"><div class="pick-channel-slow-bar-thumb"></div></div>
          </div>
          <template v-else-if="pickChannelMatches.length > 0">
            <div style="font-size: 13px; font-weight: 600; color: #C93E1A; margin-bottom: 4px;">{{ $t('aiChat_matchSectionTitle') }}</div>
            <div v-for="m in pickChannelMatches" :key="'pm-' + m.channelId" @click="pickChannelById(m)"
              style="display: flex; align-items: center; padding: 10px 12px; background: #FFF8F6; border: 1px solid #FEF0ED; border-radius: 10px; margin-bottom: 8px; cursor: pointer;">
              <img v-if="m.avatar" :src="$common.getFullImageUrl(m.avatar, $config)"
                style="width: 40px; height: 40px; border-radius: 8px; object-fit: cover; flex-shrink: 0;" />
              <div v-else style="width: 40px; height: 40px; border-radius: 8px; background: #FEF0ED; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; color: #E8543A; flex-shrink: 0;">#</div>
              <div style="flex: 1; min-width: 0; margin-left: 10px;">
                <div style="display: flex; align-items: center;">
                  <span style="font-size: 14px; font-weight: 600; color: #1A1A1A; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ m.name }}</span>
                  <span v-if="m.joined" style="flex-shrink: 0; margin-left: 6px; background: #FEF0ED; color: #C93E1A; font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 4px; line-height: 1.4;">{{ $t('aiChat_joinedBadge') }}</span>
                  <span v-if="m.memberCnt > 0" style="display: inline-flex; align-items: center; margin-left: 6px; flex-shrink: 0;">
                    <van-icon name="friends-o" size="12" color="#8A8A8A" />
                    <span style="font-size: 11px; color: #8A8A8A; margin-left: 2px;">{{ m.memberCnt }}</span>
                  </span>
                  <van-icon name="arrow" size="14" color="#F07058" style="margin-left: auto; flex-shrink: 0;" />
                </div>
                <div v-if="m.description" style="font-size: 12px; color: #8A8A8A; margin-top: 2px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-align: left;">{{ m.description }}</div>
              </div>
            </div>
          </template>
          <!-- AI 自动建群进行中卡片（对应 client creatingGroup 卡片 + 匀速慢速进度条） -->
          <div v-else-if="pickChannelCreating" style="padding: 12px; background: #FFF8F6; border: 1px solid #FEF0ED; border-radius: 10px; margin-bottom: 8px;">
            <div style="display: flex; align-items: center;">
              <div style="width: 40px; height: 40px; border-radius: 8px; background: #FEF0ED; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                <van-loading size="18" color="#E8543A" />
              </div>
              <div style="flex: 1; min-width: 0; margin-left: 10px;">
                <div style="font-size: 14px; font-weight: 600; color: #1A1A1A;">{{ $t('aiChat_creatingChannelTitle') }}</div>
                <div style="font-size: 12px; color: #8A8A8A; margin-top: 2px; line-height: 1.4;">{{ pickChannelWaitSlow ? $t('aiChat_creatingSlowHint') : $t('aiChat_creatingChannelHint') }}</div>
              </div>
            </div>
            <div class="pick-channel-slow-bar" style="margin-top: 10px;"><div class="pick-channel-slow-bar-thumb"></div></div>
          </div>
          <!-- 推荐结果不可用（requirementDetail 未落库/请求失败/后端标 pending）：
               与"真没有匹配的群"严格区分——不显示误导的空态文案、不出"创建新群"引导 -->
          <div v-else-if="pickChannelSuggestPending"
            style="display: flex; align-items: flex-start; padding: 12px; background: #F7F5F2; border-radius: 10px; margin-bottom: 8px;">
            <van-icon name="info-o" size="18" color="#8A8A8A" style="flex-shrink: 0; margin-top: 1px;" />
            <div style="flex: 1; min-width: 0; margin-left: 10px;">
              <div style="font-size: 14px; font-weight: 600; color: #1A1A1A;">{{ $t('aiChat_matchUnavailableTitle') }}</div>
              <div style="font-size: 12px; color: #8A8A8A; margin-top: 2px; line-height: 1.4;">{{ $t('aiChat_matchUnavailableHint') }}</div>
            </div>
          </div>
          <!-- 无推荐群：兜底一键 AI 建群 -->
          <div v-else style="text-align: center; padding: 8px 0;">
            <div style="font-size: 12px; color: #8A8A8A; line-height: 1.5;">{{ $t('aiChat_matchSectionEmpty') }}</div>
            <van-button size="small" type="primary" @click="autoCreateAndPick"
              style="margin-top: 10px; background: #E8543A; border-color: #E8543A; border-radius: 8px; font-weight: 600;">
              {{ $t('aiChat_suggestPublishCreateNew') }}
            </van-button>
          </div>
        </div>
        <!-- 2）群搜索框 -->
        <div style="display: flex; align-items: center; background: #E0DDD8; border-radius: 18px; padding: 0 10px; margin-top: 10px; flex-shrink: 0;">
          <van-icon name="search" size="14" color="#8A8A8A" style="flex-shrink: 0;" />
          <input ref="pickChannelSearchInput" v-model="pickChannelSearchQuery" type="text"
            :placeholder="$t('chatInput_channelSearchHint')" @keydown.enter="doPickChannelSearch(pickChannelSearchQuery)"
            style="flex: 1; border: none; background: transparent; padding: 8px; font-size: 14px; outline: none;" />
        </div>
        <!-- 3）搜索结果列表 -->
        <div style="flex: 1; overflow-y: auto; margin-top: 6px; padding-bottom: 16px;">
          <div v-if="pickChannelSearching" style="display: flex; justify-content: center; padding: 16px 0;">
            <van-loading size="20" />
          </div>
          <template v-else>
            <!-- 输入的群名无精确匹配：直达「用此名建群」 -->
            <div v-if="pickChannelSearchQuery && !pickChannelResults.some(c => c.name.toLowerCase() === pickChannelSearchQuery.toLowerCase())"
              @click="createChannelFromPick(pickChannelSearchQuery)" style="display: flex; align-items: center; padding: 8px 4px; cursor: pointer;">
              <span style="font-size: 13px; color: #1A1A1A; font-weight: 500;">#{{ pickChannelSearchQuery }}</span>
              <span style="font-size: 12px; color: #E8543A; margin-left: 6px; font-weight: 500;">{{ $t('badge_new') }}</span>
            </div>
            <div v-for="c in pickChannelResults" :key="'pc-' + c.channelId" @click="pickChannelById(c)"
              style="display: flex; align-items: center; padding: 7px 4px; cursor: pointer;">
              <img v-if="c.avatar" :src="$common.getFullImageUrl(c.avatar, $config)"
                style="width: 28px; height: 28px; border-radius: 6px; object-fit: cover; flex-shrink: 0;" />
              <div v-else style="width: 28px; height: 28px; border-radius: 6px; background: #FEF0ED; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #E8543A; flex-shrink: 0;">#</div>
              <div style="flex: 1; min-width: 0; margin-left: 10px;">
                <div style="display: flex; align-items: center;">
                  <span style="font-size: 13px; color: #4A4A4A; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ c.name }}</span>
                  <span v-if="c.memberCnt > 0" style="display: inline-flex; align-items: center; margin-left: 6px; flex-shrink: 0;">
                    <van-icon name="friends-o" size="11" color="#8A8A8A" />
                    <span style="font-size: 11px; color: #8A8A8A; margin-left: 2px;">{{ c.memberCnt }}</span>
                  </span>
                </div>
                <div v-if="c.description" style="font-size: 11px; color: #8A8A8A; margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: left;">{{ c.description }}</div>
              </div>
            </div>
            <div v-if="pickChannelHasMore" @click="loadMorePickChannelResults"
              style="text-align: center; padding: 10px 0; color: #8A8A8A; font-size: 13px; cursor: pointer;">
              {{ $t('aiChat_loadMore') }}
            </div>
            <!-- 列表末尾「创建新群」入口：聚焦搜索框输入新群名（上方会出现 #名称 NEW 直达创建） -->
            <div @click="focusPickChannelSearchInput"
              style="display: flex; align-items: center; justify-content: center; padding: 14px 0; color: #8A8A8A; font-size: 12px; cursor: pointer;">
              <van-icon name="plus" size="14" style="margin-right: 4px;" />
              {{ $t('aiChat_suggestPublishCreateNew') }}
            </div>
          </template>
        </div>
        <!-- 广告：等待 agent 选群/自动建群的十几秒~1分钟里用户盯着面板，viewability 最高；结果出来后常驻展示 -->
        <AdBanner type="banner" page="pickChannel" style="flex-shrink: 0; margin: 0 -20px;" />
      </div>
    </van-popup>

    <!-- 对方用户 profile 弹窗（点击消息头像） -->
    <ContactMethods :visible.sync="showProfilePopup" :targetUserId="profileUserId" :userName="profileUserName"
      :userAvatar="profileUserAvatar" :userBio="profileUserBio" />
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import twemoji from 'twemoji'
import emojiData from 'emoji-datasource/emoji.json'
import EmojiInputArea from './EmojiInputArea.vue'
import ChatMsgList from './ChatMsgList.vue'
import ContactMethods from './ContactMethods.vue'
import MapLocationPicker from './MapLocationPicker.vue'
import AdBanner from './AdBanner.vue'
import wsService from '@/services/websocket.js'

import { Col, Row, Button, Popup, Toast, Dialog, DropdownMenu, DropdownItem, Icon, Popover, Loading, ImagePreview, Field, Search, ActionSheet } from 'vant'

Vue.use(Col)
Vue.use(Row)
Vue.use(Button)
Vue.use(Popup)
Vue.use(Toast)
Vue.use(Dialog)
Vue.use(DropdownMenu);
Vue.use(DropdownItem);
Vue.use(Icon);
Vue.use(Popover);
Vue.use(Loading);
Vue.use(ImagePreview);
Vue.use(Field);
Vue.use(Search);
Vue.use(ActionSheet);

export default {
  name: 'ChatBox',
  props: {
    chatBoxType: String,
    targetUserId: String,
    targetUserAvatar: String,
    targetUserName: String,
    currentAvatarUrl: String,
    currentUserName: String,
    channelId: String,
    // 外部传入的 chatSessionId（如在 ChannelDetail 中点击他人消息查看详情时）
    // 优先级高于 URL query 和 loadLastSession
    initialChatSessionId: {
      type: String,
      default: ''
    },
    // 搜索结果跳转：进入聊天后自动定位到该消息（ChatMsgList.locateMessage）
    initialLocateMsgId: {
      type: String,
      default: ''
    },
    channelName: String,
    initialMessage: String,
    initialContentType: String,
    initialMsgContent: String,
    // true 时不恢复上次未发布的草稿 session（ChannelDetail 在用户 reset 过 AI 会话后使用），弹出来是干净的新会话
    skipRestore: {
      type: Boolean,
      default: false
    },
    isEmbedded: {
      type: Boolean,
      default: false
    },
    // 独立会话模式（AiChatPopup 全局弹窗）：完全不收养 URL 里的 chatSessionId，
    // 否则用户从深链（如 /channel/5?chatSessionId=99）落地后再打开弹窗，
    // 弹窗会加载 URL 上那个旧 session 并可能把别人的会话发布出去
    standalone: {
      type: Boolean,
      default: false
    },
    chatMsgListStyle: {
      type: Object,
      default: () => ({
        minHeight: '280px',
        maxHeight: '50vh',
        backgroundColor: '#ECEAE6',
        padding: '12px'
      })
    },
    showRequirementButtons: {
      type: Boolean,
      default: true
    },
    showInputArea: {
      type: Boolean,
      default: true
    },
    showUserMsgBanner: {
      type: Boolean,
      default: true
    }
  },
  components: {
    'EmojiInputArea': EmojiInputArea,
    'ChatMsgList': ChatMsgList,
    'MapLocationPicker': MapLocationPicker,
    'AdBanner': AdBanner,
    'ContactMethods': ContactMethods,
  },
  data() {
    var platformUserId = '20210802'
    var isUserChat = this.targetUserId && this.targetUserId != platformUserId
    // chatSessionId 优先级：initialChatSessionId prop > URL query > 空（后续由 loadLastSession 填充）
    // standalone 模式跳过 URL query，避免全局弹窗收养深链上的 session
    var chatSessionId = this.initialChatSessionId || (isUserChat || this.standalone ? '' : (this.$route.query.chatSessionId || ''))
    return {
      chatSessionId: chatSessionId,
      platformUserId: platformUserId,
      // 对方用户 profile 弹窗（点击消息头像）
      showProfilePopup: false,
      profileUserId: '',
      profileUserName: '',
      profileUserAvatar: '',
      profileUserBio: '',
      contactPopoverType: 'notifyUsage',
      mountedExecuted: false, // 防止 doMounted 重复执行
      // 私聊页内搜索
      showSearchPanel: false,
      msgSearchKeyword: '',
      msgSearchResults: [],
      msgSearchLoading: false,
      msgSearchDone: false,
      // chatShow: false,
      chat: {
        contact: {
          toUserId: platformUserId, // 20210802 代表是给平台在聊天
          targetUserId: this.targetUserId || platformUserId, // 使用传入的 targetUserId，默认为平台用户ID
        },
      },
      contact: {
        loading: false,
        finished: true,
        refreshing: false,
        curPage: 1,
        pageSize: 10,
        chatContacts: [],
      },
      show: false,
      reply: {
        msg: '',
        // 声明全部字段保证 Vue 2 响应式（后续动态赋值也能驱动视图）
        msgId: '',
        name: '',
        userId: '',
        userType: '',
        type: ''
      },
      insertAtCursor: null, //弹出@列表的光标位置
      preLoadScrollHeight: 0,
      preLoadScrollTop: 0,
      eventHandlers: {
        preventDefault: null,
        stopPropagation: null
      },
      // 图片上传相关
      uploadedFiles: [], // 存储已上传图片信息 {url, type, name}
      uploadingImages: false, // 是否正在上传
      maxImages: 3, // 最大上传图片数量
      // 待上传图片列表
      pendingFiles: [],
      // 文件上传相关（仅私聊支持）
      uploadedFilesList: [], // 存储已上传文件信息 {url, type, name, fileSize}
      pendingFilesList: [], // 待上传文件列表（本地URL）
      uploadingFiles: false, // 是否正在上传文件
      maxFiles: 5, // 最大上传文件数量
      // 视频上传相关（仅私聊支持）
      uploadedVideos: [], // 存储已上传视频信息 {url, type, name, fileSize, duration}
      pendingVideos: [], // 待上传视频列表（本地URL）
      uploadingVideos: false, // 是否正在上传视频
      maxVideos: 3, // 最大上传视频数量
      // 语音消息录制相关
      uploadedVoice: null, // 已上传的语音 {url, name, fileSize, duration}
      uploadingVoice: false, // 是否正在上传语音
      voicePreviewPlaying: false, // 是否正在试听语音预览
      isRecording: false, // 是否正在录音
      recordingSeconds: 0, // 已录制秒数
      recordingTimer: null, // 录音计时器
      mediaRecorder: null, // MediaRecorder 实例
      audioChunks: [], // 录音数据块
      // 操作面板显示状态
      showActionPanel: false,
      // 地图位置选择相关
      selectedLocations: [], // 存储选中的位置信息 {address, lng, lat}
      // 需求澄清相关
      requirementClarified: '0', // 是否需要澄清需求
      public: '0', // 是否可配对
      actualPublic: 0, // 实际的 public 值（0=初始状态, 1=公开, -1=用户主动设置为私有）
      matchPaused: 0, // 匹配暂停状态（0=匹配中，1=暂停匹配，消息仍可见仅停止匹配）
      matchPausedLoading: false,
      expireAt: null, // 需求有效截止时间（Unix毫秒时间戳字符串），null表示无截止时间
      // 匹配结果相关
      showMatchResult: false, // 是否显示匹配结果弹窗
      matchResults: [], // 匹配结果列表（matched 级别）
      matchInterested: [], // 建议结果列表（interested 级别）
      matchLoading: false, // 匹配加载状态
      matchPage: 1, // 匹配结果当前页
      matchPageSize: 5, // 每页显示数量
      matchIsComplete: false, // 初始加载是否完成
      // 持续匹配相关
      matchRefreshTimer: null, // 持续匹配刷新定时器
      knownMatchIds: new Set(), // 已知的匹配 session ID 集合
      hasNewMatches: false, // 是否有新匹配
      newMatchCount: 0, // 新匹配数量
      // 匹配进度相关（纯前端模拟）
      matchProgressTimer: null, // 模拟进度定时器
      matchCurrentStage: 'FETCH_CHAT', // 当前匹配阶段
      matchIsMatching: false, // 是否正在匹配中
      // 地图相关 - 已移至 MapLocationPicker 组件
      selectedLocation: null, // 用户选中的位置
      // 图片分析 loading 状态
      analyzingImage: false, // 是否正在分析图片
      // 位置选择器弹窗
      showLocationPickerPopup: false, // 是否显示位置选择器弹窗
      setPublicLoading: false, // 设置公开状态的loading
      setPrivateLoading: false, // 设置私有状态的loading
      // App 下载提示弹窗
      showAppDownloadPopup: false,
      appStoreUrl: 'https://apps.apple.com/us/app/octosay/id6760101997',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=ai.octopie.client',
      apkUrl: 'https://download.octosay.ai/octosay.apk',
      // Emoji选择相关
      showEmojiPickerPopup: false, // 是否显示emoji选择器弹窗
      activeEmojiCategory: 'Smileys & Emotion', // 当前emoji分类
      emojiCategories: [
        'Smileys & Emotion',
        'People & Body',
        'Animals & Nature',
        'Food & Drink',
        'Travel & Places',
        'Activities',
        'Objects',
        'Symbols',
        'Flags'
      ],
      emojiCategoryIcons: {
        'Smileys & Emotion': '😀',
        'People & Body': '👋',
        'Animals & Nature': '🐻',
        'Food & Drink': '🍔',
        'Travel & Places': '✈️',
        'Activities': '⚽',
        'Objects': '💡',
        'Symbols': '❤️',
        'Flags': '🏳️'
      },
      allEmojis: [], // 所有emoji对象（包含category）
      // 用户聊天 match detail 相关
      userMatchDetail: null, // 匹配详情
      userMatchDetailLoading: false, // 加载状态
      userMatchSourceChatSessionId: null, // 当前用户的 chatSessionId
      // 首页 discover 相关
      hasLoadedLastSession: false, // 是否成功加载了上一次 session
      // Stream 等待状态
      isWaitingForStream: false, // 是否正在等待 AI stream 响应
      _isSending: false, // 防止 onSendMsg 并发调用的锁
      streamTimeoutId: null, // stream 超时计时器
      lastStreamRefreshTime: null, // 上次刷新 stream 超时计时器的时间戳（节流用）
      // 新建会话标志
      isCreatingNewSession: false, // 是否正在创建新会话（由 watch 设置，mounted 检查）
      // 历史匹配记录相关
      showMatchHistoryPopup: false, // 是否显示历史匹配记录弹窗
      matchHistoryList: [], // 历史匹配记录列表
      matchHistoryLoading: false, // 历史匹配记录加载状态
      matchHistoryCurPage: 1,
      matchHistoryHasMore: false,
      matchHistoryLoadingMore: false,
      // Channel 相关
      selectedChannel: '', // 当前选中的 channel
      sessionChannel: '', // 会话已设置的 channel（从后端获取）
      needCreateChannel: false, // 选中的 channel 是否需要创建
      showChannelPicker: false, // 是否显示频道选择面板
      channelPickerSearchQuery: '', // 频道选择面板搜索关键词
      channelPickerSearchResults: [], // 频道选择面板搜索结果
      // 群相关（新增）
      sessionChannelId: '', // 会话已设置的 channelId（String 类型）
      selectedChannelId: '', // 当前选中的 channelId
      publishingToChannel: false, // 是否正在发布到群
      allowDirectContact: false, // 是否允许直接联系
      showPublishConfirmPopup: false, // 发布确认弹窗
      publishConfirmAllowDirectContact: true, // 发布弹窗中的允许直接联系开关（默认开启）
      publishConfirmExtraMsg: '', // 发布弹窗中的额外消息
      publishConfirmLoading: false, // 发布确认弹窗中的loading状态
      // 独立会话发布流程（对应 client/ ai_chat_page 的预拉取匹配群 + AI 自动建群 + 选群面板）
      publishingStandalone: false, // 独立会话「Post to Hub」按钮 loading
      publishTarget: null, // 已选定的目标群 { channelId, name, description, avatar, memberCnt }（发布确认弹窗展示卡片；null=嵌入模式走 channelId）
      showPickChannelPopup: false, // 选群面板
      pickChannelMatches: [], // Milvus 向量匹配的推荐群
      pickChannelLoadingMatches: true, // 推荐群加载中
      pickChannelSuggestPending: false, // 推荐结果是否不可用（requirementDetail 未落库/请求失败/后端标 pending）：与"真没有匹配的群"严格区分，渲染不同提示
      pickChannelWaitSlow: false, // 等待超过 20s 后切换为"慢速"安抚文案（匹配中/建群中卡片共用）
      pickChannelCreating: false, // AI 自动建群进行中（面板内展示建群卡片）
      pickChannelSearchQuery: '', // 选群面板搜索关键词
      pickChannelResults: [], // 选群面板搜索结果
      pickChannelSearching: false, // 选群面板搜索中
      pickChannelHasMore: false, // 搜索结果是否还有下一页
      pickChannelLoadingMore: false, // 加载更多中
      // 需求澄清进度相关
      completeness: null, // AI 评估的需求澄清完备度 (0-100)
      estimatedRemainingRounds: null, // AI 预估还需多少轮对话
      // 快捷建议
      suggestions: [],
      hasChatMessages: false,
      // 新增：requirementDetail / summary
      requirementDetail: '',
      aiSummary: '',
      userMsgOptions: [], // AI 建议的用户消息选项
      firstUserMessageText: '', // 用户第一条实质性消息，发布时默认作为 userMsg
      firstUserMessageImages: [], // 用户第一条消息中的图片URL列表，发布时组装到 userMsg
      pendingInitialMessage: null, // 外部传入的 initialMessage，等输入区就绪后发送
    }
  },
  computed: {
    // 页内搜索关键词是否有效：至少 2 个字（rune），与后端 ngram 最小 token=2 对齐，单字必空结果
    inChatKeywordValid() {
      return Array.from((this.msgSearchKeyword || '').trim()).length >= 2
    },
    publicHintText() {
      return this.public == '1'
        ? this.$t('aiChat_needsClarifiedPublic')
        : this.$t('aiChat_needsClarifiedPrivate');
    },
    isExpired() {
      if (!this.expireAt || !this.expireAt.toString().trim()) return false;
      var ms = parseInt(this.expireAt);
      if (isNaN(ms) || ms <= 0) return false;
      return new Date(ms) < new Date();
    },
    expireAtDisplayText() {
      if (!this.expireAt || !this.expireAt.toString().trim()) return '';
      var ms = parseInt(this.expireAt);
      if (isNaN(ms) || ms <= 0) return '';
      var dt = new Date(ms);
      var y = dt.getFullYear();
      var M = (dt.getMonth() + 1).toString().padStart(2, '0');
      var d = dt.getDate().toString().padStart(2, '0');
      var h = dt.getHours().toString().padStart(2, '0');
      var m = dt.getMinutes().toString().padStart(2, '0');
      return y + '-' + M + '-' + d + ' ' + h + ':' + m;
    },
    hasExpireAt() {
      if (!this.expireAt || !this.expireAt.toString().trim()) return false;
      var ms = parseInt(this.expireAt);
      return !isNaN(ms) && ms > 0;
    },
    beforeEatchInitialized() {
      return this.$store.state.beforeEatchInitialized;
    },
    // 过滤后的emoji列表
    filteredEmojis() {
      return this.allEmojis.filter(emoji => {
        return emoji.category === this.activeEmojiCategory;
      });
    },
    // 匹配阶段标签
    stageLabels() {
      return [
        { key: 'FETCH_CHAT', label: this.$t('match_stage_fetch_chat') },
        { key: 'QUERY_EXPANSION', label: this.$t('match_stage_query_expansion') },
        { key: 'SEMANTIC_SEARCH', label: this.$t('match_stage_semantic_search') },
        { key: 'DEEP_SEARCH', label: this.$t('match_stage_deep_search') },
        { key: 'RERANK', label: this.$t('match_stage_rerank') },
        { key: 'REFINING', label: this.$t('match_stage_refining') },
        { key: 'FETCH_DETAILS', label: this.$t('match_stage_fetch_details') },
      ]
    },
    // 是否展示需求澄清按钮（step 卡片）
    shouldShowRequirementButtons() {
      if (!this.showRequirementButtons) {
        return false;
      }
      // 始终展示 step 卡片（与 client/ AiChatPage._buildProgressSteps 对齐）
      return true;
    },
    // 置顶展示用户自己的话（始终可见，不被 AI 接管）
    displayUserMsg() {
      if (this.firstUserMessageText && this.firstUserMessageText.trim()) return this.firstUserMessageText
      if (this.pendingInitialMessage && this.pendingInitialMessage.trim()) return this.pendingInitialMessage
      if (this.initialMessage && this.initialMessage.trim()) return this.initialMessage.trim()
      return ''
    },
    // 是否展示 channel 或状态提示
    shouldShowChannelOrStatus() {
      if (this.isUserChat) return false;
      var hasChannel = (this.selectedChannel && this.selectedChannel.trim()) || (this.sessionChannel && this.sessionChannel.trim());
      var hasMessages = false;
      try {
        hasMessages = this.$refs.chatMsgList && this.$refs.chatMsgList.chat && this.$refs.chatMsgList.chat.chatMsgs && this.$refs.chatMsgList.chat.chatMsgs.length > 0;
      } catch (e) {}
      return hasChannel || hasMessages;
    },
    // 是否为用户聊天（targetUserId 不是平台用户）
    isUserChat() {
      return this.targetUserId && this.targetUserId != this.platformUserId;
    },
    // cn 版（AI 生成内容标识合规提示仅在 cn 展示）
    isCnRegion() {
      return this.$config && this.$config.region === 'cn';
    },
    // 根据登录状态和聊天类型返回输入框的 placeholder
    inputPlaceholder() {
      if (!this.$store.state.alreadyLogin) return this.$t('placeholder_login_to_chat');
      return this.isUserChat ? this.$t('userChat_typeMessage') : '';
    },
    // 需求澄清进度卡片副标题
    clarifyingSubtitle() {
      const remaining = this.estimatedRemainingRounds
      if (remaining === 0) {
        return this.$t('aiChat_clarifyingAlmostDone')
      } else if (remaining === 1) {
        return this.$t('aiChat_clarifyingOneMore')
      } else if (remaining != null && remaining > 1) {
        return this.$t('aiChat_clarifyingRemaining', { count: remaining })
      }
      return this.$t('aiChat_clarifyingEvaluating')
    },
    // 当前用户ID（用于举报功能）
    currentUserId() {
      return (this.$store.state.user && this.$store.state.user.userId) || wsService.userId || '';
    },
  },
  watch: {
    showMatchResult(newVal) {
      // 弹窗关闭时清理持续匹配定时器
      if (!newVal && this.matchRefreshTimer) {
        clearInterval(this.matchRefreshTimer)
        this.matchRefreshTimer = null
      }
      // 弹窗关闭时清理进度轮询定时器
      if (!newVal && this.matchProgressTimer) {
        clearInterval(this.matchProgressTimer)
        this.matchProgressTimer = null
      }
    },
    beforeEatchInitialized(newValue, oldValue) {
      if (newValue) {
        this.doMounted(); // 箭头函数自动绑定外层 this
      }
    },
    targetUserId(newVal, oldVal) {
      // 监听 targetUserId 变化，动态更新聊天对象
      console.log('[DEBUG] ChatBox watch targetUserId - newVal:', newVal, 'oldVal:', oldVal)
      if (newVal && newVal !== oldVal) {
        this.chat.contact.targetUserId = newVal
        var platformUserId = '20210802'
        var isUserChat = newVal && newVal != platformUserId

        // 如果是初始化（oldVal 是 undefined），不设置 chatSessionId
        // 让 mounted() 中的 loadLastSession() 来处理，避免异步冲突
        if (oldVal === undefined) {
          console.log('[DEBUG] ChatBox watch targetUserId - initial load, skip chatSessionId handling')
          return
        }

        // 只有在真正的变化时（非初始化）才处理 chatSessionId
        if (isUserChat) {
          this.chatSessionId = ''
        } else if (!this.chatSessionId) {
          // 只有在当前没有 chatSessionId 时，才从 URL 获取或生成
          this.chatSessionId = this.$route.query.chatSessionId || ''
        }
        console.log('[DEBUG] ChatBox watch targetUserId - isUserChat:', isUserChat, 'chatSessionId:', this.chatSessionId)
        // 刷新聊天消息列表
        if (this.$refs.chatMsgList) {
          this.$refs.chatMsgList.refresh()
        }
      }
    },
    // 监听 URL 中的 chatSessionId 变化，防止从 history 点击不同 session 时 chatSessionId 串了
    '$route.query.chatSessionId'(newVal, oldVal) {
      // standalone 模式（AiChatPopup 全局弹窗）不收养 URL 上的 session
      if (this.standalone) {
        return
      }
      console.log('[DEBUG] ChatBox watch $route.query.chatSessionId - newVal:', newVal, 'oldVal:', oldVal, 'current chatSessionId:', this.chatSessionId)
      // 只在和当前 chatSessionId 不一致时才处理，避免重复加载
      if (newVal && newVal !== this.chatSessionId) {
        console.log('[DEBUG] ChatBox watch $route.query.chatSessionId - updating chatSessionId from', this.chatSessionId, 'to', newVal)
        this.chatSessionId = newVal
        // 重置进度状态
        this.completeness = null
        this.estimatedRemainingRounds = null
        // 重新加载会话状态
        this.loadChatSession()
        // 刷新聊天消息列表
        if (this.$refs.chatMsgList) {
          this.$refs.chatMsgList.refresh()
        }
      }
    },
    // 监听 initialChatSessionId prop 变化
    initialChatSessionId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.chatSessionId = newVal
        // 刷新聊天消息列表
        if (this.$refs.chatMsgList) {
          this.$refs.chatMsgList.refresh()
        }
      }
    },
    // 选群面板打开时触发首次拉取（初始拉取只发起一次；放 watch 而不是模板渲染里，避免面板 rebuild 重复拉）
    showPickChannelPopup(newVal) {
      if (newVal) {
        this.pickChannelInitialFetch()
      }
    },
    // 监听频道选择面板搜索关键词变化，防抖搜索
    channelPickerSearchQuery(newVal) {
      if (this.showChannelPicker) {
        clearTimeout(this._channelPickerSearchTimer);
        this._channelPickerSearchTimer = setTimeout(() => {
          this.doChannelPickerSearch(newVal);
        }, 200);
      }
    }
  },
  created() {
    // 恢复一下状态，因为可能从别的地方调用函数发送邀约等
    const reply = window.localStorage.getItem('chatReply');
    if (reply) {
      this.reply = JSON.parse(reply);
      console.log('reply', reply)
    }
    // 初始化emoji列表
    this.initializeEmojis()
    // 独立会话发布流程的 Promise/结果缓存（按 sessionId 绑定，存实例上避免 Vue 观察 Promise）
    // 对应 client/ ai_chat_page：_autoCreateFuture 等去重锁
    this._autoCreateSessionId = ''
    this._autoCreateFuture = null
    this._autoCreatedResult = null
    this._autoCreateFlowActive = false
    this._pickChannelAutoAdvanced = false
  },
  beforeDestroy() { // 函数不被调用，不知道为啥，避免在里面写代码
    console.log('in beforeDestroy')
    let vm = this

    // 清除当前聊天对象状态
    this.$store.commit('setCurrentChatWithUserId', null)
    // 清理持续匹配定时器
    if (this.matchRefreshTimer) {
      clearInterval(this.matchRefreshTimer)
      this.matchRefreshTimer = null
    }
    // 使用名称移除 WebSocket 事件监听
    wsService.off('registered', 'chatbox_registered')
    wsService.off('msg', 'chatbox_msg')
    wsService.off('server_ack', 'chatbox_server_ack')
    wsService.off('endpoint_ack', 'chatbox_endpoint_ack')
    wsService.off('send_failed', 'chatbox_send_failed')
    wsService.off('msg_blocked', 'chatbox_msg_blocked')

    if (vm.pullChatStatusHandle) {
      clearInterval(vm.pullChatStatusHandle)
      vm.pullChatStatusHandle = null
    }

    if (vm._msgCheckInterval) {
      clearInterval(vm._msgCheckInterval)
      vm._msgCheckInterval = null
    }

    const popovers = document.querySelectorAll('.van-popover');
    const scrollContents = document.querySelectorAll('.van-popover__content');
    if (popovers) {
      popovers.forEach(popover => {
        popover.removeEventListener('touchmove', this.eventHandlers.preventDefault);
      });
    }
    if (scrollContents) {
      scrollContents.forEach(scrollContent => {
        scrollContent.removeEventListener('touchmove', this.eventHandlers.stopPropagation);
      });
    }

  },
  mounted() {
    // 只有在用户间聊天时（targetUserId 不是平台用户）才不生成 chatSessionId
    var platformUserId = '20210802'
    var isUserChat = this.targetUserId && this.targetUserId != platformUserId

    // 设置当前聊天对象，供全局 WS 消息监听判断是否需要标未读
    if (isUserChat) {
      this.$store.commit('setCurrentChatWithUserId', this.targetUserId)
    }

    // 如果外部传入了 initialMessage 或 initialMsgContent（图片等），优先作为新会话的第一条消息发送，不加载上一次 session
    var hasInitialContent = (this.initialMessage && this.initialMessage.trim()) || (this.initialMsgContent && this.initialMsgContent.trim())
    if (this.initialMessage && this.initialMessage.trim()) {
      this.pendingInitialMessage = this.initialMessage.trim()
    }

    // doMounted 里 _trySendInitialMessage 依赖 pendingInitialMessage，必须在其赋值后再初始化，
    // 否则图文同时传入时（beforeEatchInitialized 已为 true）文本会被漏发，只发出图片
    if (this.$store.state.beforeEatchInitialized) {
      this.doMounted()
    }

    // 如果没有 chatSessionId 且没有外部传入 initialChatSessionId 且没有待发送的 initialMessage/image，尝试从后端获取上一次的 session；
    // skipRestore 时不加载上一次 session（ChannelDetail reset 过后弹干净的新会话）
    if (!this.chatSessionId && !this.initialChatSessionId && !isUserChat && !hasInitialContent && !this.skipRestore) {
      this.loadLastSession()
    } else if (this.chatSessionId && !this.isCreatingNewSession) {
      this.loadChatSession()
    }

    // 搜索结果跳转：定位到指定消息（locateMessage 内部会等首屏加载/补拉历史，无需此处等待）
    if (this.initialLocateMsgId && this.$refs.chatMsgList) {
      this.$refs.chatMsgList.locateMessage(this.initialLocateMsgId)
    }

    // 重置新建会话标志
    this.isCreatingNewSession = false

    // 获取快捷建议（仅全局频道 20210802 的 AI 聊天时需要）
    if (!this.isUserChat && (!this.channelId || this.channelId === '20210802')) {
      this.fetchSuggestions()
    }
    // 轮询检查是否有聊天消息（控制快捷建议显隐）
    var vm = this
    vm._msgCheckInterval = setInterval(function () {
      try {
        var chatMsgs = vm.$refs.chatMsgList && vm.$refs.chatMsgList.chat && vm.$refs.chatMsgList.chat.chatMsgs
        vm.hasChatMessages = chatMsgs && chatMsgs.length > 0
      } catch (e) {
        vm.hasChatMessages = false
      }
    }, 500)
  },
  methods: {
    // ============ 私聊页内搜索 ============
    toggleInChatSearch(show) {
      this.showSearchPanel = show
      if (show) {
        this.$nextTick(() => {
          const input = this.$el.querySelector('.chat-search-input')
          if (input) input.focus()
        })
      } else {
        this.clearInChatSearch()
      }
    },
    clearInChatSearch() {
      this.msgSearchKeyword = ''
      this.msgSearchResults = []
      this.msgSearchDone = false
    },
    // 限定与当前联系人的私聊消息搜索（至少 2 个字，与后端 ngram 最小 token=2 对齐，单字必空结果）
    async doInChatMsgSearch() {
      const kw = (this.msgSearchKeyword || '').trim()
      if (!kw || !this.isUserChat || Array.from(kw).length < 2) return
      this.msgSearchLoading = true
      try {
        const resp = await axios.post('/api/chat/search_chat_msgs', {
          keyword: kw,
          targetUserId: this.targetUserId,
          curPage: 1,
          pageSize: 20
        })
        if (resp.data && resp.data.code === 0) {
          this.msgSearchResults = resp.data.data.chatMsgs || []
          this.msgSearchDone = true
        }
      } catch (e) {
        console.log('search in chat failed:', e)
      } finally {
        this.msgSearchLoading = false
      }
    },
    // 搜索结果预览：撤回提示 / 文本（去 HTML 标签）/ mixed 摘要
    formatInChatPreview(m) {
      if (m.revoked == '1') {
        // revokedBy=2 为平台处置（内容安全人工复核拦截），区别于本人撤回
        return m.revokedBy == '2' ? this.$t('msg_revoked_platform') : (m.direction == '0' ? this.$t('msg_revoked_self') : this.$t('msg_revoked_other'))
      }
      let text = ''
      if (m.contentType === 'mixed') {
        try {
          const arr = Array.isArray(m.msg) ? m.msg : JSON.parse(m.msg)
          const parts = []
          let imgCount = 0
          arr.forEach(item => {
            if (item.type === 'plaintext' && item.content) parts.push(item.content)
            else if (item.type === 'emoji' && item.content) parts.push(item.content)
            else if (item.type === 'img') imgCount++
          })
          text = parts.join(' ') + (imgCount > 0 ? ' [Image]' : '')
        } catch (e) {
          text = ''
        }
      } else if (m.contentType === 'plaintext' || m.contentType === 'text') {
        text = m.msg || ''
      }
      text = String(text).replace(/<[^>]*>/g, '').trim()
      if (text.length > 80) text = text.substring(0, 80) + '...'
      return text
    },
    onInChatResultClicked(m) {
      // MVP：结果已带内容与时间上下文，点击收起搜索面板
      this.toggleInChatSearch(false)
    },

    // 对方会话是否私有（后端对私有会话不下发 matchedUserMsg/matchedAiSummary）
    isPeerPrivate (item) {
      return item.matchedPublic !== undefined && item.matchedPublic !== null && String(item.matchedPublic) !== '1'
    },
    isUserMsgMixed(userMsg) {
      if (!userMsg) return false
      try {
        var parsed = Array.isArray(userMsg) ? userMsg : JSON.parse(userMsg)
        return Array.isArray(parsed) && parsed.length > 0 && parsed[0] && parsed[0].type
      } catch (e) {
        return false
      }
    },
    parseMixedContent(userMsg) {
      if (!userMsg) return []
      try {
        var parsed = Array.isArray(userMsg) ? userMsg : JSON.parse(userMsg)
        if (!Array.isArray(parsed)) return []
        return parsed
      } catch (e) {
        return []
      }
    },
    // 初始化emoji列表（从emoji-datasource动态生成）
    initializeEmojis() {
      this.allEmojis = emojiData
        .filter(e => {
          // 1. 只要 unified 里有 '-'，就排除（即多 codepoint）
          if (e.unified.includes('-')) return false;
          // 2. 排除含 ZWJ/变体/肤色的 emoji
          const codepoints = e.unified.split('-');
          // ZWJ: 200D, 变体: FE0F, 肤色: 1F3FB~1F3FF
          if (
            codepoints.includes('200D') ||
            codepoints.includes('FE0F') ||
            codepoints.some(cp => {
              const n = parseInt(cp, 16);
              return n >= 0x1F3FB && n <= 0x1F3FF;
            })
          ) return false;
          // 3. 其它正常 emoji
          return !e.obsoleted_by;
        })
        .map(emoji => ({
          unicode: this.unifiedToUnicode(emoji.unified),
          category: emoji.category,
          short_name: emoji.short_name,
          sortOrder: emoji.sort_order
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder); // 按sort_order排序
    },
    // 将unified格式转换为Unicode字符
    unifiedToUnicode(unified) {
      return unified.split('-')
        .map(u => String.fromCodePoint(parseInt(u, 16)))
        .join('');
    },
    // 设置emoji分类
    setEmojiCategory(category) {
      this.activeEmojiCategory = category;
    },
    // 创建新会话（公开方法，供外部调用）
    createNewSession() {
      // 1. 先设置标志，防止其他地方错误加载旧 session 或触发事件
      this.isCreatingNewSession = true
      // 2. 重置所有状态
      this.clearPublishFlowCaches()
      this.requirementClarified = '0'
      this.public = '0'
      this.actualPublic = 0
      this.expireAt = null
      this.completeness = null
      this.estimatedRemainingRounds = null
      this.chat.chatMsgs = []
      this.uploadedFiles = []
      this.selectedLocations = []
      this.uploadedFilesList = []
      this.pendingFiles = []
      this.pendingFilesList = []
      this.uploadedVideos = []
      this.pendingVideos = []
      this.showActionPanel = false
      this.selectedChannel = ''
      this.sessionChannel = ''
      this.needCreateChannel = false
      this.showChannelPicker = false
      // 3. 清空 chatSessionId，等发第一条消息时再创建
      this.chatSessionId = ''
      // 4. 清空 ChatMsgList 的消息
      this.$nextTick(() => {
        if (this.$refs.chatMsgList) {
          this.$refs.chatMsgList.chat.chatMsgs = []
          this.$refs.chatMsgList.chat.curPage = 1
          this.$refs.chatMsgList.chat.finished = true // 设为 true 防止自动加载
        }
        // 延迟重置标志，确保所有异步操作完成
        setTimeout(() => {
          this.isCreatingNewSession = false
        }, 500)
      })
    },
    // 从后端创建新的 chat session，获取 chatSessionId
    // 未登录时无法调用后端，回退到本地生成一个临时唯一 id（仅用于本地占位，登录后会重新创建）
    async createNewChatSession() {
      let vm = this
      // 未登录：后端 create_chat_session 需要 userId，无法调用；本地生成临时占位 id
      if (!this.$store.state.alreadyLogin) {
        vm.chatSessionId = vm.$common.generateUUID()
        console.log('[ChatBox] createNewChatSession - 未登录，本地生成临时 chatSessionId:', vm.chatSessionId)
        return
      }
      try {
        // 组装 userMsg：有图片时用 JSON 数组格式（与发布到群一致），否则纯文本
        var createUserMsg = this.firstUserMessageText || ''
        if (this.firstUserMessageImages.length > 0) {
          var contentArr = [{ type: 'plaintext', content: createUserMsg }]
          this.firstUserMessageImages.forEach(function(url) {
            contentArr.push({ type: 'img', content: { url: url } })
          })
          createUserMsg = JSON.stringify(contentArr)
        }
        const req = { toUserId: '20210802', userMsg: createUserMsg }
        if (this.channelId) {
          req.channelId = this.channelId
        }
        const response = await axios.post('/api/chat/create_chat_session', req)
        const respData = response.data
        if (respData.code === 0 && respData.data && respData.data.chatSessionId) {
          vm.chatSessionId = String(respData.data.chatSessionId)
          console.log('[ChatBox] createNewChatSession - 后端创建成功，chatSessionId:', vm.chatSessionId)
        } else {
          // 后端未返回 chatSessionId，回退到本地生成
          vm.chatSessionId = vm.$common.generateUUID()
          console.warn('[ChatBox] createNewChatSession - 后端未返回 chatSessionId，回退本地生成:', vm.chatSessionId)
        }
      } catch (error) {
        console.error('[ChatBox] createNewChatSession error:', error)
        // 出错时回退到本地生成，保证用户能继续输入
        vm.chatSessionId = vm.$common.generateUUID()
      }
    },
    // 外部调用：直接发送文本消息（用于 suggestion 快捷点击）
    sendText(text) {
      if (!text || !text.trim()) return
      if (this.$refs.emojiInputArea && typeof this.$refs.emojiInputArea.setContent === 'function') {
        this.$refs.emojiInputArea.setContent(text.trim())
        this.onSendMsg()
      }
    },
    // 等待输入区就绪后发送外部传入的 initialMessage
    _trySendInitialMessage() {
      var hasText = this.pendingInitialMessage && this.pendingInitialMessage.trim()
      var hasImage = this.initialMsgContent && this.initialMsgContent.trim()
      if (!hasText && !hasImage) return
      if (this.$refs.emojiInputArea && typeof this.$refs.emojiInputArea.setContent === 'function') {
        if (hasImage) {
          this.uploadedFiles.push({
            url: this.initialMsgContent,
            type: 'image',
            name: 'image'
          })
        }
        if (hasText) {
          this.$refs.emojiInputArea.setContent(this.pendingInitialMessage.trim())
        }
        this.pendingInitialMessage = null
        this.$nextTick(() => {
          this.onSendMsg()
        })
      } else {
        setTimeout(() => this._trySendInitialMessage(), 100)
      }
    },
    fetchSuggestions() {
      var vm = this
      // 建议文案按语言下发（对齐 client/ ai_chat_page）：global 版用实际生效语言（含 zh_Hant）
      var loc = (this.$i18n && this.$i18n.locale) || 'en'
      var lang = loc === 'zhHant' ? 'zh_Hant' : loc
      axios.get('/api/config/ai_chat_suggestions?lang=' + encodeURIComponent(lang)).then(function (resp) {
        if (resp.data && resp.data.code === 0 && Array.isArray(resp.data.data)) {
          vm.suggestions = resp.data.data
        }
      }).catch(function (err) {
        console.log('[ChatBox] fetchSuggestions error:', err)
      })
    },
    sendSuggestion(text) {
      this.sendText(text)
    },
    // 重置当前会话（与 client/ AiChatPage.resetSession 对齐）
    resetSession() {
      // 重置当前会话：清空所有状态，等用户发第一条消息时再创建新session
      // 不能调用 loadLastSession()，否则会加载旧 chatSessionId，
      // 导致 ai_handler 仍然从 Redis/DB 读取旧的历史消息带给 elicitor
      this.isCreatingNewSession = true
      // 取消旧 session 的流超时计时器并复位等待/发送锁，避免旧会话的回复影响新会话
      if (this.streamTimeoutId) {
        clearTimeout(this.streamTimeoutId)
        this.streamTimeoutId = null
      }
      this.isWaitingForStream = false
      this._isSending = false
      // 预拉取/自动建群缓存属于旧 session，一并清掉
      this.clearPublishFlowCaches()
      this.requirementClarified = '0'
      this.public = '0'
      this.actualPublic = 0
      this.aiSummary = ''
      this.requirementDetail = ''
      this.userMsgOptions = []
      this.firstUserMessageText = ''
      this.firstUserMessageImages = []
      this.expireAt = null
      this.sessionChannelId = ''
      this.selectedChannelId = ''
      this.completeness = null
      this.estimatedRemainingRounds = null
      this.hasChatMessages = false
      // 清空消息列表
      if (this.$refs.chatMsgList) {
        this.$refs.chatMsgList.chat = { chatMsgs: [] }
      }
      // 清空 chatSessionId，等发第一条消息时再创建新 session
      this.chatSessionId = ''
      this.$nextTick(() => {
        if (this.$refs.chatMsgList) {
          this.$refs.chatMsgList.chat.chatMsgs = []
          this.$refs.chatMsgList.chat.curPage = 1
          this.$refs.chatMsgList.chat.finished = true // 设为 true 防止自动加载
        }
        // 延迟重置标志，确保所有异步操作完成
        setTimeout(() => {
          this.isCreatingNewSession = false
        }, 500)
      })
      // 通知外层（如群聊详情页）重置完成：仅置标记，不关闭 AI 弹框
      this.$emit('ai-chat-reset')
    },
    // 从后端获取最近一次聊天会话
    async loadLastSession() {
      let vm = this
      try {
        if (!this.$store.state.alreadyLogin) {
          return
        }
        const reqData = {}
        if (this.channelId) {
          reqData.channelId = this.channelId
        }
        const response = await axios.post('/api/chat/get_last_chat_session', reqData)
        console.log('[ChatBox] get_last_chat_session response:', response.data)
        const respData = response.data
        if (respData.code === 0 && respData.data && respData.data.chatSession) {
          // 守卫：HTTP 在途期间用户可能已经新建会话并发送，此时覆盖 chatSessionId
          // 会让 AI 流式回复因 sessionId 不匹配被静默丢弃，还会把刚发的消息气泡清掉
          if (vm.chatSessionId || vm._isSending || vm.isWaitingForStream) {
            console.log('[ChatBox] loadLastSession: session already active (' + vm.chatSessionId + '), skip restore')
            return
          }
          var lastSession = respData.data.chatSession
          // 如果最近 session 已发布(status=1)，不加载，等发消息时再创建新 session
          var status = String(lastSession.status || '0')
          if (status == '1') {
            console.log('[ChatBox] Last session already published (status=1), will create on first message')
            return
          }
          vm.chatSessionId = String(lastSession.chatSessionId)
          vm.hasLoadedLastSession = true
          console.log('[ChatBox] Loaded last session:', vm.chatSessionId)
          vm.loadChatSession()
        } else {
          // 没有历史 session，不创建，等发消息时再创建
          console.log('[ChatBox] No last session found, will create on first message')
        }
      } catch (error) {
        console.error('[ChatBox] Failed to load last session:', error)
      }
    },

    doMounted() {
      // 防止重复执行
      if (this.mountedExecuted) {
        return
      }
      this.mountedExecuted = true

      if (this.$store.state.alreadyLogin) {
        this.setupWebSocketListeners()
        // 如果 WebSocket 已注册，立即初始化
        if (wsService.registered) {
          this.onWebSocketRegistered({ userId: wsService.userId })
        }
        // 如果是用户聊天，加载 match detail
        if (this.isUserChat) {
          this.loadUserMatchDetail()
        }
      }

      // doMounted 完成后再尝试发送外部传入的 initialMessage/image（确保 WebSocket 监听器已设置）
      if (this.pendingInitialMessage || (this.initialMsgContent && this.initialMsgContent.trim())) {
        this._trySendInitialMessage()
      }
    },
    setupWebSocketListeners() {
      console.log('[ChatBox] Setting up WebSocket listeners')
      let vm = this
      // 只有用户聊天才需要加载联系人列表
      if (vm.isUserChat) {
        vm.onContactRefresh()
      }

      // 使用命名监听器，防止重复注册
      // 监听 WebSocket 事件
      wsService.on('registered', vm.onWebSocketRegistered, 'chatbox_registered')
      wsService.on('msg', vm.onWebSocketMsg, 'chatbox_msg')
      wsService.on('server_ack', vm.onServerAck, 'chatbox_server_ack')
      wsService.on('endpoint_ack', vm.onEndpointAck, 'chatbox_endpoint_ack')
      wsService.on('send_failed', vm.onSendFailed, 'chatbox_send_failed')
      wsService.on('msg_blocked', vm.onMsgBlocked, 'chatbox_msg_blocked')
      wsService.on('msg_revoke', vm.onMsgRevoke, 'chatbox_msg_revoke')
    },
    onWebSocketRegistered(msg) {
      console.log('[ChatBox] WebSocket registered, userId:', msg.userId)
      this.chat.contact.fromUserId = msg.userId
      // 调用 ChatMsgList 的 refresh 方法
      // 私聊场景：chatSessionId 始终为空，但有 toUserId，可以立即 refresh
      // AI聊天场景：等待 chatSessionId 从后端加载完成后再 refresh（由 watch 触发）
      if (this.$refs.chatMsgList && this.isUserChat) {
        this.$refs.chatMsgList.refresh()
      }
      // AI聊天场景下，chatSessionId 变化时会由 ChatMsgList 的 watch 自动触发 refresh

      // 开始拉取聊天状态
      this.pullChatStatus()
      this.pullChatStatusHandle = setInterval(() => {
        this.pullChatStatus()
      }, 3000)
    },
    onWebSocketMsg(msg) {
      console.log('[WS DEBUG] msg received:', JSON.parse(JSON.stringify(msg)));
      if (msg.isStream) {
        console.log('[WS DEBUG] Stream msg, content:', msg.msg);
      }
      console.log('this.chat.contact: ', this.chat.contact)
      let vm = this

      //如果是当前聊天的contact过来的
      var needPushToChatMsgs = false
      if (msg.fromUserId == this.chat.contact.targetUserId) {
        needPushToChatMsgs = true
        msg.direction = '1'

        // 收到回复消息，取消 analyzingImage loading
        if (vm.analyzingImage) {
          vm.analyzingImage = false
          console.log('[DEBUG] 收到回复消息，取消 analyzingImage loading')
        }
      }

      // 处理流式消息
      if (msg.isStream) {
        vm.handleStreamMessage(msg, needPushToChatMsgs)
        // 流式消息也取消 loading
        if (vm.analyzingImage) {
          vm.analyzingImage = false
          console.log('[DEBUG] 流式消息完成，取消 analyzingImage loading')
        }
        // 流式消息的 endpoint_ack 仅在 stream_end 时发送
        return
      }

      // 非stream消息：标记为已读并发送 endpoint_ack
      if (msg.fromUserId == this.chat.contact.targetUserId) {
        msg.read = '1'
        wsService.sendEndpointAck(msg.msgId, msg.fromUserId)
        console.log('[DEBUG] 发送 endpoint_ack，msgId:', msg.msgId, 'toUserId:', msg.fromUserId)
      }

      // 非stream消息的正常处理
      if (needPushToChatMsgs) {
        vm.$refs.chatMsgList.addMessage(msg)
      }
      vm.updateChatContacts(msg, false)
    },
    onServerAck(msg) {
      console.log('[DEBUG] server_ack received: ', msg);
      if (this.$refs.chatMsgList) {
        this.$refs.chatMsgList.updateMessageServerReceived(msg.msgId)
      } else {
        console.log('[DEBUG] server_ack - chatMsgList 不存在')
      }
    },
    onEndpointAck(msg) {
      console.log('endpoint_ack received: ', msg);
      this.$refs.chatMsgList.updateMessageRead(msg.msgId)
    },
    // 收到撤回事件（可能是自己撤回后服务端广播回来的，也可能是对方撤回），本地更新占位展示
    onMsgRevoke(data) {
      console.log('[ChatBox] msg_revoke received: ', data);
      if (this.$refs.chatMsgList && data && data.msgId) {
        this.$refs.chatMsgList.revokeMessageLocally(data.msgId)
      }
    },
    onSendFailed(data) {
      console.log('[DEBUG] send_failed received: ', data);
      if (this.$refs.chatMsgList) {
        this.$refs.chatMsgList.updateMessageSendStatus(data.msgId, 'failed', data.error)
      } else {
        console.log('[DEBUG] send_failed - chatMsgList 不存在')
      }
    },
    onMsgBlocked(data) {
      console.log('[DEBUG] msg_blocked received: ', data);
      // 消息被屏蔽，重置等待 stream 状态
      this.isWaitingForStream = false
      // 清除超时计时器
      if (this.streamTimeoutId) {
        clearTimeout(this.streamTimeoutId)
        this.streamTimeoutId = null
      }
      // 消息被屏蔽，显示提示并更新消息状态
      if (this.$refs.chatMsgList) {
        // 平台审核拦截（payload 携带 blockedMsg，含 blocked/blockReason）才标 blocked='1'，本端即时渲染屏蔽标记
        // （消息不入库，刷新后即消失——「未发出」语义）；用户互相屏蔽（code 1/2）仅即时红字提示
        if (data.blockedMsg) {
          this.$refs.chatMsgList.markMessageBlockedLocally(data.msgId, data.blockedMsg.blockReason)
        } else {
          this.$refs.chatMsgList.updateMessageSendStatus(data.msgId, 'blocked', data.message)
        }
      }
      // 显示 Toast 提示
      Toast(data.message || this.$t('toast_message_blocked'))
    },
    fixScrollProblem() {
      this.$nextTick(() => {
        // 定义可复用的具名事件处理器
        if (!this.eventHandlers.preventDefault) {
          this.eventHandlers.preventDefault = (e) => e.preventDefault();
        }
        if (!this.eventHandlers.stopPropagation) {
          this.eventHandlers.stopPropagation = (e) => e.stopPropagation();
        }

        const popovers = document.querySelectorAll('.van-popover');
        const scrollContents = document.querySelectorAll('.van-popover__content');

        // 先移除所有旧事件监听
        popovers.forEach(popover => {
          popover.removeEventListener('touchmove', this.eventHandlers.preventDefault);
        });
        scrollContents.forEach(scrollContent => {
          scrollContent.removeEventListener('touchmove', this.eventHandlers.stopPropagation);
        });

        // 添加新的事件监听
        popovers.forEach(popover => {
          popover.addEventListener('touchmove', this.eventHandlers.preventDefault, { passive: false });
        });

        scrollContents.forEach(scrollContent => {
          scrollContent.addEventListener('touchmove', this.eventHandlers.stopPropagation, { passive: true });
        });

        //修复防止左侧溢出
        popovers.forEach(popover => {
          const rect = popover.getBoundingClientRect();
          if (rect.left < 0) {
            // 向右偏移超出部分的绝对值 + 安全距离
            const offsetX = Math.abs(rect.left) + 8;
            popover.style.transform = `translateX(${offsetX}px)`;
          }
        });

      });
    },
    handleStreamMessage(msg, needPushToChatMsgs) {
      let vm = this
      // 只处理当前 session 的流：重置/新聊天后旧 session 的流可能还在推送，须丢弃（对齐 client/ ai_chat_page）
      var streamSessionId = msg && msg.chatSessionId != null ? String(msg.chatSessionId) : ''
      if (streamSessionId) {
        if (!vm.chatSessionId && (vm._isSending || vm.isWaitingForStream)) {
          // 兜底：正在等 AI 回复但 session 尚未就位时，收养这条在途流的 sessionId，
          // 避免整条 AI 回复被下面静默丢弃
          console.log('[ChatBox] stream adopts sessionId (local was empty):', streamSessionId)
          vm.chatSessionId = streamSessionId
        } else if (vm.chatSessionId && streamSessionId !== vm.chatSessionId) {
          console.log('[ChatBox] stream ignored: chatSessionId mismatch (stream=' + streamSessionId + ', local=' + vm.chatSessionId + ')')
          return
        }
      }
      if (msg.msgType === 'stream_end') {
        // 流结束消息，不显示在聊天记录中，重置等待状态
        vm.isWaitingForStream = false
        // 清除超时计时器
        if (vm.streamTimeoutId) {
          clearTimeout(vm.streamTimeoutId)
          vm.streamTimeoutId = null
        }
        // 解析 userMsgOptions（AI 建议的用户消息选项）
        if (msg.userMsgOptions) {
          try {
            var opts = JSON.parse(String(msg.userMsgOptions))
            if (Array.isArray(opts)) {
              vm.userMsgOptions = opts.filter(function(e) { return typeof e === 'string'; })
            }
          } catch (e) { /* ignore */ }
        }
        console.log('[DEBUG] Stream ended, set isWaitingForStream = false')
        // 需求已澄清时，流结束后重新加载 session 元数据（expireAt 等可能动态变化）
        if (vm.requirementClarified === '1' && vm.chatSessionId) {
          vm.loadChatSession()
        }
        // 流结束时才发送 endpoint_ack
        if (needPushToChatMsgs) {
          msg.read = '1'
          wsService.sendEndpointAck(msg.msgId, msg.fromUserId)
          console.log('[DEBUG] Stream ended, 发送 endpoint_ack，msgId:', msg.msgId, 'toUserId:', msg.fromUserId)
        }
        vm.updateChatContacts(msg, false)
        return
      }

      // 正在接收流消息，需要追加到对应消息
      if (msg.msgType === 'stream_error') {
        // 流错误，重置等待状态
        vm.isWaitingForStream = false
        // 清除超时计时器
        if (vm.streamTimeoutId) {
          clearTimeout(vm.streamTimeoutId)
          vm.streamTimeoutId = null
        }
        console.log('[DEBUG] Stream error, set isWaitingForStream = false')
        // msg.msg = JSON.parse(msg.msg)
        Toast.fail(msg.msg)
        return
      }

      // 解析assistant类型的消息，提取增量内容
      try {
        msg.msg = JSON.parse(msg.msg)
      } catch (e) {
        console.error('[ChatBox] Failed to parse stream message:', e)
        return
      }
      var incrementalMsg = msg.msg.msg
      console.log('[STREAM FRONTEND] Processing stream message:', msg)
      console.log('[STREAM FRONTEND] incremental content:', incrementalMsg)

      // 收到 stream 数据，节流刷新超时计时器（最多每 3 秒刷新一次，避免频繁操作）
      const now = Date.now()
      if (!vm.lastStreamRefreshTime || now - vm.lastStreamRefreshTime > 3000) {
        vm.lastStreamRefreshTime = now
        if (vm.streamTimeoutId) {
          clearTimeout(vm.streamTimeoutId)
        }
        vm.streamTimeoutId = setTimeout(() => {
          console.log('[DEBUG] Stream timeout, reset isWaitingForStream')
          vm.isWaitingForStream = false
          Toast(this.$t('toast_ai_timeout'))
        }, 30000)
      }

      if (needPushToChatMsgs) {
        // 检查消息是否已存在，如果不存在则先创建初始消息
        const chatMsgs = vm.$refs.chatMsgList.getChatMsgs()
        const existingMsg = chatMsgs.find(m => m.msgId === msg.msgId)

        if (!existingMsg) {
          // 创建初始消息
          const initialMsg = {
            chatSessionId: msg.chatSessionId,
            msgId: msg.msgId,
            contentType: 'assistant',
            msg: { msg: '' },
            direction: '1',
            read: vm.show ? '1' : '0',
            serverReceived: '1',
            createdAt: msg.createdAt || new Date().getTime(),
            createdAtText: vm.$common.formatDateTime(msg.createdAt || new Date().getTime()),
            fromUserId: msg.fromUserId,
            toUserId: msg.toUserId,
            system: '0'
          }
          vm.$refs.chatMsgList.addMessage(initialMsg)
        }

        // 调用 ChatMsgList 的方法追加流式消息
        vm.$refs.chatMsgList.appendStreamMessage(
          msg.msgId,
          incrementalMsg
        )
      }
      vm.updateChatContacts(msg, false)
    },
    clearReply() {
      window.localStorage.removeItem('chatReply')
      this.reply.msg = ''
      this.reply.name = ''
      this.reply.userId = ''
      this.reply.userType = ''
      this.reply.type = ''
      this.reply.msgId = ''
    },
    // 长按消息选择引用（ChatMsgList emit）：生成摘要存入 reply，随下一条消息发送
    onReplyClicked(chatMsg, type) {
      // 已撤回的消息不允许引用
      if (chatMsg.revoked == '1') {
        return
      }
      // 摘要去 HTML 标签，引用条/引用块按纯文本展示
      var summary = String(this.getMsgSummary(chatMsg.msg, chatMsg.contentType, 30) || '')
      summary = summary.replace(/<[^>]*>/g, '').trim()
      this.reply.msg = summary
      this.reply.msgId = chatMsg.msgId
      this.reply.userId = chatMsg.fromUserId
      // 被引用者名字：自己发的用当前用户名，对方的用目标用户名
      this.reply.name = String(chatMsg.fromUserId) == String(this.currentUserId)
        ? (this.currentUserName || '')
        : (this.targetUserName || '')
      this.reply.type = type || chatMsg.contentType
      window.localStorage.setItem('chatReply', JSON.stringify(this.reply));
    },
    // 获取消息摘要，用于联系人列表和回复功能
    getMsgSummary(msg, contentType, len) {
      if (!msg) {
        return ''
      }
      if (contentType == 'plaintext' || contentType == 'text') {
        // plaintext是纯文本
        return this.$common.limitLength(msg, len)
      } else if (contentType == 'mixed') {
        // mixed是数组，生成友好的摘要
        if (Array.isArray(msg)) {
          var summary = ''
          var hasText = false
          var textContent = ''
          var imgCount = 0
          var fileCount = 0
          var locationCount = 0
          var emojiCount = 0
          var voiceCount = 0
          var videoCount = 0

          // 统计各类型数量
          msg.forEach(item => {
            if (item.type === 'plaintext' && item.content) {
              hasText = true
              textContent = item.content.replace(/<[^>]*>/g, '')
            } else if (item.type === 'img') {
              imgCount++
            } else if (item.type === 'file') {
              fileCount++
            } else if (item.type === 'video') {
              videoCount++
            } else if (item.type === 'audio') {
              voiceCount++
            } else if (item.type === 'pick_location') {
              locationCount++
            } else if (item.type === 'emoji') {
              emojiCount++
            }
          })

          // 构建摘要
          var prefix = ''
          if (imgCount > 0) {
            prefix += imgCount === 1 ? '[Image] ' : '[Imagex' + imgCount + '] '
          }
          if (fileCount > 0) {
            prefix += fileCount === 1 ? '[File] ' : '[Filex' + fileCount + '] '
          }
          if (videoCount > 0) {
            prefix += videoCount === 1 ? '[Video] ' : '[Videox' + videoCount + '] '
          }
          if (voiceCount > 0) {
            prefix += voiceCount === 1 ? '[Voice] ' : '[Voicex' + voiceCount + '] '
          }
          if (locationCount > 0) {
            prefix += locationCount === 1 ? '[Location] ' : '[Locationx' + locationCount + '] '
          }
          if (emojiCount > 0) {
            prefix += emojiCount === 1 ? '[Emoji] ' : '[Emojix' + emojiCount + '] '
          }

          if (hasText && textContent) {
            // 有文本内容，显示前缀+文本
            var remainingLen = len - prefix.length
            if (remainingLen > 0) {
              summary = prefix + this.$common.limitLength(textContent, remainingLen)
            } else {
              summary = prefix.trim()
            }
          } else {
            // 只有附件
            summary = prefix.trim()
          }

          return summary || '[Mixed]'
        }
        return '[混合内容]'
      } else {
        // 其他类型
        return '[其他]'
      }
    },
    /**
 * 发送心跳请求以保持连接活跃
 */

    sendHeartbeat() {
      // 心跳由全局 WebSocket 服务管理，此处不再需要
    },
    pullChatStatus() {
      if (!this.show) {
        return
      }
      let vm = this
      var url = '/api/uuid/get_chat_stutus'
      var req = { targetUserId: vm.chat.contact.targetUserId, test: JSON.stringify(vm.chat.contact) }
      axios.post(url, req).then(
        response => {
          var body = response.data
          if (body.code !== 0) {
            return
          }
          var data = body.data
          vm.chat.contact.targetUserChatStatusText = data.chatStatusText
        }
      ).catch(error => {
        console.log(error)
      })
    },
    // 处理需求已明确事件
    async handleRequirementClarified() {
      // 如果正在创建新会话，忽略这个事件
      if (this.isCreatingNewSession) {
        return
      }
      // 如果是查看他人会话（通过 initialChatSessionId 传入），不显示 toast
      if (this.initialChatSessionId) {
        return
      }
      // 立即更新标志，避免重复调用
      if (this.requirementClarified === '1') return;
      if (!this.chatSessionId) {
        return;
      }
      // 更新状态
      this.requirementClarified = '1';
      // 重新加载 session 元数据以获取 expireAt 等后端更新的字段
      this.loadChatSession();
      // 不再预拉推荐群：agent 选群成本较高，统一改为用户点发布时才首次跑（结果持久化到 DB）
      // 给用户即时反馈
      Toast.success(this.$t('toast_need_clear'))
      // 自动滚动到底部，让用户看到按钮
      this.$nextTick(() => {
        if (this.$refs.chatMsgList) {
          this.$refs.chatMsgList.scrollToBottom()
        }
      })
    },
    // 处理需求澄清进度更新
    handleProgressUpdate(progress) {
      if (progress.completeness != null) {
        // 完备度只增不减，避免进度条倒退
        this.completeness = (this.completeness == null)
          ? progress.completeness
          : (this.completeness > progress.completeness ? this.completeness : progress.completeness)
      }
      if (progress.estimatedRemainingRounds != null) {
        this.estimatedRemainingRounds = progress.estimatedRemainingRounds
      }
    },
    // ChatMsgList 从最后一条 AI 消息的 <user_msg_options> 隐形标签兜底解析而来
    //（对齐 client/ ai_chat_page：session meta 没有返回时才用消息里的），meta / WS 字段已提供的优先
    onUserMsgOptions(opts) {
      if (Array.isArray(opts) && opts.length > 0 && this.userMsgOptions.length === 0) {
        this.userMsgOptions = opts
      }
    },
    // 历史消息加载后提取首条用户消息（兜底 userMsg，与 client 端逻辑一致）
    handleFirstUserMsgExtracted(payload) {
      if (!payload) return
      // 文本/图片独立记录是故意设计：后续消息里的图也要并入 userMsg，不依赖首条多媒体内容
      if (!this.firstUserMessageText && payload.text) {
        this.firstUserMessageText = payload.text
      }
      if (this.firstUserMessageImages.length === 0 && payload.images && payload.images.length > 0) {
        this.firstUserMessageImages = payload.images
      }
    },

    handleChatMsgKeydown(event) {
      if (event.keyCode === 13) {
        this.onSendMsg()
        event.preventDefault()
        return
      }
    },
    // 点击对方消息头像，打开对方 profile 弹窗
    async onTargetAvatarClick() {
      if (!this.isUserChat) return
      var contact = this.chat.contact || {}
      var userId = this.targetUserId || contact.targetUserId || ''
      if (!userId) return
      this.profileUserId = String(userId)
      this.profileUserName = this.targetUserName || contact.targetUsername || ''
      this.profileUserAvatar = this.targetUserAvatar || contact.targetAvatar || ''
      this.profileUserBio = ''
      this.showProfilePopup = true
      // 拉取最新的基本信息（头像、bio）
      try {
        const response = await axios.post('/api/user/get_basic_user_info', {
          targetUserId: this.profileUserId
        })
        if (response.data && response.data.code == 0 && response.data.data) {
          this.profileUserBio = response.data.data.bio || ''
          if (response.data.data.avatar) {
            this.profileUserAvatar = response.data.data.avatar
          }
        }
      } catch (error) {
        console.error('[ChatBox] Failed to load user profile info:', error)
      }
    },
    onContactClicked(chatContactId) {
      this.showContactPopover = false
      for (var i = 0; i < this.contact.chatContacts.length; i++) {
        if (chatContactId == this.contact.chatContacts[i].chatContactId) {
          this.contact.chatContacts[i].lastChatMsg.read = '1'
          this.chat.contact = this.contact.chatContacts[i]
          // 调用 ChatMsgList 的 refresh 方法
          if (this.$refs.chatMsgList) {
            this.$refs.chatMsgList.refresh()
          }
        }
      }
    },
    onContactRefresh() {
      // 清空列表数据
      this.contact.chatContacts = []
      this.contact.curPage = 1
      this.contact.finished = false
      // 重新加载数据
      // 将 loading 设置为 true，表示处于加载状态
      // this.loading = true

      this.onContactLoad()
    },
    async loadChatSession() {
      let vm = this
      console.log('[DEBUG] loadChatSession() - chatSessionId:', vm.chatSessionId, 'initialChatSessionId:', vm.initialChatSessionId)
      // 安全检查：必须登录才能加载会话
      if (!this.$store.state.alreadyLogin) {
        console.log('[DEBUG] loadChatSession() - not logged in, returning')
        return
      }
      // 如果是新建会话，不加载旧的会话状态
      if (vm.isCreatingNewSession) {
        return
      }
      try {
        var url = '/api/chat/get_chat_session'
        var req = { chatSessionId: vm.chatSessionId }
        const response = await axios.post(url, req);
        console.log('[DEBUG] loadChatSession() - API response:', response.data)
        var body = response.data
        if (body.code === 0 && body.data && body.data.chatSession) {
          var chatSession = body.data.chatSession
          if (chatSession.requirementClarified === 1 || chatSession.requirementClarified === '1') {
            vm.requirementClarified = '1'
          }
          // 检查public状态
          const publicValue = chatSession.public || 0;
          vm.actualPublic = typeof publicValue === 'number' ? publicValue : parseInt(publicValue);
          if (vm.actualPublic === 1) {
            vm.public = '1'
          } else {
            // public == 0（初始状态）或 public == -1（用户主动设置为私有）都视为私有
            vm.public = '0'
          }
          // 读取匹配暂停状态（matchPaused: 0=匹配中，1=暂停）
          vm.matchPaused = (chatSession.matchPaused || 0) == 1 ? 1 : 0
          // 读取 channel（兼容旧字段）
          if (chatSession.channelId) {
            vm.sessionChannelId = String(chatSession.channelId);
            vm.selectedChannelId = String(chatSession.channelId);
            vm.sessionChannel = chatSession.channelName || '';
            vm.selectedChannel = chatSession.channelName || '';
          } else if (chatSession.channel) {
            vm.sessionChannel = chatSession.channel;
            vm.selectedChannel = chatSession.channel;
          } else {
            vm.sessionChannelId = '';
            vm.selectedChannelId = '';
            vm.sessionChannel = '';
            vm.selectedChannel = '';
          }
          // 读取 allowDirectContact
          if (chatSession.allowDirectContact != null) {
            vm.allowDirectContact = chatSession.allowDirectContact == 1 || chatSession.allowDirectContact == '1';
          }
          // 读取 requirementDetail / aiSummary
          if (chatSession.requirementDetail != null) {
            vm.requirementDetail = String(chatSession.requirementDetail);
          }
          if (chatSession.aiSummary != null) {
            vm.aiSummary = String(chatSession.aiSummary);
          }
          // 读取 userMsgOptions
          if (chatSession.userMsgOptions) {
            try {
              var opts = JSON.parse(String(chatSession.userMsgOptions));
              if (Array.isArray(opts)) {
                vm.userMsgOptions = opts.filter(function(e) { return typeof e === 'string'; });
              }
            } catch (e) { /* ignore */ }
          }
          // 读取 expireAt
          if (chatSession.expireAt != null) {
            vm.expireAt = chatSession.expireAt.toString();
          } else {
            vm.expireAt = null;
          }
          // 不再预拉推荐群：agent 选群成本较高，统一改为用户点发布时才首次跑（结果持久化到 DB）
        }

      } catch (error) {
        console.error('拉取chatSession失败:', error)
      }
    },
    onContactLoad() {
      let vm = this
      if (vm.contact.finished) {
        return;
      }
      vm.contact.loading = true;
      var url = '/api/chat/get_chat_contacts'
      var req = { curPage: vm.contact.curPage }
      axios.post(url, req).then(
        response => {
          vm.contact.loading = false
          vm.contact.finished = false
          if (vm.contact.refreshing) {
            vm.contact.refreshing = false
          }
          var body = response.data
          if (body.code !== 0) {
            // Toast.fail('获取信息出错，请重新进入.' + body.msg)
            vm.contact.finished = true
            return
          }
          var data = body.data
          for (var i = 0; i < data.chatContacts.length; i++) {
            if (data.chatContacts[i].lastChatMsg && data.chatContacts[i].lastChatMsg.msgId) {
              var msg = data.chatContacts[i].lastChatMsg
              //如果最后这条消息其实是自己向对方发的消息，那肯定是read，而不依赖消息本身的read
              if (msg.toUserId == data.chatContacts[i].targetUserId) {
                data.chatContacts[i].lastChatMsg.read = '1'
              }
              if (data.chatContacts[i].lastChatMsg.contentType == 'plaintext' || data.chatContacts[i].lastChatMsg.contentType == 'text') {
                // plaintext是纯文本，直接用twemoji解析
                if (data.chatContacts[i].lastChatMsg.msg) {
                  data.chatContacts[i].lastChatMsg.msg = data.chatContacts[i].lastChatMsg.msg
                }
              } else if (data.chatContacts[i].lastChatMsg.contentType == 'mixed') {
                // mixed是JSON数组，每个元素有type和content
                if (data.chatContacts[i].lastChatMsg.msg) {
                  try {
                    var contentArray = Array.isArray(data.chatContacts[i].lastChatMsg.msg) ? data.chatContacts[i].lastChatMsg.msg : JSON.parse(data.chatContacts[i].lastChatMsg.msg)
                    // 使用 getMsgSummary 生成友好摘要
                    var summary = vm.getMsgSummary(contentArray, 'mixed', 50)
                    data.chatContacts[i].lastChatMsg.msg = summary
                  } catch (e) {
                    console.error('[ChatBox] Failed to parse mixed content:', e)
                    data.chatContacts[i].lastChatMsg.msg = '[Mixed]'
                  }
                }
              } else {
                // 其他类型也是JSON格式
                var msgStr = data.chatContacts[i].lastChatMsg.msg
                if (msgStr && typeof msgStr === 'string' && (msgStr.startsWith('{') || msgStr.startsWith('['))) {
                  try {
                    data.chatContacts[i].lastChatMsg.msg = JSON.parse(msgStr)
                  } catch (e) {
                    console.error('[ChatBox] Failed to parse content:', e)
                  }
                }
              }
            }
          }
          vm.contact.chatContacts = vm.contact.chatContacts.concat(data.chatContacts)
          if (data.chatContacts.length < data.pageSize) {
            console.log('finished')
            vm.contact.finished = true
          }
          vm.contact.curPage = parseInt(data.curPage) + 1
        }
      ).catch(error => {
        console.log(error)
        vm.contact.finished = true
        Toast.fail(this.$t('toast_load_contacts_failed'))
      })
    },
    async updateChatContacts(msg, isSendMsg) {
      let vm = this
      var found = false
      for (var i = 0; i < vm.contact.chatContacts.length; i++) {
        var chatContact = vm.contact.chatContacts[i]
        if (((!isSendMsg) && (msg.fromUserId == chatContact.fromUserId && msg.toUserId == chatContact.toUserId ||
          msg.fromUserId == chatContact.toUserId && msg.toUserId == chatContact.fromUserId)) ||
          (isSendMsg && ((msg.toUserId == chatContact.toUserId) || (msg.toUserId == chatContact.fromUserId)))) {
          found = true
          // 如果是自己发送的消息，标记为已读
          if (isSendMsg) {
            msg.read = '1'
          }
          // 为联系人列表生成消息摘要
          var msgForContact = JSON.parse(JSON.stringify(msg))
          if (msgForContact.contentType === 'mixed' && typeof msgForContact.msg === 'string') {
            try {
              var contentArray = Array.isArray(msgForContact.msg) ? msgForContact.msg : JSON.parse(msgForContact.msg)
              msgForContact.msg = vm.getMsgSummary(contentArray, 'mixed', 50)
            } catch (e) {
              msgForContact.msg = '[Mixed]'
            }
            }
            vm.contact.chatContacts[i].lastChatMsg = msgForContact
        }
      }

      // 如果没找到联系人，用消息中携带的 sender 信息创建临时联系人
      if (!found) {
        var msgForContact = JSON.parse(JSON.stringify(msg))
        if (msgForContact.contentType === 'mixed' && typeof msgForContact.msg === 'string') {
          try {
            var contentArray = Array.isArray(msgForContact.msg) ? msgForContact.msg : JSON.parse(msgForContact.msg)
            msgForContact.msg = vm.getMsgSummary(contentArray, 'mixed', 50)
          } catch (e) {
            msgForContact.msg = '[Mixed]'
          }
        }
        // 自己发送的消息标记为已读
        if (isSendMsg) {
          msgForContact.read = '1'
        }
        var targetUserId = isSendMsg ? msg.toUserId : msg.fromUserId
        var newContact = {
          chatContactId: 'temp_' + targetUserId,
          targetUserId: targetUserId,
          targetUsername: msg.fromUserName || targetUserId,
          targetEmail: '',
          targetAvatar: msg.fromUserAvatar || '',
          fromUserId: targetUserId,
          toUserId: vm.currentUserId,
          lastChatMsg: msgForContact
        }
        vm.contact.chatContacts.unshift(newContact)
      }
    },
    handleEditorClick() {
      // 点击输入框时检查登录状态，未登录则展示登录框
      if (!this.$store.state.alreadyLogin) {
        this.$store.state.needLogin = true
      }
    },

    // 清除选中的 channel
    clearSelectedChannel() {
      this.selectedChannel = '';
      this.sessionChannel = '';
      this.selectedChannelId = '';
      this.sessionChannelId = '';
      this.needCreateChannel = false;
      // 通知服务端清除 session 的 channel，避免 loadChatSession 时又从服务端恢复回来
      if (this.chatSessionId) {
        this.updateSessionChannel('');
      }
    },

    // 切换频道选择面板
    toggleChannelPicker() {
      this.showChannelPicker = !this.showChannelPicker;
      if (this.showChannelPicker) {
        // 关闭其他面板
        this.showActionPanel = false;
        // 加载初始频道列表
        this.channelPickerSearchQuery = '';
        this.doChannelPickerSearch('');
        // 聚焦搜索框
        this.$nextTick(() => {
          if (this.$refs.channelPickerSearchInput) {
            this.$refs.channelPickerSearchInput.focus();
          }
        });
      }
    },

    // 频道选择面板搜索
    async doChannelPickerSearch(query) {
      try {
        const response = await axios.post('/api/channel/search', {
          query: query || '',
          pageSize: 20,
          curPage: 1
        });
        if (response.data.code === 0) {
          const channels = (response.data.data && response.data.data.channels) || [];
          this.channelPickerSearchResults = channels.map(c => ({
            channelId: String(c.channelId || c.id || ''),
            name: c.name || '',
            description: c.description || '',
            memberCnt: c.memberCnt || 0,
            chatSessionCnt: c.chatSessionCnt || 0
          }));
        }
      } catch (error) {
        console.error('Channel picker search error:', error);
      }
    },

    // 从频道选择面板选择一个 channel
    selectChannelFromPicker(item, isNew) {
      const name = typeof item === 'string' ? item : (item.name || '');
      const channelId = typeof item === 'object' ? String(item.channelId || '') : '';
      const cleanName = name.startsWith('#') ? name.substring(1) : name;
      // 如果点击的是当前已选中的 channel，则取消选中
      if (this.selectedChannel && this.selectedChannel.toLowerCase() === cleanName.toLowerCase()) {
        this.clearSelectedChannel();
      } else {
        this.selectedChannel = cleanName;
        this.sessionChannel = cleanName;
        this.selectedChannelId = channelId;
        this.sessionChannelId = channelId;
        this.needCreateChannel = isNew;
      }
      this.showChannelPicker = false;
      this.channelPickerSearchQuery = '';
    },

    // 防抖函数
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // 更新会话的 channelId
    async updateSessionChannel(channel = null) {
      if (!this.chatSessionId) return;

      // 如果传入了 channel 参数，使用传入的值；否则使用 selectedChannel
      const channelValue = channel !== null ? channel : this.selectedChannel;

      try {
        await axios.post('/api/channel/update_session_channel_id', {
          chatSessionId: this.chatSessionId,
          channelId: this.selectedChannelId || '',
          channel: channelValue,
          userId: this.currentUserId
        });
        this.sessionChannel = channelValue;
      } catch (error) {
        console.error('Update session channel error:', error);
      }
    },
    async onSendMsg() {
      console.log('onSendMsg')
      let vm = this

      // 防护：OS 文件选择框刚关闭时，确认选择的 Enter/点击可能以"幽灵事件"漏进页面，
      // 误触编辑器回车(@enter)或发送按钮导致自动发送。选完文件 800ms 内忽略发送请求。
      // 人不可能在选完文件 0.8s 内完成"看到预览→决定发送"，真实发送不受影响。
      if (this._filePickerClosedAt && Date.now() - this._filePickerClosedAt < 800) {
        console.log('[ChatBox] onSendMsg ignored: ghost input within 800ms after file picker close')
        return
      }

      // 检查登录状态
      if (!this.$store.state.alreadyLogin) {
        this.$store.state.needLogin = true
        return
      }

      // 检查是否正在等待 AI stream 响应
      if (this.isWaitingForStream) {
        Toast(this.$t('toast_ai_responding'))
        return
      }

      // 防止并发调用（如快速双击 suggestion），在 await 之前就加锁
      if (this._isSending) {
        return
      }
      this._isSending = true

      // 检查是否有图片正在上传
      if (this.uploadingImages) {
        this._isSending = false
        Toast(this.$t('toast_uploading_image'))
        return
      }

      // 检查是否有文件正在上传
      if (this.uploadingFiles) {
        this._isSending = false
        Toast(this.$t('toast_uploading_file'))
        return
      }

      // 检查是否有视频正在上传
      if (this.uploadingVideos) {
        this._isSending = false
        Toast(this.$t('toast_uploading_file'))
        return
      }

      // 录音中/语音上传中不允许发送
      if (this.isRecording) {
        this._isSending = false
        Toast(this.$t('voice_recording'))
        return
      }
      if (this.uploadingVoice) {
        this._isSending = false
        Toast(this.$t('toast_uploading_file'))
        return
      }

      var msg = {}
      var msgId = vm.$common.generateUUID()
      var rawContent = (this.$refs.emojiInputArea && typeof this.$refs.emojiInputArea.getRawContent === 'function') ? this.$refs.emojiInputArea.getRawContent() : ''

      // channel 会在 CreateChatMsg 时自动处理，不需要提前调用 update_session_channel

      // 检查是否有内容（文本、图片、文件、语音或位置至少有一个）
      var hasAttachments = this.uploadedFiles.length > 0 || this.selectedLocations.length > 0 || this.uploadedFilesList.length > 0 || this.uploadedVoice || this.uploadedVideos.length > 0
      if ((!rawContent || !rawContent.trim()) && !hasAttachments) {
        this._isSending = false
        return
      }

      // 记录用户第一条实质性消息，发布时默认作为 userMsg
      // 注意：文本取"第一条有字的"、图片取"第一条带图的"是故意设计——后续消息里的图
      // 也要并入 userMsg（不依赖首条消息是否带多媒体），别当成 bug 改成"只取首条消息内容"
      if (!this.firstUserMessageText && rawContent && rawContent.trim()) {
        this.firstUserMessageText = rawContent.trim()
      }
      // 记录用户第一条消息中的图片
      if (this.firstUserMessageImages.length == 0 && this.uploadedFiles.length > 0) {
        this.firstUserMessageImages = this.uploadedFiles.map(function(f) { return f.url || '' }).filter(function(u) { return !!u })
      }

      // 如果 chatSessionId 为空，先创建新 session
      if (!this.chatSessionId && !this.isUserChat) {
        await this.createNewChatSession()
        if (!this.chatSessionId) {
          this._isSending = false
          Toast('Failed to create session')
          return
        }
      }

      var toUserId = this.chat.contact.targetUserId
      // 根据是否有图片、文件或位置，决定content type（hasAttachments 已在上面定义）
      var contentType = hasAttachments ? 'mixed' : 'plaintext'
      // 构建msg内容
      var msgContent
      if (contentType === 'mixed') {
        // 构建内容数组，支持plaintext, img, pick_location等类型
        var contentArray = []
        // 添加文本内容（plaintext 在前）
        if (rawContent && rawContent.trim()) {
          contentArray.push({
            type: 'plaintext',
            content: rawContent
          })
        }
        // 添加图片
        this.uploadedFiles.forEach(file => {
          contentArray.push({
            type: 'img',
            content: file
          })
        })
        // 添加文件（仅私聊支持）
        if (this.uploadedFilesList.length > 0) {
          this.uploadedFilesList.forEach(file => {
            contentArray.push({
              type: 'file',
              content: file
            })
          })
        }
        // 添加视频（仅私聊支持）
        if (this.uploadedVideos.length > 0) {
          this.uploadedVideos.forEach(video => {
            contentArray.push({
              type: 'video',
              content: video
            })
          })
        }
        // 添加选中的位置
        this.selectedLocations.forEach(location => {
          contentArray.push({
            type: 'pick_location',
            content: location
          })
        })
        // 添加语音
        if (this.uploadedVoice) {
          contentArray.push({
            type: 'audio',
            content: this.uploadedVoice
          })
        }

        msgContent = JSON.stringify(contentArray)
      } else {
        msgContent = rawContent
      }
      msg = {
        chatSessionId: this.chatSessionId,
        msgId: msgId,
        toUserId: toUserId,
        contentType: contentType,
        msg: msgContent, // plaintext为纯文本，mixed为JSON字符串数组
        channelId: this.selectedChannelId || this.sessionChannelId || this.channelId || '', // 传递 channelId 字段（String 类型）
        channel: this.selectedChannel || '', // 兼容旧字段
      }

      if (this.reply.msg) {
        //deep copy
        msg.reply = JSON.parse(JSON.stringify(this.reply));
      }

      // 如果消息包含图片，且是发给 AI 平台用户，显示 analyzing loading
      if (contentType === 'mixed' && !this.isUserChat) {
        var hasImage = this.uploadedFiles.length > 0;
        if (hasImage) {
          this.analyzingImage = true;
          console.log('[DEBUG] 发给 AI 的消息包含图片，显示 analyzingImage loading');
        }
      }

      // 清空已上传的图片、文件、视频、语音和选中的位置
      this.stopVoicePreview();
      this.clearVoiceObjectUrl();
      this.uploadedFiles = [];
      this.uploadedFilesList = [];
      this.pendingFiles = [];
      this.uploadedVideos = [];
      this.pendingVideos = [];
      this.selectedLocations = [];
      this.uploadedVoice = null;

      // 完善消息对象
      msg.read = '0'
      msg.direction = '0'
      msg.serverReceived = '0'
      msg.createdAt = new Date().getTime()
      msg.createdAtText = this.$common.formatDateTime(msg.createdAt)
      msg.fromUserId = wsService.userId
      // 初始化消息发送状态
      msg.sendStatus = 'sending'

      console.log('[DEBUG] onSendMsg - 准备添加消息到聊天列表:', {
        msgId: msg.msgId,
        toUserId: msg.toUserId,
        chatSessionId: msg.chatSessionId,
        fromUserId: msg.fromUserId,
        direction: msg.direction
      })

      // 先发送消息（使用原始内容）
      console.log('msg', msg)
      var sendSuccess = wsService.sendMessage(msg);

      // 再调用 ChatMsgList 的 addMessage 方法（会解析内容用于显示）
      if (vm.$refs.chatMsgList) {
        console.log('[DEBUG] onSendMsg - chatMsgList 存在，准备调用 addMessage')
        vm.$refs.chatMsgList.addMessage(msg)
        console.log('[DEBUG] onSendMsg - addMessage 调用完成，当前 chatMsgs 数量:', vm.$refs.chatMsgList.chat.chatMsgs.length)
      } else {
        console.log('[DEBUG] onSendMsg - chatMsgList 不存在！')
      }
      if (!sendSuccess) {
        // 发送失败，更新消息状态为 failed
        if (this.$refs.chatMsgList) {
          this.$refs.chatMsgList.updateMessageSendStatus(msg.msgId, 'failed', this.$t('network_disconnected'))
        }
      } else {
        // 发送成功，如果是发给 AI 的消息，设置等待 stream 状态
        if (toUserId === this.platformUserId) {
          this.isWaitingForStream = true
          console.log('[DEBUG] Set isWaitingForStream = true')
          // 设置 30 秒超时
          this.lastStreamRefreshTime = Date.now()
          this.streamTimeoutId = setTimeout(() => {
            console.log('[DEBUG] Stream timeout, reset isWaitingForStream')
            this.isWaitingForStream = false
            Toast(this.$t('toast_ai_timeout'))
          }, 30000)
        }
      }

      if (this.reply.msg) {
        this.clearReply()
      }

      this.updateChatContacts(msg, true)

      if (this.$refs.emojiInputArea && typeof this.$refs.emojiInputArea.clearContentAndBlur === 'function') {
        this.$refs.emojiInputArea.clearContentAndBlur()
      }
      this._isSending = false
      return msgId
      // this.$refs.inputField.blur();
    },
    howItWorks: function (e) {
      this.showHowItWorksModel = true
    },
    limitLength(s, len) {
      if (s.length <= len) {
        return s;
      }
      return s.substring(0, len - 2) + ".."
    },
    // 处理图片选择
    async handleFileSelect(event) {
      const files = event.target.files;
      // 文件框真正关闭（无论选没选），刷新幽灵事件防护窗口
      this._filePickerClosedAt = Date.now();
      if (!files || files.length === 0) {
        return;
      }

      const remainingSlots = this.maxImages - this.uploadedFiles.length - this.pendingFiles.length;
      const filesToUpload = Array.from(files).slice(0, remainingSlots);

      if (filesToUpload.length < files.length) {
        Toast(this.$t('toast_max_photos', {max: this.maxImages}));
      }

      // 重置 input，允许重复选择同一张图片
      event.target.value = '';

      if (this.uploadingImages) {
        Toast(this.$t('toast_uploading'));
        return;
      }

      this.uploadingImages = true;

      try {
        for (const file of filesToUpload) {
          // 验证图片大小（最大 5MB）
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (file.size > maxSize) {
            Toast(this.$t('toast_file_too_large', {name: file.name}));
            continue;
          }

          // 压缩图片（最大 1024x1024，质量 85%，输出 JPEG）
          let uploadFile = file;
          try {
            uploadFile = await this.compressImage(file);
            console.log('[ImageCompress] original=' + file.size + ' compressed=' + uploadFile.size);
          } catch (compressErr) {
            console.error('[ImageCompress] failed, use original:', compressErr);
          }

          // 先添加到待上传列表显示
          const pendingFile = {
            url: URL.createObjectURL(uploadFile),
            name: file.name
          };
          this.pendingFiles.push(pendingFile);

          // 上传图片
          const fileResult = await this.uploadFileToS3(uploadFile);
          if (fileResult) {
            this.uploadedFiles.push(fileResult);
          }
          // 从待上传列表移除
          const pendingIndex = this.pendingFiles.findIndex(p => p.url === pendingFile.url);
          if (pendingIndex > -1) {
            this.pendingFiles.splice(pendingIndex, 1);
          }
        }
      } catch (error) {
        console.error('上传图片失败:', error);
        Toast(this.$t('toast_upload_failed'));
      } finally {
        this.uploadingImages = false;
        this.pendingFiles = [];
      }
    },

    // 压缩图片（使用 canvas）
    compressImage(file, maxWidth = 1024, maxHeight = 1024, quality = 0.85) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            let width = img.width;
            let height = img.height;

            // 等比例缩放，不超过最大尺寸
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('canvas toBlob failed'));
                return;
              }
              // 保持原文件名，类型统一为 jpeg
              const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
              resolve(compressedFile);
            }, 'image/jpeg', quality);
          };
          img.onerror = (err) => reject(err);
          img.src = e.target.result;
        };
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });
    },

    // 上传图片到 S3（前端直接上传）
    async uploadFileToS3(file, fileType = 'image') {
      try {
        // 生成唯一文件名（拼 128bit 随机 token 防枚举，S3 公共读下 URL 不可猜是防爬关键）
        const fileExtension = file.name.split('.').pop();
        const fileName = `${wsService.userId}_${Date.now()}_${this.genUploadToken()}.${fileExtension}`;

        // 确定 MIME 类型
        let mimeType = file.type;
        if (!mimeType || mimeType === 'application/octet-stream') {
          mimeType = this.getMimeType(file.name);
        }

        // 调用后端获取预签名 URL
        const presignResponse = await axios.post('/api/chat/get_s3_presigned_url', {
          fileName: fileName,
          fileType: mimeType,
          fileSize: file.size
        });

        if (presignResponse.data.code !== 0) {
          Toast(this.$t('toast_upload_link_failed') + ': ' + (presignResponse.data.msg || 'Unknown error'));
          return null;
        }

        console.log('presignResponse data:', presignResponse.data);
        const { uploadUrl, imagePath } = presignResponse.data.data;
        if (!uploadUrl || !imagePath) {
          Toast(this.$t('toast_upload_link_failed') + ': No URL returned');
          return null;
        }

        // 使用预签名 URL 直接上传到 S3，设置正确的 Content-Type
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': mimeType
          }
        });

        // 返回结果
        const result = {
          url: imagePath,
          type: fileType,
          name: file.name
        };
        // 如果是文件或视频，添加 fileSize
        if (fileType === 'file' || fileType === 'video') {
          result.fileSize = file.size;
        }
        return result;
      } catch (error) {
        console.error('上传到S3失败:', error);
        if (error.response && error.response.data && error.response.data.msg) {
          Toast(`Upload failed: ${error.response.data.msg}`);
        } else {
          Toast(this.$t('toast_upload_failed'));
        }
        return null;
      }
    },
    // ============ 语音消息录制 ============
    // 操作面板点击语音：关闭面板并开始录音
    openVoiceRecording() {
      this.showActionPanel = false
      this.startRecording()
    },
    async startRecording() {
      // 已有待发送语音或上传中，不再录新的
      if (this.uploadedVoice || this.uploadingVoice) return

      // 浏览器能力检查（非 HTTPS 或过老浏览器没有 mediaDevices）
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
        Toast(this.$t('toast_recording_not_supported'))
        return
      }

      // 收起操作面板
      this.showActionPanel = false

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        // 选择录制格式：优先 mp4，回退 webm（无论哪种，上传前都会转码为 WAV，全端兼容）
        let mimeType = ''
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4'
        } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus'
        }

        this.audioChunks = []
        this.mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType: mimeType }) : new MediaRecorder(stream)
        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            this.audioChunks.push(e.data)
          }
        }
        this.mediaRecorder.start()

        this.isRecording = true
        this.recordingSeconds = 0
        // 计时，最长 60 秒自动停止并发送
        this.recordingTimer = setInterval(() => {
          this.recordingSeconds++
          if (this.recordingSeconds >= 60) {
            this.stopRecording(false)
          }
        }, 1000)
      } catch (error) {
        console.error('[ChatBox] Start recording failed:', error)
        if (error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
          Toast(this.$t('toast_mic_permission_denied'))
        } else {
          Toast(this.$t('toast_voice_failed'))
        }
      }
    },
    // 停止录音（discard=true 丢弃）
    stopRecording(discard) {
      const recorder = this.mediaRecorder
      if (!recorder || !this.isRecording) return

      clearInterval(this.recordingTimer)
      this.recordingTimer = null
      const seconds = this.recordingSeconds
      this.isRecording = false
      this.recordingSeconds = 0

      if (discard) {
        recorder.ondataavailable = null
        recorder.onstop = null
        this.stopRecorderTracks(recorder)
        this.mediaRecorder = null
        this.audioChunks = []
        return
      }

      // 录音太短，丢弃
      if (seconds < 1) {
        recorder.ondataavailable = null
        recorder.onstop = null
        this.stopRecorderTracks(recorder)
        this.mediaRecorder = null
        this.audioChunks = []
        Toast(this.$t('toast_voice_too_short'))
        return
      }

      recorder.onstop = async () => {
        this.stopRecorderTracks(recorder)
        this.mediaRecorder = null
        try {
          const rawBlob = new Blob(this.audioChunks, { type: recorder.mimeType || 'audio/webm' })
          this.audioChunks = []
          if (rawBlob.size <= 0) {
            Toast(this.$t('toast_voice_failed'))
            return
          }
          this.uploadingVoice = true
          // 关键：浏览器 MediaRecorder 产出的是 webm/opus 或 fragmented MP4，
          // Android/iOS 原生播放器不支持这两种容器（Android MediaPlayer 报 -14 UNSUPPORTED）。
          // 统一转码为 16kHz 单声道 WAV（全端 100% 兼容的保底格式）再上传
          const wavBlob = await this.blobToWav16kMono(rawBlob)
          if (!wavBlob) {
            Toast(this.$t('toast_voice_failed'))
            return
          }
          // 先立即展示本地预览（objectURL 可零延迟试听），后台上传（与图片/文件流程一致）
          // key 拼入 128bit 随机 token：S3 公共读下时间戳可被枚举，token 是防爬关键
          const fileName = `voice_${wsService.userId}_${Date.now()}_${this.genUploadToken()}.wav`
          this.setVoiceObjectUrl(wavBlob)
          this.uploadedVoice = {
            url: '',
            name: fileName,
            fileSize: wavBlob.size,
            duration: seconds
          }
          const voice = await this.uploadVoiceToS3(wavBlob, fileName, seconds)
          if (voice) {
            // 等待期间用户可能已删除预览，此时不要回填
            if (this.uploadedVoice) {
              this.uploadedVoice = voice
            }
          } else {
            this.clearVoiceObjectUrl()
            this.uploadedVoice = null
            Toast(this.$t('toast_voice_failed'))
          }
        } catch (e) {
          console.error('[ChatBox] Process recording failed:', e)
          Toast(this.$t('toast_voice_failed'))
        } finally {
          this.uploadingVoice = false
        }
      }
      recorder.stop()
    },
    // 释放 MediaRecorder 关联的轨道（关闭麦克风占用指示）
    stopRecorderTracks(recorder) {
      try {
        if (recorder && recorder.stream) {
          recorder.stream.getTracks().forEach(track => track.stop())
        }
      } catch (e) {
        console.error('[ChatBox] Stop recorder tracks failed:', e)
      }
    },
    // 将 MediaRecorder blob 转码为 16kHz 单声道 16bit PCM WAV
    // （Android/iOS 原生 MediaPlayer 不支持 webm 与 fragmented MP4，WAV 是全端兼容的保底格式）
    async blobToWav16kMono(rawBlob) {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        if (!AudioCtx) return null
        const audioCtx = new AudioCtx()
        try {
          const arrayBuf = await rawBlob.arrayBuffer()
          const audioBuf = await audioCtx.decodeAudioData(arrayBuf)
          const srcRate = audioBuf.sampleRate
          const targetRate = 16000
          const srcData = audioBuf.getChannelData(0) // 取第一声道转 mono
          const ratio = srcRate / targetRate
          const outLen = Math.floor(srcData.length / ratio)
          const pcm = new Int16Array(outLen)
          for (let i = 0; i < outLen; i++) {
            // 线性插值下采样
            const pos = i * ratio
            const idx = Math.floor(pos)
            const frac = pos - idx
            let sample = (idx + 1 < srcData.length)
              ? srcData[idx] * (1 - frac) + srcData[idx + 1] * frac
              : srcData[idx]
            sample = Math.max(-1, Math.min(1, sample))
            pcm[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF
          }
          // 组装 WAV 文件（44 字节头 + PCM 数据）
          const dataLen = outLen * 2
          const buffer = new ArrayBuffer(44 + dataLen)
          const view = new DataView(buffer)
          const writeStr = (off, s) => {
            for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i))
          }
          writeStr(0, 'RIFF')
          view.setUint32(4, 36 + dataLen, true)
          writeStr(8, 'WAVE')
          writeStr(12, 'fmt ')
          view.setUint32(16, 16, true) // fmt chunk size
          view.setUint16(20, 1, true) // PCM format
          view.setUint16(22, 1, true) // mono
          view.setUint32(24, targetRate, true)
          view.setUint32(28, targetRate * 2, true) // byteRate = rate * blockAlign
          view.setUint16(32, 2, true) // blockAlign = 1ch * 2bytes
          view.setUint16(34, 16, true) // bits per sample
          writeStr(36, 'data')
          view.setUint32(40, dataLen, true)
          new Int16Array(buffer, 44).set(pcm)
          return new Blob([buffer], { type: 'audio/wav' })
        } finally {
          audioCtx.close().catch(() => {})
        }
      } catch (e) {
        console.error('[ChatBox] Transcode to wav failed:', e)
        return null
      }
    },
    // 生成 128bit 安全随机 token（32位hex），用于 S3 key 防枚举：
    // S3 公共读下 URL 不可猜是防爬唯一屏障，时间戳可被枚举，必须拼高熵随机段
    genUploadToken() {
      const bytes = new Uint8Array(16)
      crypto.getRandomValues(bytes)
      return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
    },
    // 上传语音到 S3（fileName 由调用方生成，与预览保持一致）
    async uploadVoiceToS3(blob, fileName, duration) {
      try {
        // Safari 录出来是 audio/mp4；Chrome 兼容旧实现可能报 audio/webm;codecs=opus，取主类型
        let mimeType = blob.type || 'audio/mp4'
        if (mimeType.indexOf(';') >= 0) {
          mimeType = mimeType.split(';')[0]
        }

        const presignResponse = await axios.post('/api/chat/get_s3_presigned_url', {
          fileName: fileName,
          fileType: mimeType,
          fileSize: blob.size
        })

        if (presignResponse.data.code !== 0) {
          Toast(this.$t('toast_upload_link_failed') + ': ' + (presignResponse.data.msg || 'Unknown error'))
          return null
        }

        const { uploadUrl, imagePath } = presignResponse.data.data
        if (!uploadUrl || !imagePath) {
          Toast(this.$t('toast_upload_link_failed') + ': No URL returned')
          return null
        }

        await axios.put(uploadUrl, blob, {
          headers: {
            'Content-Type': mimeType
          }
        })

        return {
          url: imagePath,
          name: fileName,
          fileSize: blob.size,
          duration: duration
        }
      } catch (error) {
        console.error('[ChatBox] 上传语音失败:', error)
        return null
      }
    },
    // 本地 objectURL 管理（录音 blob 的本地预览/试听地址）
    setVoiceObjectUrl(blob) {
      this.clearVoiceObjectUrl()
      try {
        this._voiceObjectUrl = URL.createObjectURL(blob)
      } catch (e) {
        this._voiceObjectUrl = null
      }
    },
    clearVoiceObjectUrl() {
      if (this._voiceObjectUrl) {
        try {
          URL.revokeObjectURL(this._voiceObjectUrl)
        } catch (e) { /* ignore */ }
        this._voiceObjectUrl = null
      }
    },
    // 移除已上传的语音
    removeUploadedVoice() {
      this.stopVoicePreview()
      this.clearVoiceObjectUrl()
      this.uploadedVoice = null
    },
    // ============ 语音预览试听 ============
    // 点击语音预览条：播放/暂停（上传中也允许，播本地 objectURL）
    toggleVoicePreview() {
      const voice = this.uploadedVoice
      if (!voice) return
      // 正在播 → 暂停
      if (this.voicePreviewPlaying) {
        this.stopVoicePreview()
        return
      }
      // 优先本地 objectURL（录音刚结束即可零延迟试听），否则远程 CDN
      const url = this._voiceObjectUrl || this.$common.getFullImageUrl(voice.url, this.$config)
      if (!url) return
      if (!this._voicePreviewAudio) {
        this._voicePreviewAudio = new Audio()
        this._voicePreviewAudio.onended = () => {
          this.voicePreviewPlaying = false
        }
      }
      this._voicePreviewAudio.src = url
      this._voicePreviewAudio.play().then(() => {
        this.voicePreviewPlaying = true
      }).catch((e) => {
        console.error('[ChatBox] Voice preview play failed:', e)
        this.voicePreviewPlaying = false
      })
    },
    stopVoicePreview() {
      if (this._voicePreviewAudio) {
        this._voicePreviewAudio.pause()
      }
      this.voicePreviewPlaying = false
    },
    // 秒 → m:ss（录音/语音预览用）
    formatRecordingDuration(seconds) {
      const total = Math.floor(seconds || 0)
      const m = Math.floor(total / 60)
      const s = total % 60
      return m + ':' + (s < 10 ? '0' : '') + s
    },
    // 获取 MIME 类型
    getMimeType(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      const mimeTypes = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'txt': 'text/plain',
        'zip': 'application/zip',
        'rar': 'application/vnd.rar',
        '7z': 'application/x-7z-compressed',
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'mp4': 'video/mp4',
        'avi': 'video/x-msvideo',
        'mov': 'video/quicktime',
        'json': 'application/json',
        'xml': 'application/xml'
      };
      return mimeTypes[ext] || 'application/octet-stream';
    },
    // 删除已上传的图片
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },
    // 删除待上传的图片
    removePendingFile(index) {
      this.pendingFiles.splice(index, 1);
      if (this.pendingFiles.length === 0 && this.uploadedFiles.length === 0) {
        this.uploadingImages = false;
      }
    },
    // 删除已上传的文件
    removeUploadedFileItem(index) {
      this.uploadedFilesList.splice(index, 1);
    },
    // 切换操作面板显示
    toggleActionPanel() {
      this.showActionPanel = !this.showActionPanel;
      if (this.showActionPanel) {
        this.showChannelPicker = false;
      }
    },
    // 打开位置选择器
    openLocationPicker() {
      // 使用 MapLocationPicker 组件
      this.showActionPanel = false;
      // 触发位置选择 - 这里需要通过某种方式调用 MapLocationPicker
      // 由于 MapLocationPicker 是嵌入式的，我们可以显示一个弹窗或者使用其他方式
      // 参考 client 的做法，使用 van-popup 包裹 MapLocationPicker
      this.showLocationPickerPopup = true;
      this.$nextTick(() => {
        if (this.$refs.locationPicker) {
          this.$refs.locationPicker.forceInitMap();
        }
      });
    },
    // 打开emoji选择器
    openEmojiPicker() {
      this.showActionPanel = false;
      this.showEmojiPickerPopup = true;
    },
    // 选择emoji - 插入到输入框光标位置
    selectEmoji(emoji) {
      if (this.$refs.emojiInputArea && typeof this.$refs.emojiInputArea.insertEmoji === 'function') {
        this.$refs.emojiInputArea.insertEmoji(emoji);
      }
      // 关闭emoji选择器弹窗
      this.showEmojiPickerPopup = false;
    },
    // 解析emoji为twemoji图片
    parseEmoji(emoji) {
      return twemoji.parse(emoji, {
        className: 'emoji-small-img',
        base: this.$publicPath + 'static/twemoji/',
        size: 'svg',
        ext: '.svg'
      });
    },
    // 处理位置选择
    handleLocationSelected(location) {
      console.log('用户选中位置:', location);
      this.selectedLocations = [{
        address: location.address || '',
        lng: location.lng,
        lat: location.lat
      }];
      this.showLocationPickerPopup = false;

      // 位置数据已通过 selectedLocations 存储，不再将经纬度追加到输入框

      Toast.success(this.$t('toast_location_selected'));
    },
    // 触发图片上传
    triggerFileUpload() {
      if (this.uploadedFiles.length + this.pendingFiles.length >= this.maxImages) {
        Toast(this.$t('toast_max_photos', {max: this.maxImages}));
        return;
      }
      this.showActionPanel = false;
      // 记录文件框打开时间：OS 文件框确认动作（Enter/点击）可能以幽灵事件漏进页面误触发送
      this._filePickerClosedAt = Date.now();
      this.$nextTick(() => {
        if (this.$refs.imageInput) {
          this.$refs.imageInput.click();
        }
      });
    },
    // 触发文件上传（仅私聊）
    triggerFileInput() {
      if (this.uploadedFilesList.length >= this.maxFiles) {
        Toast(`Max ${this.maxFiles} files allowed`);
        return;
      }
      this.showActionPanel = false;
      this._filePickerClosedAt = Date.now();
      this.$nextTick(() => {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.click();
        }
      });
    },
    // 文件框被取消关闭时也记录时间（cancel 无 change 事件）
    handleFilePickerCancel() {
      this._filePickerClosedAt = Date.now();
    },
    // 触发视频上传（仅私聊）
    triggerVideoUpload() {
      if (this.uploadedVideos.length >= this.maxVideos) {
        Toast(`Max ${this.maxVideos} videos allowed`);
        return;
      }
      this.showActionPanel = false;
      this.$nextTick(() => {
        if (this.$refs.videoInput) {
          this.$refs.videoInput.click();
        }
      });
    },
    // 处理视频选择（仅私聊）
    async handleVideoInputChange(event) {
      const files = event.target.files;
      this._filePickerClosedAt = Date.now();
      if (!files || files.length === 0) {
        return;
      }

      const remainingSlots = this.maxVideos - this.uploadedVideos.length;
      const filesToUpload = Array.from(files).slice(0, remainingSlots);

      if (filesToUpload.length < files.length) {
        Toast(`Max ${this.maxVideos} videos allowed`);
      }

      // 重置 input，允许重复选择同一文件
      event.target.value = '';

      if (this.uploadingVideos) {
        Toast(this.$t('toast_uploading'));
        return;
      }

      this.uploadingVideos = true;

      try {
        for (const file of filesToUpload) {
          // 仅支持视频类型
          if (file.type && !file.type.startsWith('video/')) {
            Toast(this.$t('toast_upload_failed'));
            continue;
          }

          // 验证文件大小（视频最大 20MB，与后端限制保持一致）
          const maxSize = 20 * 1024 * 1024; // 20MB
          let sendFile = file;

          // 所有视频统一转码为 720p mp4（降低 S3 存储/CDN 带宽成本）
          // 转码后仍超 20MB 自动降 480p 再转一次，仍超才拒绝
          // 转码不可用（老浏览器）时：原文件合规则直传原文件，超限才拒绝
          Toast(this.$t('toast_video_processing'));
          const compressed = await this.compressVideo(file, maxSize);
          if (compressed) {
            sendFile = compressed;
          } else if (file.size > maxSize) {
            Toast(this.$t('toast_video_too_large', {name: file.name}));
            continue;
          }

          // 本地抽帧：dataUrl 用于输入栏首帧预览，blob 上传 S3 给接收端
          const frame = await this.captureVideoFrame(sendFile);

          // 先添加到待上传列表显示
          const pendingVideo = {
            name: sendFile.name,
            fileSize: sendFile.size,
            thumbDataUrl: frame ? frame.dataUrl : '',
            url: URL.createObjectURL(sendFile)
          };
          this.pendingVideos.push(pendingVideo);

          // 并行：上传视频 + 上传首帧缩略图 + 提取时长
          const results = await Promise.all([
            this.uploadFileToS3(sendFile, 'video'),
            frame && frame.blob ? this.uploadVideoThumbBlob(frame.blob) : Promise.resolve(null),
            this.getVideoDuration(sendFile)
          ]);
          const videoResult = results[0];
          if (videoResult) {
            // 首帧缩略图 S3 path（接收端直接加载该图，无需下载视频本体）
            videoResult.thumb = results[1];
            // 补充时长信息（上传接口不返回时长，这里在前端提取）
            videoResult.duration = results[2];
            // 本地首帧（输入栏预览用）
            videoResult.thumbDataUrl = frame ? frame.dataUrl : '';
            this.uploadedVideos.push(videoResult);
          }
          // 从待上传列表移除
          const pendingIndex = this.pendingVideos.findIndex(p => p.url === pendingVideo.url);
          if (pendingIndex > -1) {
            this.pendingVideos.splice(pendingIndex, 1);
            URL.revokeObjectURL(pendingVideo.url);
          }
        }
      } catch (error) {
        console.error('上传视频失败:', error);
        Toast(this.$t('toast_upload_failed'));
      } finally {
        this.uploadingVideos = false;
        this.pendingVideos = [];
      }
    },
    // 按需加载 mediabunny（self-hosted ESM，运行时动态注入 module script，
    // 完全绕开 webpack3/babel6 构建链，加载完成暴露全局 window.Mediabunny）
    async ensureMediabunny() {
      if (window.Mediabunny) return
      // 并发调用时复用同一个加载 promise
      if (!this._mediabunnyLoading) {
        this._mediabunnyLoading = new Promise((resolve, reject) => {
          const timer = setTimeout(() => {
            reject(new Error('mediabunny load timeout'))
          }, 15000)
          const onReady = () => {
            clearTimeout(timer)
            resolve()
          }
          window.addEventListener('mediabunny-ready', onReady, { once: true })
          const bridge = document.createElement('script')
          bridge.type = 'module'
          bridge.textContent =
            "import * as m from '/static/js/mediabunny.min.mjs'; window.Mediabunny = m; window.dispatchEvent(new Event('mediabunny-ready'));"
          bridge.onerror = () => {
            clearTimeout(timer)
            window.removeEventListener('mediabunny-ready', onReady)
            reject(new Error('mediabunny load failed'))
          }
          document.head.appendChild(bridge)
        })
      }
      await this._mediabunnyLoading
    },
    // 浏览器端视频压缩：mediabunny(WebCodecs 硬件编码) 转码为 H.264/AAC mp4
    // Chrome 94+ / Edge / Safari 16.4+ / Firefox 130+ 均支持，覆盖主流用户
    // 两档策略：先 720p/2Mbps（20MB ≈ 2Mbps × 80s），仍超 maxSize 再降 480p/1Mbps
    // 不支持/失败/仍超限 返回 null（调用方决定是否兜底直传或拒绝）
    async compressVideo(file, maxSize) {
      const presets = [
        { height: 720, bitrate: 2000000 },
        { height: 480, bitrate: 1000000 }
      ]
      try {
        await this.ensureMediabunny()
        const Mb = window.Mediabunny
        for (const preset of presets) {
          try {
            const input = new Mb.Input({
              source: new Mb.BlobSource(file),
              formats: Mb.ALL_FORMATS
            })
            const output = new Mb.Output({
              format: new Mb.Mp4OutputFormat(),
              target: new Mb.BufferTarget()
            })
            const conversion = await Mb.Conversion.init({
              input,
              output,
              video: async (videoTrack) => ({
                codec: 'avc',
                height: Math.min(await videoTrack.getDisplayHeight(), preset.height),
                quality: new Mb.Quality({ bitrate: preset.bitrate }),
                frameRate: 30
              }),
              audio: {
                codec: 'aac',
                quality: new Mb.Quality({ bitrate: 128000 })
              }
            })
            if (!conversion.isValid) {
              // 环境不支持编码（如无 WebCodecs），放弃压缩
              return null
            }
            await conversion.execute()
            const buffer = output.target && output.target.buffer
            if (buffer && buffer.byteLength > 0) {
              const blob = new Blob([buffer], { type: 'video/mp4' })
              // 空产出或没有明显变小，试下一档
              if (blob.size > 0 && blob.size < file.size) {
                const name = file.name.replace(/\.[^.]+$/, '') + '.mp4'
                const compressed = new File([blob], name, { type: 'video/mp4' })
                if (compressed.size <= maxSize) {
                  return compressed
                }
              }
            }
          } catch (e) {
            console.error('compress video preset failed:', e)
            // 尝试下一档
          }
        }
        return null
      } catch (e) {
        console.error('compress video failed:', e)
        return null
      }
    },
    // 获取视频时长（秒），失败返回0
    getVideoDuration(file) {
      return new Promise((resolve) => {
        try {
          const video = document.createElement('video');
          video.preload = 'metadata';
          const objectUrl = URL.createObjectURL(file);
          const cleanup = () => {
            URL.revokeObjectURL(objectUrl);
            video.removeAttribute('src');
          };
          const timeout = setTimeout(() => {
            cleanup();
            resolve(0);
          }, 4000);
          video.onloadedmetadata = () => {
            clearTimeout(timeout);
            const d = video.duration && isFinite(video.duration) ? Math.round(video.duration) : 0;
            cleanup();
            resolve(d);
          };
          video.onerror = () => {
            clearTimeout(timeout);
            cleanup();
            resolve(0);
          };
          video.src = objectUrl;
        } catch (e) {
          resolve(0);
        }
      });
    },
    // 本地抽帧：加载视频 seek 到 0.1s 绘制到 canvas，一次产出
    // dataUrl（输入栏预览用）和 blob（上传 S3 给接收端用）；失败返回 null
    captureVideoFrame(file) {
      return new Promise((resolve) => {
        try {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.muted = true;
          video.playsInline = true;
          const objectUrl = URL.createObjectURL(file);
          const cleanup = () => {
            try { URL.revokeObjectURL(objectUrl); } catch (e) { /* ignore */ }
            video.removeAttribute('src');
          };
          const timeout = setTimeout(() => {
            cleanup();
            resolve(null);
          }, 8000);
          // loadeddata 后 seek 0.1s：部分容器首帧此时还未解码，seek 一下确保可绘制
          video.onloadeddata = () => {
            try {
              video.currentTime = 0.1;
            } catch (e) {
              clearTimeout(timeout);
              cleanup();
              resolve(null);
            }
          };
          video.onseeked = () => {
            try {
              const canvas = document.createElement('canvas');
              const maxW = 512;
              const vw = video.videoWidth || maxW;
              const vh = video.videoHeight || Math.round(maxW * 9 / 16);
              const scale = Math.min(1, maxW / vw);
              canvas.width = Math.max(1, Math.round(vw * scale));
              canvas.height = Math.max(1, Math.round(vh * scale));
              const ctx = canvas.getContext('2d');
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
              canvas.toBlob((blob) => {
                clearTimeout(timeout);
                cleanup();
                resolve({ dataUrl, blob: blob && blob.size > 0 ? blob : null });
              }, 'image/jpeg', 0.8);
            } catch (e) {
              clearTimeout(timeout);
              cleanup();
              resolve(null);
            }
          };
          video.onerror = () => {
            clearTimeout(timeout);
            cleanup();
            resolve(null);
          };
          video.src = objectUrl;
        } catch (e) {
          resolve(null);
        }
      });
    },
    // 上传首帧缩略图 blob 到 S3，返回 imagePath（不含host）；失败返回 null（接收端退化为深色兜底）
    async uploadVideoThumbBlob(thumbBlob) {
      try {
        const thumbFile = new File([thumbBlob], 'thumb_' + Date.now() + '.jpg', { type: 'image/jpeg' });
        const result = await this.uploadFileToS3(thumbFile, 'image');
        return result ? result.url : null;
      } catch (e) {
        return null;
      }
    },
    // 删除已上传的视频
    removeUploadedVideo(index) {
      this.uploadedVideos.splice(index, 1);
    },
    // 删除待上传的视频
    removePendingVideo(index) {
      this.pendingVideos.splice(index, 1);
    },
    // 处理文件选择
    async handleFileInputChange(event) {
      const files = event.target.files;
      this._filePickerClosedAt = Date.now();
      if (!files || files.length === 0) {
        return;
      }

      const remainingSlots = this.maxFiles - this.uploadedFilesList.length;
      const filesToUpload = Array.from(files).slice(0, remainingSlots);

      if (filesToUpload.length < files.length) {
        Toast(`Max ${this.maxFiles} files allowed`);
      }

      // 重置 input，允许重复选择同一文件
      event.target.value = '';

      if (this.uploadingFiles) {
        Toast(this.$t('toast_uploading'));
        return;
      }

      this.uploadingFiles = true;

      try {
        for (const file of filesToUpload) {
          // 验证文件大小（最大 5MB）
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (file.size > maxSize) {
            Toast(this.$t('toast_file_too_large', {name: file.name}));
            continue;
          }

          // 先添加到待上传列表显示
          const pendingFile = {
            name: file.name,
            fileSize: file.size,
            url: URL.createObjectURL(file)
          };
          this.pendingFilesList.push(pendingFile);

          // 上传文件
          const fileResult = await this.uploadFileToS3(file, 'file');
          if (fileResult) {
            this.uploadedFilesList.push(fileResult);
          }
          // 从待上传列表移除
          const pendingIndex = this.pendingFilesList.findIndex(p => p.name === file.name && p.url === pendingFile.url);
          if (pendingIndex > -1) {
            this.pendingFilesList.splice(pendingIndex, 1);
          }
        }
      } catch (error) {
        console.error('上传文件失败:', error);
        Toast(this.$t('toast_upload_failed'));
      } finally {
        this.uploadingFiles = false;
        this.pendingFilesList = [];
      }
    },
    // 获取文件图标
    getFileIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      const iconMap = {
        'pdf': 'description',
        'doc': 'description',
        'docx': 'description',
        'xls': 'chart',
        'xlsx': 'chart',
        'ppt': 'slideshow',
        'pptx': 'slideshow',
        'txt': 'description',
        'zip': 'description',
        'rar': 'description',
        'mp3': 'music',
        'wav': 'music',
        'mp4': 'video',
        'avi': 'video',
        'mov': 'video'
      };
      return iconMap[ext] || 'description';
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes < 1024) {
        return bytes + ' B';
      } else if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(1) + ' KB';
      } else {
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      }
    },
    // 删除已选中的位置
    removeLocation(index) {
      this.selectedLocations.splice(index, 1);
    },
    // 处理从 ChatMsgList 传来的日期选择事件 - 直接将文本放入输入框
    handleDateSelectedFromList(data) {
      console.log('用户选中日期:', data)
      if (data.type === 'pick_date') {
        // 单日期选择
        const dateText = `Date: ${data.date}`
        this.replaceTextInInput(dateText)
      } else if (data.type === 'pick_date_range') {
        // 日期范围选择
        const dateRangeText = `Date Range: ${data.startDate} to ${data.endDate}`
        this.replaceTextInInput(dateRangeText)
      }
    },
    // 替换输入框中的日期文本（查找已有的 Date/Date Range 开头的文本并替换）
    replaceTextInInput(text) {
      if (this.$refs.emojiInputArea) {
        const currentContent = this.$refs.emojiInputArea.getRawContent() || ''
        console.log('当前输入框内容:', currentContent)

        // 使用正则匹配已有的日期文本并替换
        // 移除 ^ 锚点，匹配任意位置；简化正则，更宽松
        const dateRegex = /Date Range: .+? to .+|Date: \d{4}-\d{2}-\d{2}/
        const hasDatePattern = dateRegex.test(currentContent)

        console.log('是否匹配到日期文本:', hasDatePattern)

        let newContent
        if (hasDatePattern) {
          // 有日期文本，替换
          newContent = currentContent.replace(dateRegex, text.trim())
        } else {
          // 没有日期文本，追加
          newContent = currentContent ? `${currentContent} ${text}` : text
        }

        console.log('替换后内容:', newContent)
        this.$refs.emojiInputArea.setContent(newContent)
      }
    },
    // 追加文本到输入框（保留给位置选择使用）
    appendTextToInput(text) {
      if (this.$refs.emojiInputArea) {
        const currentContent = this.$refs.emojiInputArea.getRawContent() || ''
        const newContent = currentContent ? `${currentContent} ${text}` : text
        this.$refs.emojiInputArea.setContent(newContent)
      }
    },
    // 预览图片
    previewImage(url) {
      console.log('previewImage')
      ImagePreview({
        images: [url],
        closeable: true,
        closeIcon: 'cross',
        closeIconPosition: 'top-right',
        closeOnClickOverlay: true,
        showIndex: false
      });
    },
    // 处理需求澄清 - 全局设为公开
    async doSetPublic() {
      if (this.setPublicLoading) {
        return
      }
      this.setPublicLoading = true
      try {
        const response = await axios.post('/api/chat/update_public', {
          chatSessionId: this.chatSessionId,
          public: 1
        })
        if (response.data.code === 0) {
          Toast.success(this.$t('toast_shared_finding'))
          this.public = '1'
          this.actualPublic = 1
          // 自动调用 findMatch
          setTimeout(() => {
            this.findMatch()
          }, 500)
        } else {
          // 框架返回结构是 {code, data}，错误文案在 data（如：需求卡已被平台限制发布）
          Toast.fail(this.$t('toast_update_failed') + ': ' + (response.data.data || 'Unknown error'))
        }
      } catch (error) {
        console.error('设置公开失败:', error)
        Toast.fail(this.$t('toast_update_failed') + ', please try again')
      } finally {
        this.setPublicLoading = false
      }
    },
    handleSetPublicGlobal() {
      // 临时用户需要先登录/注册
      if (this.$store.state.alreadyLogin && !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      this.showPublicConfirmDialog();
    },
    showPublicConfirmDialog() {
      Dialog.confirm({
        title: this.$t('aiChat_setPublicTitle'),
        message: this.$t('aiChat_setPublicMessage'),
        messageAlign: 'left',
        width: '85vw',
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
        confirmButtonColor: '#E8543A',
      }).then(() => {
        this.doSetPublic();
      }).catch(() => { });
    },
    // 处理需求澄清 - 全局设为私有
    async handleSetPrivateGlobal() {
      if (this.setPrivateLoading) return
      this.setPrivateLoading = true
      try {
        const response = await axios.post('/api/chat/update_public', {
          chatSessionId: this.chatSessionId,
          public: -1
        })
        if (response.data.code === 0) {
          Toast.success(this.$t('toast_set_private'))
          this.public = '0'
          this.actualPublic = -1
        } else {
          // 框架返回结构是 {code, data}，错误文案在 data（如：需求卡已被平台限制发布）
          Toast.fail(this.$t('toast_update_failed') + ': ' + (response.data.data || 'Unknown error'))
        }
      } catch (error) {
        console.error('设置私有失败:', error)
        Toast.fail(this.$t('toast_update_failed') + ', please try again')
      } finally {
        this.setPrivateLoading = false
      }
    },
    // 暂停/恢复匹配（与公开/私密解耦：消息仍可见，仅停止/恢复 AI 持续匹配）
    async handleToggleMatchPaused() {
      if (this.matchPausedLoading) return
      // 临时用户需要先登录/注册
      if (this.$store.state.alreadyLogin && !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      this.matchPausedLoading = true
      try {
        var newMatchPaused = this.matchPaused == 1 ? 0 : 1
        const response = await axios.post('/api/chat/update_match_paused', {
          chatSessionId: this.chatSessionId,
          matchPaused: newMatchPaused
        })
        if (response.data.code === 0) {
          this.matchPaused = newMatchPaused
          Toast.success(newMatchPaused == 1 ? this.$t('session_matchPausedToast') : this.$t('session_matchResumedToast'))
        } else {
          Toast.fail(this.$t('toast_update_failed') + ': ' + (response.data.data || 'Unknown error'))
        }
      } catch (error) {
        console.error('toggleMatchPaused 失败:', error)
        Toast.fail(this.$t('toast_update_failed') + ', please try again')
      } finally {
        this.matchPausedLoading = false
      }
    },
    // 查找匹配的会话（纯前端模拟进度，不真正触发后端）
    async findMatch(force = false) {
      // 临时用户需要先登录/注册
      if (this.$store.state.alreadyLogin && !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      if (this.matchLoading) {
        this.showMatchResult = true
        return
      }

      // 确保 chatSessionId 有效
      if (!this.chatSessionId || this.chatSessionId.includes('undefined')) {
        console.warn('[DEBUG] findMatch: chatSessionId 无效，重新创建', this.chatSessionId)
        await this.createNewChatSession()
      }

      // 重置状态（直接显示模拟动画，不显示loading）
      this.matchResults = []
      this.matchInterested = []
      this.matchIsComplete = false
      this.hasNewMatches = false
      this.newMatchCount = 0
      this.knownMatchIds = new Set()
      this.matchIsMatching = true
      this.matchCurrentStage = 'FETCH_CHAT'
      this.matchLoading = false // 确保不显示loading icon

      // 先显示弹窗
      this.showMatchResult = true

      // 启动前端模拟进度动画（不请求后端）
      this.startFakeProgress()

      // 从 getMatchHistory 加载已有匹配结果
      await this.loadMatchResults()

      // 启动持续匹配定时器
      this.startContinuousMatching()
    },
    // 启动纯前端模拟进度动画
    startFakeProgress() {
      const stages = ['FETCH_CHAT', 'QUERY_EXPANSION', 'SEMANTIC_SEARCH', 'DEEP_SEARCH', 'RERANK', 'REFINING', 'FETCH_DETAILS']

      let stageIndex = 0
      this.matchProgressTimer = setInterval(() => {
        stageIndex = (stageIndex + 1) % stages.length
        this.matchCurrentStage = stages[stageIndex]
      }, 3000)
    },
    // 获取阶段索引
    getStageIndex(stage) {
      const stageKeys = ['FETCH_CHAT', 'QUERY_EXPANSION', 'SEMANTIC_SEARCH', 'DEEP_SEARCH', 'RERANK', 'REFINING', 'FETCH_DETAILS']
      return stageKeys.indexOf(stage)
    },
    // 从 getMatchHistory 加载匹配结果
    async loadMatchResults() {
      try {
        const response = await axios.post('/api/chat/get_match_history', {
          chatSessionId: this.chatSessionId,
          pageSize: 50,
          curPage: 1
        })

        if (response.data.code === 0 && response.data.data) {
          const details = response.data.data.matchDetails || []
          const matches = []
          const interested = []

          for (const d of details) {
            const otherSessionId = d.matchedChatSessionId || ''
            if (!otherSessionId) continue

            const item = {
              knowledge_id: otherSessionId,
              aiSummary: d.matchedAiSummary || '',
              matchedUserMsg: d.matchedUserMsg || '',
              matchedPublic: d.matchedPublic,
              userName: d.matchedUserName || '',
              avatar: d.matchedAvatar || '',
              brief_match_reason: d.briefMatchReason || '',
              detailed_match_reason: d.detailedMatchReason || '',
              match_level: d.matchLevel || 'interested',
              matchDetailId: d.matchDetailId,
              channel_id: d.channelId || 0,
              createdAtText: d.createdAt ? vm.$common.formatDateTime(d.createdAt) : ''
            }

            if (d.matchLevel === 'matched') {
              matches.push(item)
            } else {
              interested.push(item)
            }
          }

          // 检测新匹配
          let newCount = 0
          for (const m of [...matches, ...interested]) {
            const id = m.knowledge_id
            if (id && !this.knownMatchIds.has(id)) {
              newCount++
            }
          }

          if (newCount > 0) {
            this.hasNewMatches = true
            this.newMatchCount = newCount
          }

          // 如果有真正的匹配结果，停止模拟动画
          if (matches.length > 0 && this.matchIsMatching) {
            if (this.matchProgressTimer) {
              clearInterval(this.matchProgressTimer)
              this.matchProgressTimer = null
            }
            this.matchIsMatching = false
          }

          this.matchResults = matches
          this.matchInterested = interested

          // 更新已知匹配ID
          this.knownMatchIds = new Set()
          for (const m of matches) {
            if (m.knowledge_id) this.knownMatchIds.add(m.knowledge_id)
          }
          for (const m of interested) {
            if (m.knowledge_id) this.knownMatchIds.add(m.knowledge_id)
          }
        }
      } catch (e) {
        console.error('[loadMatchResults] error:', e)
      } finally {
        this.matchLoading = false
        this.matchIsComplete = true
      }
    },
    // 启动持续匹配定时器，每15秒刷新（已暂停匹配则不启动）
    startContinuousMatching() {
      if (this.matchPaused == 1) return
      if (this.matchRefreshTimer) {
        clearInterval(this.matchRefreshTimer)
      }
      this.matchRefreshTimer = setInterval(() => {
        this.loadMatchResults()
      }, 15000)
    },
    // 点击匹配结果，跳转到AI解释页面
    goToMatchedSession(matchDetailId, matchedChatSessionId, briefMatchReason = '', detailedMatchReason = '') {
      // 使用 sessionStorage 缓存 detailedMatchReason，避免 URL 过长
      // brief_match_reason 用于列表展示，detailed_match_reason 用于详情页展示
      if (detailedMatchReason) {
        const cacheKey = `match_reason_${this.chatSessionId}_${matchedChatSessionId}`
        try {
          sessionStorage.setItem(cacheKey, detailedMatchReason)
        } catch (e) {
          console.warn('[ChatBox] Failed to cache detailedMatchReason:', e)
        }
      }
      // URL 使用 matchDetailId，后端可以根据 id 查询完整信息
      let url = `/app/#/matched-session?currentChatSessionId=${this.chatSessionId}&matchedChatSessionId=${matchedChatSessionId}`
      if (matchDetailId) {
        url += `&matchDetailId=${matchDetailId}`
      }
      window.open(url, '_blank')
    },
    // 处理匹配需求按钮点击
    handleMatchRequirement() {
      // 临时用户需要先登录/注册
      if (this.$store.state.alreadyLogin && !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return
      }
      // 直接展示匹配结果弹窗，匹配任务由后台自动发起
      this.showMatchResult = true
      if (!this.matchRefreshTimer) {
        this.startContinuousMatching()
      }
      // 没有数据时刷新一次
      if ((!this.matchResults || this.matchResults.length === 0) && (!this.matchInterested || this.matchInterested.length === 0) && !this.matchLoading) {
        this.loadMatchResults()
      }
    },
    // 重新匹配（强制刷新）
    handleReMatch() {
      this.matchResults = []
      this.matchInterested = []
      this.matchIsComplete = false
      this.hasNewMatches = false
      this.newMatchCount = 0
      this.knownMatchIds = new Set()
      this.findMatch(true)
    },
    // 显示历史匹配记录弹窗
    async showMatchHistory() {
      if (!this.chatSessionId) {
        Toast(this.$t('toast_start_conversation_first'))
        return
      }

      this.showMatchHistoryPopup = true
      this.matchHistoryLoading = true
      this.matchHistoryCurPage = 1
      this.matchHistoryHasMore = false

      try {
        const response = await axios.post('/api/chat/get_match_history', {
          chatSessionId: this.chatSessionId,
          pageSize: 10,
          curPage: 1
        })

        if (response.data.code === 0 && response.data.data) {
          const details = response.data.data.matchDetails || []
          this.matchHistoryList = details
          this.matchHistoryHasMore = details.length >= 10
        } else {
          this.matchHistoryList = []
          this.matchHistoryHasMore = false
          Toast(response.data.msg || this.$t('toast_load_history_failed'))
        }
      } catch (error) {
        console.error('[ChatBox] Load match history error:', error)
        this.matchHistoryList = []
        this.matchHistoryHasMore = false
        Toast(this.$t('toast_load_history_failed'))
      } finally {
        this.matchHistoryLoading = false
      }
    },
    async loadMoreMatchHistory() {
      if (this.matchHistoryLoadingMore || !this.matchHistoryHasMore) return
      this.matchHistoryLoadingMore = true
      this.matchHistoryCurPage++
      try {
        const response = await axios.post('/api/chat/get_match_history', {
          chatSessionId: this.chatSessionId,
          pageSize: 10,
          curPage: this.matchHistoryCurPage
        })
        if (response.data.code === 0 && response.data.data) {
          const details = response.data.data.matchDetails || []
          this.matchHistoryList = this.matchHistoryList.concat(details)
          this.matchHistoryHasMore = details.length >= 10
        } else {
          this.matchHistoryHasMore = false
        }
      } catch (error) {
        console.error('[ChatBox] loadMoreMatchHistory error:', error)
        this.matchHistoryHasMore = false
      } finally {
        this.matchHistoryLoadingMore = false
      }
    },
    // 打开统一历史页面（匹配tab）
    openHistoryPage() {
      // 关闭当前匹配结果弹窗
      this.showMatchResult = false
      // 通过 $parent 链找到 FrontPage 并打开 HistoryPage popup
      // ChatBox -> AiMatch -> FrontPage
      const frontPage = this.$parent.$parent
      if (frontPage && frontPage.showHistoryChats !== undefined) {
        frontPage.showHistoryChats = true
        // 切换到匹配 tab（需要延迟等 popup 渲染完成）
        this.$nextTick(() => {
          setTimeout(() => {
            const historyPage = frontPage.$refs.historyPage
            if (historyPage) {
              historyPage.activeTab = 'matches'
            }
          }, 300)
        })
      }
    },
    // 加载历史匹配记录并跳转到详情页
    loadMatchHistoryItem(item) {
      if (!item) return

      // 关闭历史弹窗
      this.showMatchHistoryPopup = false

      // 跳转到匹配详情页
      this.goToMatchedSession(
        item.matchDetailId,
        item.matchedChatSessionId,
        item.briefMatchReason,
        item.detailedMatchReason
      )
    },
    // 处理从 ChatMsgList 传来的位置选择事件
    handleLocationSelectedFromList(data) {
      const location = data.location
      // 覆盖之前选中的位置（只保留最新选择的位置）
      this.selectedLocations = [{
        address: location.address || '',
        lng: location.lng,
        lat: location.lat
      }]
      // 位置数据已通过 selectedLocations 存储，不再将经纬度追加到输入框
      Toast.success(this.$t('toast_location_selected'))
    },
    // 加载用户聊天的 match detail
    async loadUserMatchDetail() {
      if (!this.isUserChat || !wsService.userId) return

      this.userMatchDetailLoading = true

      try {
        const response = await axios.post('/api/chat/get_match_detail_by_users', {
          userId: wsService.userId,
          peerUserId: this.targetUserId
        })

        console.log('[ChatBox] loadUserMatchDetail response:', response.data)

        if (response.data.code === 0 && response.data.data && response.data.data.matchDetail) {
          this.userMatchDetail = response.data.data.matchDetail
          this.userMatchSourceChatSessionId = response.data.data.matchDetail.sourceChatSessionId
          console.log('[ChatBox] Loaded match detail:', this.userMatchDetail.briefMatchReason)
        }
      } catch (error) {
        console.error('[ChatBox] Failed to load match detail:', error)
      } finally {
        this.userMatchDetailLoading = false
      }
    },
    // 打开用户聊天的 match detail 页面
    openUserMatchDetail() {
      if (!this.userMatchDetail || !this.userMatchSourceChatSessionId) return

      const matchedChatSessionId = this.userMatchDetail.matchedChatSessionId
      const briefReason = this.userMatchDetail.briefMatchReason || ''

      // 使用 sessionStorage 缓存 match reason
      if (this.userMatchDetail.detailedMatchReason) {
        const cacheKey = `match_reason_${this.userMatchSourceChatSessionId}_${matchedChatSessionId}`
        try {
          sessionStorage.setItem(cacheKey, this.userMatchDetail.detailedMatchReason)
        } catch (e) {
          console.warn('[ChatBox] Failed to cache match reason:', e)
        }
      }

      // 跳转到 match detail 页面
      let url = `/app/#/matched-session?currentChatSessionId=${this.userMatchSourceChatSessionId}&matchedChatSessionId=${matchedChatSessionId}`
      if (this.userMatchDetail.matchDetailId) {
        url += `&matchDetailId=${this.userMatchDetail.matchDetailId}`
      }
      window.open(url, '_blank')
    },
    // 发布到群 - 打开确认弹窗
    // ==================== 独立会话发布流程（对齐 client/ ai_chat_page._publishStandalone） ====================
    // 是否独立会话模式（无 channelId 的 AI 对话；嵌入群页/私聊都不是）
    isStandaloneAiChat() {
      return !this.isUserChat && !this.isEmbedded && (!this.channelId || this.channelId === '20210802')
    },
    // 发起（或复用）当前 session 的建群请求：进行中/已建好的直接共享，绝不重复发起。
    // 成功结果缓存；失败返回 null 且不缓存，调用方可择机重试
    startAutoCreate(sessionId) {
      const vm = this
      if (this._autoCreateSessionId === sessionId) {
        if (this._autoCreatedResult) return Promise.resolve(this._autoCreatedResult)
        if (this._autoCreateFuture) return this._autoCreateFuture
      }
      // 超时对齐 client/ 的 120s（axios 默认无超时，后端慢时 spinner 会永远转）
      const future = axios.post('/api/channel/auto_create_for_session', { chatSessionId: sessionId }, { timeout: 240000 })
        .then(function (resp) {
          if (resp.data && resp.data.code === 0 && resp.data.data && resp.data.data.channel) {
            return { channel: resp.data.data.channel, created: resp.data.data.created === true }
          }
          console.warn('[ChatBox] auto_create_for_session 返回异常:', resp.data && resp.data.code)
          return null
        })
        .catch(function (e) {
          console.warn('[ChatBox] auto_create_for_session 失败:', e)
          return null
        })
      this._autoCreateSessionId = sessionId
      this._autoCreateFuture = future
      future.then(function (created) {
        if (created) vm._autoCreatedResult = created
        if (vm._autoCreateFuture === future) vm._autoCreateFuture = null
      })
      return future
    },
    // 清空发布流程缓存（reset/新会话时调用；缓存按 sessionId 绑定，留着也不会被新 session 误用）
    clearPublishFlowCaches() {
      this._autoCreateSessionId = ''
      this._autoCreateFuture = null
      this._autoCreatedResult = null
      this._autoCreateFlowActive = false
      this._pickChannelAutoAdvanced = false
      this.publishTarget = null
    },
    // 发布前确保非匿名（发布必然要进群，joinChannel 要求正式账号）
    ensureNonAnonymousForPublish() {
      if (!this.$store.state.alreadyLogin || !this.$store.state.user.userName) {
        this.$store.state.needLogin = true
        return false
      }
      return true
    },
    joinChannelById(channelId) {
      return axios.post('/api/channel/join', { channelId: channelId })
        .then(function (resp) { return resp.data && resp.data.code === 0 })
        .catch(function () { return false })
    },
    // 打开发布确认弹窗（带目标群卡片）
    openPublishConfirmWithTarget(target) {
      this.publishTarget = {
        channelId: String(target.channelId || ''),
        name: target.name || '',
        description: target.description || '',
        avatar: target.avatar || '',
        memberCnt: Number(target.memberCnt || 0)
      }
      this.publishConfirmAllowDirectContact = true
      this.publishConfirmExtraMsg = this.firstUserMessageText || (this.userMsgOptions.length > 0 ? this.userMsgOptions[0] : '')
      this.publishConfirmLoading = false
      this.showPublishConfirmPopup = true
    },
    // 独立会话模式发布：直接弹选群面板，由面板首次拉 agent 推荐群（agent 成本较高，
    // 只在用户点发布时才跑，结果服务端持久化）→ 自动选中最佳群直达发布确认框
    async handlePublishStandalone() {
      if (this.publishingStandalone) return
      if (!this.chatSessionId) {
        Toast(this.$t('toast_start_conversation_first'))
        return
      }
      this.publishingStandalone = true
      try {
        this.openPickChannel({ autoPickBest: true })
      } finally {
        // 弹框/面板打开后即可恢复按钮（它们有自己的 loading/交互态）
        this.publishingStandalone = false
      }
    },
    // 打开选群面板；opts.autoPickBest: 拉到推荐群自动选中最佳群直达确认框，
    // 无推荐群自动建群
    openPickChannel(opts) {
      opts = opts || {}
      this.publishTarget = null
      this.pickChannelMatches = []
      this.pickChannelLoadingMatches = true
      this.pickChannelSuggestPending = false
      this.pickChannelCreating = false
      this.pickChannelSearchQuery = ''
      this.pickChannelResults = []
      this.pickChannelHasMore = false
      this.pickChannelLoadingMore = false
      this._pickChannelAutoAdvanced = false
      this._pickChannelInitialFetched = false
      this._pickChannelOpts = opts
      // 长等待安抚：每 20s 检查一次，匹配中/建群中任一阶段超时即切换慢速提示文案
      // （agent 最多 3 次 LLM 调用 + 起名，最坏可能等 1~2 分钟，静态文案撑不住）
      if (this._pickChannelWaitTimer) clearInterval(this._pickChannelWaitTimer)
      this.pickChannelWaitSlow = false
      this._pickChannelWaitTimer = setInterval(() => {
        if (!this.showPickChannelPopup || (!this.pickChannelLoadingMatches && !this.pickChannelCreating)) {
          clearInterval(this._pickChannelWaitTimer)
          return
        }
        this.pickChannelWaitSlow = true
      }, 20000)
      this.showPickChannelPopup = true
    },
    // 选群面板首次拉取：全部群列表（第一页）+ agent 推荐群（只发起一次，放 watch 防止面板 rebuild 重复拉）
    async pickChannelInitialFetch() {
      if (this._pickChannelInitialFetched) return
      this._pickChannelInitialFetched = true
      const opts = this._pickChannelOpts || {}
      this.doPickChannelSearch('')
      if (this.chatSessionId) {
        const vm = this
        let matches = []
        let suggestPending = false
        try {
          const resp = await axios.post('/api/channel/suggest_list_for_session', { chatSessionId: this.chatSessionId }, { timeout: 240000 })
          const data = resp.data && resp.data.data
          if (data && data.pending) {
            // session 的 requirementDetail 尚未落库，搜索结果不可信
            suggestPending = true
          } else if (data && Array.isArray(data.suggestions)) {
            matches = data.suggestions
          }
        } catch (error) {
          // 请求失败同样不算"无相似群"
          suggestPending = true
        }
        if (!this.showPickChannelPopup) return
        this.pickChannelMatches = matches || []
        this.pickChannelLoadingMatches = false
        this.pickChannelSuggestPending = suggestPending
        // 自动推进（仅发布流程首次打开）：有推荐群 → 自动选中最佳群；
        // 推荐未就绪 → 绝不自动建群（防止"明明有相似群却建出新群"），用户手动选
        if (opts.autoPickBest && !vm._pickChannelAutoAdvanced) {
          vm._pickChannelAutoAdvanced = true
          if (matches && matches.length > 0) {
            vm.pickChannelById(matches[0])
          } else if (!suggestPending) {
            vm.autoCreateAndPick()
          }
        }
      } else {
        this.pickChannelLoadingMatches = false
      }
    },
    // 选群面板搜索（回车触发）
    async doPickChannelSearch(query) {
      this.pickChannelSearching = true
      this._pickChannelLastQuery = query || ''
      try {
        const response = await axios.post('/api/channel/search', {
          query: query || '',
          pageSize: 20,
          curPage: 1
        })
        if (response.data.code === 0) {
          const channels = (response.data.data && response.data.data.channels) || []
          this.pickChannelResults = channels.map(c => ({
            channelId: String(c.channelId || c.id || ''),
            name: c.name || '',
            avatar: c.avatar || '',
            description: c.description || '',
            memberCnt: c.memberCnt || 0
          }))
          this.pickChannelHasMore = channels.length >= 20
          this._pickChannelPage = 1
        }
      } catch (error) {
        console.error('[ChatBox] pick channel search error:', error)
      } finally {
        this.pickChannelSearching = false
      }
    },
    // 搜索结果加载下一页
    async loadMorePickChannelResults() {
      if (this.pickChannelSearching || this.pickChannelLoadingMore || !this.pickChannelHasMore) return
      this.pickChannelLoadingMore = true
      try {
        const response = await axios.post('/api/channel/search', {
          query: this._pickChannelLastQuery || '',
          pageSize: 20,
          curPage: (this._pickChannelPage || 1) + 1
        })
        if (response.data.code === 0) {
          const channels = (response.data.data && response.data.data.channels) || []
          const mapped = channels.map(c => ({
            channelId: String(c.channelId || c.id || ''),
            name: c.name || '',
            avatar: c.avatar || '',
            description: c.description || '',
            memberCnt: c.memberCnt || 0
          }))
          const existIds = {}
          this.pickChannelResults.forEach(function (c) { existIds[c.channelId] = true })
          const vm = this
          mapped.forEach(function (c) {
            if (!existIds[c.channelId]) vm.pickChannelResults.push(c)
          })
          this._pickChannelPage = (this._pickChannelPage || 1) + 1
          this.pickChannelHasMore = channels.length >= 20
        }
      } catch (error) {
        console.error('[ChatBox] pick channel load more error:', error)
      } finally {
        this.pickChannelLoadingMore = false
      }
    },
    // 选中一个群 → 非匿名 → 入群（幂等）→ 发布确认弹框
    async pickChannelById(item) {
      const channelId = String(item.channelId || '')
      if (!channelId) return
      if (!this.ensureNonAnonymousForPublish()) return
      const ok = await this.joinChannelById(channelId)
      if (!ok) {
        Toast.fail(this.$t('toast_publish_failed'))
        return
      }
      this.showPickChannelPopup = false
      this.openPublishConfirmWithTarget(item)
    },
    // 无推荐群时：AI 起名建群 → 入群 → 发布确认框；失败回落选群页（只剩搜索列表可手动选）
    async autoCreateAndPick() {
      if (!this.ensureNonAnonymousForPublish()) return
      if (this._autoCreateFlowActive) {
        // 已有建群流程在跑：只挂上「建群中」状态等它完成，绝不重复发起
        this.pickChannelCreating = true
        const pending = this._autoCreateSessionId === this.chatSessionId ? this._autoCreateFuture : null
        if (pending) await pending
        this.pickChannelCreating = false
        return
      }
      const flowSessionId = this.chatSessionId
      if (!flowSessionId) return
      this._autoCreateFlowActive = true
      this.pickChannelCreating = true
      try {
        const created = await this.startAutoCreate(flowSessionId)
        // 换了 session 就放弃（发布确认框用的是页面当前状态，不能拿旧群发新 session）
        if (this.chatSessionId !== flowSessionId) return
        const channel = created && created.channel
        if (!channel || !String(channel.channelId || '')) {
          this.pickChannelCreating = false
          Toast.fail(this.$t('toast_publish_failed'))
          return
        }
        const ok = await this.joinChannelById(String(channel.channelId))
        if (this.chatSessionId !== flowSessionId) return
        if (!ok) {
          this.pickChannelCreating = false
          Toast.fail(this.$t('toast_publish_failed'))
          return
        }
        this.pickChannelCreating = false
        this.showPickChannelPopup = false
        this.openPublishConfirmWithTarget(channel)
      } finally {
        this._autoCreateFlowActive = false
      }
    },
    // 选群面板内用输入的群名直接建群 → 入群 → 发布确认框
    async createChannelFromPick(name) {
      const cleanName = (name || '').trim().replace(/^#/, '')
      if (!cleanName) {
        this.focusPickChannelSearchInput()
        return
      }
      if (!this.ensureNonAnonymousForPublish()) return
      const locale = (this.$i18n && this.$i18n.locale) || 'en'
      try {
        const response = await axios.post('/api/channel/create', {
          name: cleanName,
          lang: locale === 'zhHant' ? 'zh_Hant' : locale,
          chatSessionId: this.chatSessionId || ''
        })
        if (response.data.code === 0 && response.data.data) {
          const channelId = String(response.data.data.channelId || '')
          if (channelId) {
            const ok = await this.joinChannelById(channelId)
            if (!ok) {
              Toast.fail(this.$t('toast_publish_failed'))
              return
            }
            this.showPickChannelPopup = false
            this.openPublishConfirmWithTarget({
              channelId: channelId,
              name: cleanName,
              description: '',
              avatar: '',
              memberCnt: 1
            })
            return
          }
        }
        Toast.fail(this.$t('toast_publish_failed'))
      } catch (error) {
        console.error('[ChatBox] create channel from pick error:', error)
        Toast.fail(this.$t('toast_publish_failed'))
      }
    },
    focusPickChannelSearchInput() {
      if (this.$refs.pickChannelSearchInput) {
        this.$refs.pickChannelSearchInput.focus()
      }
    },
    // 发布确认弹窗「重新选群」：关闭确认框，回到选群面板（现拉推荐群，不自动推进）
    repickChannel() {
      this.showPublishConfirmPopup = false
      this.openPickChannel({ autoPickBest: false })
    },

    handlePublishToChannel() {
      if (this.publishingToChannel) return
      if (!this.chatSessionId) {
        Toast(this.$t('toast_start_conversation_first'))
        return
      }
      // 重置弹窗状态
      this.publishConfirmAllowDirectContact = true
      // 优先用用户第一条原话作为 userMsg 默认值；没有则取 AI 建议的第一条；再没有则留空
      this.publishConfirmExtraMsg = this.firstUserMessageText || (this.userMsgOptions.length > 0 ? this.userMsgOptions[0] : '')
      this.publishConfirmLoading = false
      this.showPublishConfirmPopup = true
    },
    // 确认发布到群
    async confirmPublishToChannel() {
      if (this.publishConfirmLoading) return
      this.publishConfirmLoading = true
      this.publishingToChannel = true
      try {
        // 组装 userMsg：有图片时用 JSON 数组格式，否则纯文本
        var publishUserMsg = this.publishConfirmExtraMsg || ''
        if (this.firstUserMessageImages.length > 0) {
          var contentArr = [{ type: 'plaintext', content: publishUserMsg }]
          this.firstUserMessageImages.forEach(function(url) {
            contentArr.push({ type: 'img', content: { url: url } })
          })
          publishUserMsg = JSON.stringify(contentArr)
        }
        const response = await axios.post('/api/channel/publish_chat_session', {
          chatSessionId: this.chatSessionId,
          // 选群流程选定的目标群优先；嵌入模式走 channelId；旧的内联选群兜底
          channelId: (this.publishTarget && this.publishTarget.channelId) || this.channelId || this.selectedChannelId || this.sessionChannelId || '',
          allowDirectContact: this.publishConfirmAllowDirectContact ? 1 : 0,
          userMsg: publishUserMsg
        })
        if (response.data.code === 0) {
          this.showPublishConfirmPopup = false
          Toast.success(this.$t('toast_publish_success'))
          this.$emit('published', this.chatSessionId)
          // 独立会话发布成功：直接落群详情页，让用户看到卡挂在群里（对齐 client 跳 ChannelDetailPage）
          if (this.publishTarget && this.publishTarget.channelId) {
            const targetChannelId = this.publishTarget.channelId
            this.publishTarget = null
            // 全局独立弹窗（AiChatPopup）模式：发布完成落群详情页，收起弹窗，
            // 否则用户被顶在 90% 高的弹窗后面看群详情
            if (this.standalone) {
              this.$store.commit('setShowAiChatPopup', false)
            }
            this.$router.push('/channel/' + targetChannelId)
          }
        } else {
          Toast.fail(this.$t('toast_publish_failed') + ': ' + (response.data.msg || ''))
        }
      } catch (error) {
        console.error('Publish to channel error:', error)
        Toast.fail(this.$t('toast_publish_failed'))
      } finally {
        this.publishConfirmLoading = false
        this.publishingToChannel = false
      }
    },
    // 更新直接联系设置
    async updateAllowDirectContact(allowDirectContact) {
      if (!this.chatSessionId) return
      try {
        const response = await axios.post('/api/chat/update_allow_direct_contact', {
          chatSessionId: this.chatSessionId,
          allowDirectContact: allowDirectContact
        })
        if (response.data.code === 0) {
          this.allowDirectContact = allowDirectContact
          Toast.success(this.$t('session_setDirectContactSuccess'))
        } else {
          Toast.fail(this.$t('session_setDirectContactFailed'))
        }
      } catch (error) {
        console.error('Update allow direct contact error:', error)
        Toast.fail(this.$t('session_setDirectContactFailed'))
      }
    },
    // 获取会话元数据
    async getSessionMeta() {
      if (!this.chatSessionId) return
      try {
        const response = await axios.post('/api/chat/get_session_meta', {
          chatSessionId: this.chatSessionId
        })
        if (response.data.code === 0 && response.data.data) {
          const meta = response.data.data
          if (meta.requirementDetail != null) {
            this.requirementDetail = String(meta.requirementDetail)
          }
          if (meta.aiSummary != null) {
            this.aiSummary = String(meta.aiSummary)
          }
          if (meta.userMsgOptions) {
            try {
              var opts = JSON.parse(String(meta.userMsgOptions))
              if (Array.isArray(opts)) {
                this.userMsgOptions = opts.filter(function(e) { return typeof e === 'string'; })
              }
            } catch (e) { /* ignore */ }
          }
          if (meta.allowDirectContact != null) {
            this.allowDirectContact = meta.allowDirectContact == 1 || meta.allowDirectContact == '1'
          }
        }
      } catch (error) {
        console.error('Get session meta error:', error)
      }
    },
    // 格式化 match detail 时间戳
    formatMatchDetailTime(createdAt) {
      if (!createdAt) return ''
      try {
        const timestamp = parseInt(createdAt)
        const date = new Date(timestamp)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hour = String(date.getHours()).padStart(2, '0')
        const minute = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hour}:${minute}`
      } catch (e) {
        return ''
      }
    },
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Welcome 状态（logo + prompt message） */
.welcome-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 0;
}
.welcome-logo-wrapper {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 20px 2px rgba(248, 160, 144, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}
.welcome-logo {
  width: 56px;
  height: 56px;
}
.welcome-message {
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 560px;
}
.welcome-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #FEF0ED;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.welcome-avatar-img {
  width: 20px;
  height: 20px;
}
.welcome-bubble {
  margin-left: 10px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #FEF0ED, #FEF0ED);
  border-radius: 12px;
  font-size: 14px;
  color: #4A4A4A;
  line-height: 1.5;
}
/* 需求澄清进度卡片 */
.clarification-progress-card {
  margin: 12px;
  padding: 14px;
  background: #F7F5F2;
  border-radius: 12px;
  border: 1px solid #E0DDD8;
}

.clarification-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.clarification-progress-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #1A1A1A;
}

.clarification-progress-percent {
  font-size: 13px;
  font-weight: 700;
  color: #E8543A;
}

.clarification-progress-bar-wrapper {
  margin-bottom: 8px;
}

.clarification-progress-bar-bg {
  width: 100%;
  height: 6px;
  background: #E0DDD8;
  border-radius: 4px;
  overflow: hidden;
}

.clarification-progress-bar-fill {
  height: 100%;
  background: #E8543A;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.clarification-progress-subtitle {
  font-size: 11px;
  color: #8A8A8A;
}

.brief-match-reason-card {
  margin-top: 8px;
  margin-bottom: 6px;
  padding: 8px 10px;
  background: #FEF0ED;
  border-radius: 8px;
  border: 0.5px solid #F8A090;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  text-align: left;
}

.brief-match-reason-icon {
  flex-shrink: 0;
  font-size: 12px;
  line-height: 1.5;
}

.brief-match-reason-tag {
  flex-shrink: 0;
  font-size: 10px;
  color: #C93E1A;
  background: #fff;
  padding: 1px 6px;
  border-radius: 4px;
}

.brief-match-reason-text {
  font-size: 12px;
  color: #4A4A4A;
  line-height: 1.5;
  word-break: break-word;
}

.spinning-logo {
  animation: spin 4s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse-dot {
  from {
    opacity: 0.3;
  }

  to {
    opacity: 1;
  }
}

.mini-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: mini-spin 0.8s linear infinite;
}

@keyframes mini-spin {
  to {
    transform: rotate(360deg);
  }
}

.search-scroll-container {
  overscroll-behavior: contain;
}

.van-popover .van-popover__content {
  overflow: visible !important;
}

.van-search>>>.van-field__control {
  font-size: 0.65rem;
}

.van-search>>>.van-cell {
  font-weight: 600;
}

.app-hint-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: linear-gradient(135deg, #E8543A, #C93E1A);
  color: #ffffff;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.app-hint-pill:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

.app-download-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.app-download-btn:hover {
  opacity: 0.9;
}

.app-download-btn.app-store {
  background: #1A1A1A;
}

.app-download-btn.google-play {
  background: #E8543A;
}

.app-download-btn.apk {
  background: #34A853;
  color: #1A1A1A;
}

.msg-left {
  text-align: left;
  float: left;
}

.msg-right {
  text-align: right;
  float: right;
}

.msg-center {
  width: 90% !important;
  max-width: 320px !important;
  margin: 0 auto !important;
  float: none !important;
}

/* 对方消息（接收的消息）- 白色气泡，小圆角 */
.msg-left .msg-body {
  background: #FFFFFF;
  padding: 10px 12px;
  border-radius: 4px;
  position: relative;
  margin-left: 4px;
}

/* 自己发送的消息 - 淡橙色背景，稍大圆角 */
.msg-right .msg-body {
  background: #FEF0ED;
  padding: 8px 12px;
  border-radius: 8px;
  position: relative;
  margin-right: 4px;
}

/* 小箭头 - 使用伪元素实现 */
.msg-left .msg-body::before {
  content: '';
  position: absolute;
  left: -5px;
  top: 8px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #FFFFFF;
}

.msg-right .msg-body::before {
  content: '';
  position: absolute;
  right: -6px;
  top: 8px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 8px solid #FEF0ED;
}

.small-btn .van-button {
  height: 28px;
}

.small-btn .van-button--normal {
  font-size: 0.75rem;
}

.item {
  /* background-color: #eee; */
  /* border-radius: 4px; */
  padding: 2px 8px 2px 0px;
  font-size: 0.65rem;
  color: #8A8A8A;
}

.reply-btn .van-button {
  height: 18px;
}

.reply-btn .van-button--normal {
  font-size: 0.60rem;
  padding: 0 8px;
}

/* 隐藏滚动条 */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* 确保内容在超出容器尺寸时可以滚动 */
.no-scrollbar {
  overflow: auto;
  /* 或者使用 'scroll' */
  -ms-overflow-style: none;
  /* IE 和 Edge */
  scrollbar-width: none;
  /* Firefox */
}

.flex-container {
  display: flex;
  justify-content: center;
  /* 水平居中对齐 */
  align-items: center;
  /* 垂直居中对齐，如果需要的话 */
  gap: 4px;
  /* 控制子元素之间的间距 */
}

.msg-left .item {
  padding: 2px 6px;
  background-color: #eee;
  border-radius: 4px;
}

.min-order-card-width {
  min-width: 90%;
}

#intentionType .van-dropdown-menu /deep/ .van-dropdown-menu__title {
  color: #8A8A8A !important;
  font-size: 10px !important;
}

#intentionType /deep/ .van-dropdown-menu__title .van-ellipsis {
  color: #8A8A8A !important;
  font-size: 10px !important;
}

#intentionType /deep/ .van-dropdown-menu__bar {
  height: 24px !important;
}

/* 编辑器样式适配 */
.twemoji-editor {
  min-height: 36px;
  line-height: 24px;
  padding: 6px 16px;
  overflow-y: auto;
}

/* 表情图片尺寸 */
.emoji-img {
  width: 24px;
  height: 24px;
  margin: 4px;
}

/* 弹窗面板布局 */
.emoji-panel {
  display: flex;
  flex-wrap: wrap;
  max-height: 40vh;
  overflow-y: auto;
  padding: 8px;
}

.msg-body>>>.twemoji {
  width: 20px !important;
  height: 20px !important;
  position: relative;
  top: 8px;
  padding: 2px;
}

/* 深度选择器处理SVG */
.msg-body>>>.twemoji svg {
  width: 100% !important;
  height: 100% !important;
  vertical-align: top;
  /* 修复对齐问题 */
}

#searchChatMsgPopup {
  z-index: 30000 !important;
}

.highlight-message>>>.msg-body {
  border: 2px solid #E8543A !important;
}

.html-content {
  -webkit-user-select: text;
  user-select: text;
  -webkit-touch-callout: default;
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
}

/* #intentionType /deep/ .van-dropdown-menu__item {
  width: 30px;
} */

/* 图片上传相关样式 */
.image-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #FAFAF8;
  border-radius: 4px;
}

.image-preview-item {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-delete-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.image-delete-btn .van-icon {
  color: white;
}

.add-image-btn {
  width: 60px;
  height: 60px;
  border: 2px dashed #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #FAFAF8;
}

.add-image-btn:active {
  background: #E0DDD8;
}

.uploading-indicator {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FAFAF8;
  border-radius: 4px;
}

/* 位置预览样式 */
.location-preview-item {
  position: relative;
  max-width: 260px;
  min-height: 36px;
  border-radius: 8px;
  overflow: hidden;
  background: #FEF0ED;
  border: 1px solid #E8543A;
}

.location-preview-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 6px 12px;
  box-sizing: border-box;
  gap: 6px;
}

.emoji-preview-item {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  background: #FFF3E0;
  border: 1px solid #F5A623;
  display: flex;
  align-items: center;
  justify-content: center;
}

.emoji-preview-text {
  font-size: 32px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.emoji-preview-text img {
  width: 32px;
  height: 32px;
}

.emoji-small-img {
  width: 20px;
  height: 20px;
  vertical-align: middle;
}

/* Emoji分类导航样式 */
.emoji-category-tab {
  flex: 1;
  min-width: 32px;
  padding: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s;
}

.emoji-category-tab:hover {
  background: #E0DDD8;
}

.emoji-category-tab.active {
  background: #FEF9E7;
}

.emoji-category-tab img {
  width: 20px;
  height: 20px;
}

.location-preview-text {
  font-size: 12px;
  color: #E8543A;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
}

/* 消息中的图片样式 */
.msg-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 4px;
  margin-top: 8px;
  cursor: pointer;
}

/* 消息中的文件样式 */
.msg-files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  margin-top: 8px;
}

.msg-file-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.msg-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background: #FAFAF8;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.msg-file:hover {
  background: #E0DDD8;
}

.msg-file .file-name {
  font-size: 12px;
  color: #8A8A8A;
  margin-top: 8px;
  text-align: center;
  word-break: break-all;
  padding: 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.msg-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  margin-top: 8px;
}

/* 预览区域的文件样式 */
.preview-file {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #FAFAF8;
  padding: 8px;
}

.file-name-preview {
  font-size: 10px;
  color: #8A8A8A;
  margin-top: 4px;
  text-align: center;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 用户聊天 match detail 卡片样式 */
.user-match-detail-card {
  display: flex;
  align-items: center;
  margin: 12px;
  padding: 8px 10px;
  background: #FEF0ED;
  border-radius: 8px;
  border: 0.5px solid rgba(255, 151, 106, 0.3);
  cursor: pointer;
  transition: background 0.2s;
}

.user-match-detail-card:hover {
  background: #FEF0ED;
}

.user-match-detail-card:active {
  background: #FEF0ED;
}

.user-match-detail-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.user-match-detail-time {
  font-size: 11px;
  color: #8A8A8A;
  margin-top: 4px;
  margin-left: 24px;
}

/* 私聊页内搜索 */
.chat-search-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 2px 14px 4px;
  cursor: pointer;
}

.chat-search-panel {
  margin: 0 12px 8px;
  padding: 10px 12px;
  background: #FFFFFF;
  border: 1px solid #E0DDD8;
  border-radius: 12px;
}

.chat-search-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F7F5F2;
  border-radius: 16px;
  padding: 7px 10px;
}

.chat-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #1A1A1A;
}

.chat-search-close {
  flex-shrink: 0;
  cursor: pointer;
}

.chat-search-status {
  padding: 12px 0 4px;
  text-align: center;
  color: #8A8A8A;
  font-size: 12px;
}

.chat-search-results {
  max-height: 240px;
  overflow-y: auto;
}

.chat-search-item {
  padding: 8px 4px;
  border-bottom: 1px solid #F0EDE8;
  cursor: pointer;
}

.chat-search-item:last-child {
  border-bottom: none;
}

.chat-search-item-time {
  font-size: 11px;
  color: #8A8A8A;
  margin-bottom: 2px;
}

.chat-search-item-content {
  font-size: 13px;
  color: #1A1A1A;
  word-break: break-word;
  text-align: left;
}

/* 首页 Discover 区域样式 */
.home-discover-section {
  padding: 10px 200px 20px 10px;
  background: rgb(245, 245, 245);
  -ms-flex-negative: 0;
  flex-shrink: 0;
}

.home-discover-header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  padding-left: 4px;
}

.home-discover-title {
  font-size: 10px;
  font-weight: 500;
  color: #C93E1A;
  margin-left: 4px;
}

.home-discover-item {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  margin-bottom: 4px;
  background: white;
  border-radius: 6px;
  border: 1px solid #E0DDD8;
  cursor: pointer;
}

.home-discover-summary {
  flex: 1;
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
}

.home-discover-avatar {
  width: 16px;
  height: 16px;
  border-radius: 16%;
  margin-left: 8px;
  flex-shrink: 0;
}

.home-discover-avatar-placeholder {
  width: 16px;
  height: 16px;
  border-radius: 16%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 7px;
  font-weight: 500;
  margin-left: 8px;
  flex-shrink: 0;
}

/* 发送按钮（与群聊详情页 channel-input-send 一致） */
.chatbox-send-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;

  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.2s;
  vertical-align: middle;
}

.chatbox-send-btn:active {
  opacity: 0.85;
}

/* 录音状态条 */
.recording-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #FEF0ED;
  border-top: 1px solid #F8A090;
}

.recording-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #E05252;
  animation: recording-blink 1s infinite;
}

@keyframes recording-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.recording-text {
  font-size: 13px;
  color: #4A4A4A;
}

.recording-time {
  font-size: 13px;
  font-weight: 600;
  color: #1A1A1A;
}

.recording-cancel {
  margin-left: auto;
  font-size: 13px;
  color: #8A8A8A;
  cursor: pointer;
  padding: 4px 8px;
}

.recording-done {
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: #E8543A;
  border-radius: 14px;
  cursor: pointer;
  padding: 4px 14px;
}

/* 语音预览（输入栏上方附件区） */
.voice-preview-item {
  position: relative;
  min-width: 110px;
  height: 36px;
  border-radius: 8px;
  background: #FEF0ED;
  border: 1px solid #F8A090;
  display: flex;
  align-items: center;
  padding: 0 28px 0 10px;
  cursor: pointer;
}

.voice-preview-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.voice-preview-text {
  font-size: 12px;
  color: #C93E1A;
  white-space: nowrap;
}

/* 输入区（与群聊详情页 channel-bottom-input-bar / channel-input-wrapper 一致） */
/* AI 生成内容界面级显著提示（合规） */
.ai-generated-notice {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  background: #FEF0ED;
  border-top: 1px solid #F8A090;
  font-size: 11px;
  color: #8A8A8A;
  text-align: center;
}

.chatbox-input-bar {
  position: relative;
  flex: 0 0 auto;
  background: white;
  border-top: 1px solid #E0DDD8;
  padding: 12px 12px 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
}

/* 引用（回复）条 */
.reply-preview-bar {
  display: flex;
  align-items: center;
  background: #F7F5F2;
  border-left: 2px solid #E8543A;
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 8px;
}

.reply-preview-text {
  flex: 1;
  margin: 0 8px;
  font-size: 0.75rem;
  color: #4A4A4A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.chatbox-input-wrapper {
  display: flex;
  align-items: center;
  background: #F7F5F2;
  border-radius: 22px;
  padding: 6px 6px 6px 14px;
}

.chatbox-input-wrapper .emoji-input-container {
  flex: 1;
  min-width: 0;
}

/* + 按钮容器 */
.add-btn-container {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F7F5F2;
  border-radius: 50%;
  cursor: pointer;
  margin-right: 8px;
  flex-shrink: 0;
}

/* 频道选择面板 */
.channel-picker-panel {
  background: #F7F5F2;
  border: 1px solid #E0DDD8;
  max-height: 250px;
}

.channel-picker-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s;
  border: 1px solid #E0DDD8;
}

.channel-picker-item:hover {
  background: #E0DDD8;
}

.channel-picker-item:last-child {
  border-bottom: none;
}

.channel-picker-item-active {
  background: #FEF0ED;
}

/* 操作面板 */
.action-panel {
  position: relative;
  z-index: 2;
  display: flex;
  padding: 12px;
  background: #F7F5F2;
  gap: 24px;
  border: 1px solid #E0DDD8;
}

.action-panel-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.action-panel-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.action-panel-label {
  font-size: 11px;
  color: #8A8A8A;
}

/* 文件预览样式 */
.file-preview-item {
  position: relative;
  width: 120px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  background: #FEF0ED;
  border: 1px solid #F8A090;
}

.file-preview-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 4px;
  box-sizing: border-box;
}

.file-preview-info {
  display: flex;
  flex-direction: column;
  margin-left: 4px;
  flex: 1;
  overflow: hidden;
}

.file-name {
  font-size: 10px;
  color: #E8543A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 9px;
  color: #E8543A;
}

/* 视频预览样式（首帧缩略图+播放角标，与图片预览同风格） */
.video-preview-item {
  position: relative;
  width: 96px;
  height: 54px;
  border-radius: 4px;
  overflow: hidden;
  background: #1A1A1A;
  border: none;
}

.video-preview-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.video-preview-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-preview-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding-left: 2px;
}

/* 待上传图片的 loading 叠加层 */
.uploading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 操作面板遮罩层 */
.action-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

/* 匹配结果弹窗整体（对齐 ChannelDetail MatchHistorySheet） */
.chatbox-match-sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #FAFAF8;
}
.chatbox-match-sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
}

/* 匹配结果为空时旋转的 logo */
.chatbox-rotating-logo {
  animation: chatbox-logo-spin 3s linear infinite;
}

@keyframes chatbox-logo-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* AI 自动建群中卡片：匀速慢速的不确定进度条（系统 loading 动画太急促，
   不适合「AI 思考中可能要 10~30 秒」的长等待场景，自绘 2.8s 一循环的滑动短条，对齐 client _SlowIndeterminateBar） */
.pick-channel-slow-bar {
  position: relative;
  height: 4px;
  background: #F2EFEC;
  border-radius: 2px;
  overflow: hidden;
}

.pick-channel-slow-bar-thumb {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 35%;
  background: #E8543A;
  border-radius: 2px;
  animation: pickChannelSlowSlide 2.8s linear infinite;
}

@keyframes pickChannelSlowSlide {
  0% {
    left: -35%;
  }
  100% {
    left: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ChatBox.vue